import { WalletPane } from '../features/customer-care/wallet/WalletPane';
import { WalletSearchResult } from '../features/customer-care/wallet/WalletSearchResult';
import { formatRand, formatWalletBalance, statusBadgeVariant } from '../features/customer-care/wallet/walletFormat';
import { fetchCustomerGraph, isWalletAccountNumber, type CustomerGraph } from '../features/customer-care/customerGraph';
import { getApiErrorMessage } from '../features/customer-care/apiErrorMessage';
import { SimPukDetails } from '../features/customer-care/SimPukDetails';
import { useState,useEffect,useRef,useMemo,useCallback } from 'react';
import {
IconSearch,
IconUser,
IconPhone,
IconId,
IconCircleCheck,
IconAlertCircle,
IconInfoCircle,
IconReceipt,
IconGift,
IconMessage,
IconDashboard,
IconArrowsExchange,
IconBan,
IconLockOpen,
IconLoader2,
IconRefresh,IconShieldCheck,
IconWallet,IconCalendar,
IconEye,
IconCopy,
IconChartBar
} from '@tabler/icons-react';
import { toast } from 'sonner';
import api from '../lib/api';
import novaOpsApi from '../lib/novaOpsApi';
import { API_BASE_URL,GOOGLE_MAPS_API_KEY } from '../env';
import { useAuth } from '../contexts/AuthContext';
import { AiSummaryCard } from '../components/AiSummaryCard';
import { CustomerEdrUsagePanel } from '../features/customer-care/CustomerEdrUsagePanel';
import { resolveEdrMsisdnFilter } from '../features/customer-care/customerEdrUsageUtils';
import { customerMessageContentForDisplay } from '../features/customer-care/customerMessagePrivacy';
import { getAccountSuspension, isAccountSuspended } from '../features/customer-care/accountSuspension';
import {
getMtnStatusLabel,
resolveMtnStatus,
} from '../features/customer-care/mtnStatus';
import {
createBasePlanGroups,
getBalanceLabel,
getBasePlanName,
getOtherApplicableBalances,
getOtherBalanceSegments,
isBasePlanName,
formatOtherBalanceExpiry,
formatOtherBalanceUsage,
type BasePlanName,
type RawActivePlanVersion,
type RawBalance,
} from '../features/customer-care/balancePlans';
import {
formatReplacementSimBlocker,
getSimSwapSummaryLabel,
getSimSwapVerificationFlowLabel,
getSimSwapVerificationRequirement,
isSimSwapVerificationCurrent,
type SimSwapVerificationState,
} from '../features/customer-care/simSwapReadiness';
import { canSendSimSwapVerification, sendAndRefreshSimSwapVerification } from '../features/customer-care/simSwapVerificationPrompt';
import { SimSwapVerificationPanel } from '../features/customer-care/SimSwapVerificationPanel';
import { RicaDeadlineRecovery } from '../features/customer-care/RicaDeadlineRecovery';
import { SmsPortalBlistPanel } from '../features/customer-care/SmsPortalBlistPanel';
import {
  useSimSwapReplacementEligibility,
  type ReplacementSimEligibility,
} from '../features/customer-care/useSimSwapReplacementEligibility';
import MainCard from '../components/MainCard';
import { SimInventoryResult, type SimInventorySearchResult } from '../features/customer-care/SimInventoryResult';
import { StatCard } from '../components/cards';
import { formatDateTime as formatAppDateTime } from '../utils/dateFormat';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from '@/components/ui/select';
import {
Popover,
PopoverContent,
PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert,AlertTitle,AlertDescription } from '@/components/ui/alert';
import { Dialog,DialogContent,DialogHeader,DialogFooter,DialogTitle,DialogDescription } from '@/components/ui/dialog';
import { Tabs,TabsList,TabsTrigger,TabsContent } from '@/components/ui/tabs';
import { Accordion,AccordionItem,AccordionTrigger,AccordionContent } from '@/components/ui/accordion';
import { Table,TableHeader,TableBody,TableRow,TableHead,TableCell } from '@/components/ui/table';
import { Tooltip,TooltipTrigger,TooltipContent,TooltipProvider } from '@/components/ui/tooltip';

type CustomerReward = {
  id: number;
  i_customer: number;
  type: string | null;
  reward_type?: string | null;
  pulse_basket_id: number | null;
  halo_rule_id: number | null;
  halo_transaction_id: number | null;
  credit: number | null;
  debit: number | null;
  spend_requirement: number;
  status: string;
  spend_requirement_met: number;
  spend_check_date: string | null;
  allocated_at: string | null;
  billing_response: string | null;
  billing_transaction_id: string | null;
  billing_batch_total: number | null;
  billing_failure_reason: string | null;
  expires_at: string | null;
  pulse_allocated_data_id: number | null;
  pulse_rule_id: number | null;
  backfill_method: string | null;
  created_at: string;
  updated_at: string;
};

type CustomerRewardsResponse = {
  rewards?: CustomerReward[];
  rewardUsage?: {
    blocked: boolean;
    status: string;
    latestHistory?: CustomerRewardBlockHistory | null;
  };
  rewardBlockHistory?: CustomerRewardBlockHistory[];
};

type CustomerRewardBlockHistory = {
  id: string | number;
  action: 'block' | 'unblock' | string;
  reason: string | null;
  previous_value: boolean;
  new_value: boolean;
  performed_by: string | null;
  created_at: string;
};

type NovaAccountDetails = {
  uuid: string;
  msisdn: string;
  status: string;
  simCard: {
    uuid: string;
    iccid: string;
    status?: string;
    starterPack?: {
      uuid: string;
      code: string;
      name: string;
      status: string;
      airtimeCents: number | null;
      dataMb: number | null;
      voiceMinutes: number | null;
      smsCount: number | null;
      expiryPeriod: string;
    } | null;
  } | null;
  attributedPartner: { uuid: string; name: string } | null;
  ricaEntity: { uuid: string } | null;
  defaultPlansLoaded: boolean;
  provisionedAt: string | null;
  activatedAt: string | null;
  deactivatedAt: string | null;
  linkedUsers: Array<{
    uuid: string;
    email: string;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    isPrimary: boolean;
    verified: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
};

type BalanceCategory = {
  formatted: string;
  percentageUsed: number;
  value?: number;
  availableValue?: number;
  usedValue?: number;
  reservedValue?: number;
  totalValue?: number;
};

type BundleSummary = {
  planSubscriptionId?: string | null;
  name: string;
  category: string;
  remainingPercent: number;
  formattedTimeRemaining: string;
  formattedUsage: string;
  components?: Array<{
    label: string;
    category: string;
    remainingPercent: number;
    formattedUsage: string;
  }>;
};

type NovaBalancePlans = {
  aggregated: {
    airtime: BalanceCategory;
    data: BalanceCategory;
    voice: BalanceCategory;
    sms: BalanceCategory;
  };
  usage: {
    data: { formatted: string };
    voice: { formatted: string };
    sms: { formatted: string };
  };
  bundles: Record<'combo' | 'data' | 'social' | 'voice' | 'sms', BundleSummary[]>;
  activePlanVersions?: RawActivePlanVersion[];
  balances?: RawBalance[];
};

type IdentityAssuranceSignal = {
  key?: string;
  label: string;
  status: 'ready' | 'partial' | 'missing';
  value: string;
  source?: string;
  mono?: boolean;
};

type IdentityAssuranceResponse = {
  success?: boolean;
  i_customer?: number;
  accountId?: string | null;
  tenant?: {
    mvno?: string;
    displayName?: string;
  };
  identityAssurance: {
    level: string;
    provider?: string;
    requiredLevel?: string;
    verifiedAt?: string | null;
    expiresAt?: string | null;
    eligibleForSimSwap: boolean;
    blockers?: string[];
    signals?: IdentityAssuranceSignal[];
    simSwapVerification?: SimSwapVerificationState | null;
    verificationTrigger?: {
      configured: boolean;
      threshold: number;
      current: number | null;
      remaining: number | null;
      unit: string;
      reason?: string;
    };
  };
  simSwap?: {
    enabled: boolean;
    eligible: boolean;
    disabledReason?: string;
    currentProcess?: {
      transactionId: string;
      simSwapStatus: string;
      msisdn: string;
      oldSimIccid?: string | null;
      newSimIccid?: string | null;
      requestedAt?: string | null;
      updatedAt: string;
    } | null;
  };
  migrationStatus?: { state?: string; note?: string };
};

type SimHistoryResponse = {
  history: Array<{
    id: string;
    event_type: string;
    status: string | null;
    iccid: string | null;
    imsi: string | null;
    legacy_id: number | null;
    allocated_at: string | null;
    rica_at: string | null;
    activated_at: string | null;
    churned_at: string | null;
    created_at: string | null;
    description: string | null;
    performed_by: string | null;
  }>;
  migrationStatus?: { state?: string; reason?: string };
};

type VoucherStatusResponse = {
  vouchers: Array<{
    id: string;
    request_id: string | null;
    advice_id: string | null;
    voucher_id: string | null;
    source: string | null;
    value_type: string | null;
    amount: string | number | null;
    quantity: string | number | null;
    status: string;
    effective_status?: string | null;
    customer_voucher_role?: string | null;
    product_name: string | null;
    sale_transaction_id: string | null;
    redemption_transaction_id: string | null;
    redeemed_by_account_id: string | null;
    requested_at: string | null;
    confirmed_at: string | null;
    redeemed_at: string | null;
    expired_at: string | null;
    reversed_at: string | null;
    reversal_reason: string | null;
    rrn: string | null;
    stan: string | null;
    originator_reference: string | null;
    service_reference: string | null;
    state: string | null;
    matched_status: string | null;
    created_at: string | null;
    updated_at: string | null;
    code_available: boolean;
    formatted_code_available: boolean;
    code_display?: string | null;
    code_display_mode?: 'full' | 'masked' | string | null;
    code_masked?: string | null;
    redeemed_by_msisdn?: string | null;
    redeemed_by_i_customer?: string | number | null;
    selling_partner_name: string | null;
    reseller_partner_name: string | null;
    sale_transaction_status: string | null;
    sale_payment_reference: string | null;
    redemption_transaction_status: string | null;
    redemption_payment_reference: string | null;
  }>;
  summary?: {
    totalVouchers: number;
    purchasedVouchers?: number;
    redeemedVouchers: number;
    redeemedByCustomerVouchers?: number;
    activeVouchers: number;
  };
  migrationStatus?: { state?: string; reason?: string };
};

type CustomerMessagesResponse = {
  messages: Array<{
    id: string;
    channel: string;
    provider: string;
    status: string;
    purpose: string;
    content?: string | null;
    contentRedacted?: boolean;
    provider_ref?: string | null;
    recipient_address?: string | null;
    recipient_address_masked: string | null;
    provider_message_id: string | null;
    submitted_at: string | null;
    sent_at: string | null;
    delivered_at: string | null;
    failed_at: string | null;
    failure_reason: string | null;
    created_at: string | null;
    updated_at: string | null;
    metadata?: Record<string, unknown> | null;
    linkedVoucherStatus?: string | null;
  }>;
  summary?: {
    totalMessages: number;
    failedMessages: number;
    submittedMessages: number;
  };
  migrationStatus?: { state?: string; reason?: string };
};

type CustomerTransaction = {
  id: string;
  transaction_type_id?: string | null;
  account_id?: string | null;
  app_user_id?: string | null;
  i_customer?: number | null;
  msisdn?: string | null;
  amount?: string | number | null;
  currency?: string | null;
  payment_category?: string | null;
  payment_method?: string | null;
  payment_reference?: string | null;
  product_id?: string | null;
  product_name?: string | null;
  product_code?: string | null;
  product_type_code?: string | null;
  status?: string | null;
  transaction_direction?: string | null;
  request_channel?: string | null;
  external_reference?: string | null;
  totogi_reference?: string | null;
  payment_status?: string | null;
  payment_provider?: string | null;
  payment_provider_reference?: string | null;
  payment_completed_at?: string | null;
  provisioning_status?: string | null;
  provisioning_reference?: string | null;
  provisioning_completed_at?: string | null;
  failure_reason?: string | null;
  reversal_reason?: string | null;
  reversed_at?: string | null;
  legacy_transaction_id?: number | null;
  legacy_transaction_source?: string | null;
  legacy_transaction_type_id?: number | null;
  linked_transaction_id?: string | null;
  linked_transaction_status?: string | null;
  linked_transaction_direction?: string | null;
  linked_transaction_amount?: string | number | null;
  linked_transaction_created_at?: string | null;
  intent?: string | null;
  idempotency_key?: string | null;
  ops_notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  activating_partner_name?: string | null;
  selling_partner_name?: string | null;
  selling_agent_partner_name?: string | null;
  linked_voucher_count?: number;
  linked_vouchers?: Array<Record<string, any>>;
  transaction_steps?: Array<Record<string, any>>;
  transaction_events?: Array<Record<string, any>>;
  transaction_type_name?: string | null;
  transaction_type_type?: string | null;
  debit?: string | number | null;
  credit?: string | number | null;
  quantity?: string | number | null;
  reference?: string | null;
  voucher_type?: string | null;
};

const getMessageHistoryTimestamp = (
  message: CustomerMessagesResponse['messages'][number],
): Date | null => {
  const raw =
    message.created_at ||
    message.submitted_at ||
    message.sent_at ||
    message.delivered_at ||
    message.failed_at;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseDateInputStart = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const parseDateInputEnd = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
};

const formatMessageHistoryDateRangeLabel = (from: string, to: string) => {
  if (!from && !to) return 'All dates';
  const formatLabel = (value: string) =>
    new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  if (from && to) return `${formatLabel(from)} – ${formatLabel(to)}`;
  if (from) return `From ${formatLabel(from)}`;
  return `Until ${formatLabel(to)}`;
};

const formatMessagePurposeLabel = (purpose?: string | null) => {
  if (!purpose) return '-';
  const labels: Record<string, string> = {
    'ops-customer-support-sms': 'Support SMS',
    'ops-customer-voucher-resend-sms': 'Voucher resend',
    'ops-customer-sim-swap-identity-check': 'SIM swap identity check',
  };
  return labels[purpose] ?? purpose;
};

const formatMessageContentPreview = (
  value?: string | null,
  purpose?: string | null,
) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (purpose === 'ops-customer-sim-swap-identity-check') {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
      return `WhatsApp template security_check_sim_swap (verification run ${trimmed.slice(0, 8)}…)`;
    }
  }
  return trimmed;
};

const maskVoucherCodeText = (value?: string | null, purpose?: string | null) => {
  if (!value) return value || '';

  const maskToken = (token: string) => {
    const cleaned = token.trim();
    if (cleaned.length <= 6) return cleaned;
    return `${cleaned.slice(0, 2)}${'•'.repeat(Math.max(4, cleaned.length - 6))}${cleaned.slice(-4)}`;
  };

  const phraseMasked = value.replace(
    /(voucher\s+code\s+(?:is|:)\s*:?\s*)([A-Z0-9-]{7,})/gi,
    (_match, prefix: string, code: string) => `${prefix}${maskToken(code)}`,
  );

  if (purpose !== 'ops-customer-voucher-resend-sms') {
    return phraseMasked;
  }

  return phraseMasked.replace(/\b[A-Z0-9]{8,}\b/g, (token) => {
    if (/^\d{8,}$/.test(token)) return token;
    return maskToken(token);
  });
};


const LoginHistoryHeatmap: React.FC<{ 
  loginHistory: any[], 
  lastLogin: any,
  isOpen: boolean 
}> = ({ loginHistory, lastLogin, isOpen }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const mapInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (!isOpen || !mapRef.current || !lastLogin?.lat || !lastLogin?.lng) return;
    
    const lastLat = parseFloat(lastLogin.lat);
    const lastLng = parseFloat(lastLogin.lng);
    
    if (isNaN(lastLat) || isNaN(lastLng)) return;

    const apiKey = GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('Google Maps API key not found');
      return;
    }

    function initializeMap() {
      if (!mapRef.current || !(window as any).google?.maps?.visualization) return;
      
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      
      const map = new (window as any).google.maps.Map(mapRef.current, {
        center: { lat: lastLat, lng: lastLng },
        zoom: 13,
        mapTypeId: 'roadmap',
      });
      
      mapInstanceRef.current = map;
      
      const heatmapData: Array<{location: any, weight: number}> = [];
      
      loginHistory.forEach((login: any) => {
        const loginLat = parseFloat(login.lat || login.location_lat);
        const loginLng = parseFloat(login.lng || login.location_lng);
        
        if (isNaN(loginLat) || isNaN(loginLng)) return;
        
        const R = 6371;
        const dLat = (loginLat - lastLat) * Math.PI / 180;
        const dLng = (loginLng - lastLng) * Math.PI / 180;
        const a = 
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(lastLat * Math.PI / 180) * Math.cos(loginLat * Math.PI / 180) *
          Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        
        const weight = Math.max(0.1, 1 / (1 + distance));
        heatmapData.push({
          location: new (window as any).google.maps.LatLng(loginLat, loginLng),
          weight
        });
      });
      
      const heatmap = new (window as any).google.maps.visualization.HeatmapLayer({
        data: heatmapData,
        radius: 50,
        opacity: 0.6,
        gradient: [
          'rgba(0, 255, 255, 0)',
          'rgba(0, 255, 255, 1)',
          'rgba(0, 191, 255, 1)',
          'rgba(0, 127, 255, 1)',
          'rgba(0, 63, 255, 1)',
          'rgba(0, 0, 255, 1)',
          'rgba(0, 0, 223, 1)',
          'rgba(0, 0, 191, 1)',
          'rgba(0, 0, 159, 1)',
          'rgba(0, 0, 127, 1)',
          'rgba(63, 0, 91, 1)',
          'rgba(127, 0, 63, 1)',
          'rgba(191, 0, 31, 1)',
          'rgba(255, 0, 0, 1)'
        ]
      });
      heatmap.setMap(map);
      
      new (window as any).google.maps.Marker({
        position: { lat: lastLat, lng: lastLng },
        map: map,
        title: 'Last Login',
        icon: {
          path: (window as any).google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#FF0000',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        }
      });
    }

    if ((window as any).google?.maps?.visualization) {
      initializeMap();
    } else if (!googleMapsLoaded) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=visualization`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setGoogleMapsLoaded(true);
        initializeMap();
      };
      document.head.appendChild(script);
    }
  }, [isOpen, loginHistory, lastLogin, googleMapsLoaded]);

  return (
    <div 
      ref={mapRef}
      className="w-full h-[500px] rounded overflow-hidden"
    />
  );
};

const balanceLabels: Array<{
  key: keyof NovaBalancePlans['aggregated'];
  label: string;
}> = [
  { key: 'airtime', label: 'Airtime' },
  { key: 'data', label: 'Data' },
  { key: 'voice', label: 'Voice' },
  { key: 'sms', label: 'SMS' },
];

type BalanceCategoryKey = 'airtime' | 'data' | 'voice' | 'sms';

/**
 * Formats a raw balance amount (bytes/seconds/count/currency) the same way
 * the backend formats each category's headline value, so hover tooltips on
 * the segment legend agree with the numbers elsewhere on the card instead of
 * introducing a third, differently-rounded framing of the same balance.
 */
const formatBalanceSegmentAmount = (key: BalanceCategoryKey, amount: number) => {
  if (key === 'airtime') return `R${amount.toFixed(2)}`;
  if (key === 'voice') return `${Math.floor(amount / 60)} min`;
  if (key === 'sms') return `${Math.round(amount)} SMS`;

  const mb = amount / 1_000_000;
  if (mb >= 1000) return `${(mb / 1000).toFixed(2)} GB`;
  return `${Math.round(mb)} MB`;
};

const getBalanceSegments = (balance: BalanceCategory) => {
  const total = Math.max(0, Number(balance.totalValue ?? 0));
  const available = Math.max(
    0,
    Number(balance.availableValue ?? balance.value ?? 0),
  );
  const used = Math.max(
    0,
    Number(
      balance.usedValue ??
        (total > 0 ? (balance.percentageUsed / 100) * total : 0),
    ),
  );
  const reserved = Math.max(
    0,
    Number(balance.reservedValue ?? Math.max(0, total - available - used)),
  );
  const scale = Math.max(total, available + reserved + used);
  const toPercent = (value: number) =>
    scale > 0 ? Math.min(100, Math.max(0, (value / scale) * 100)) : 0;

  return {
    total,
    available,
    used,
    reserved,
    availablePercent: toPercent(available),
    reservedPercent: toPercent(reserved),
  };
};

const BalanceSegmentBar = ({
  balance,
  categoryKey,
}: {
  balance: BalanceCategory;
  categoryKey: BalanceCategoryKey;
}) => {
  const segments = getBalanceSegments(balance);

  return (
    <div className="space-y-2">
      <div className="flex h-2 overflow-hidden rounded-full bg-secondary">
        <div
          className="bg-blue-600"
          style={{ width: `${segments.availablePercent}%` }}
        />
        <div
          className="bg-amber-400"
          style={{ width: `${segments.reservedPercent}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <Tooltip>
          <TooltipTrigger render={<span className="inline-flex cursor-default items-center gap-1" />}>
            <span className="size-2 rounded-sm bg-blue-600" />
            Available
          </TooltipTrigger>
          <TooltipContent>
            {formatBalanceSegmentAmount(categoryKey, segments.available)} available
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger render={<span className="inline-flex cursor-default items-center gap-1" />}>
            <span className="size-2 rounded-sm bg-amber-400" />
            Temporarily allocated
          </TooltipTrigger>
          <TooltipContent>
            {formatBalanceSegmentAmount(categoryKey, segments.reserved)} temporarily allocated
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

const bundleLabels: Array<{
  key: keyof NovaBalancePlans['bundles'];
  label: string;
}> = [
  { key: 'combo', label: 'Combo Bundles' },
  { key: 'data', label: 'Data Bundles' },
  { key: 'social', label: 'Social Bundles' },
  { key: 'voice', label: 'Voice Bundles' },
  { key: 'sms', label: 'SMS Bundles' },
];

const formatLinkedUserName = (user: NovaAccountDetails['linkedUsers'][number]) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || user.username || user.email || user.uuid;
};

const formatMoney = (value?: string | number | null, currency = 'ZAR') => {
  if (value === null || value === undefined || value === '') return '-';
  const amount = parseFloat(String(value));
  if (!Number.isFinite(amount)) return '-';
  return currency === 'ZAR'
    ? formatRand(amount)
    : `${currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const copyToClipboard = async (value: string, label = 'Value') => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}`);
  }
};

const CopyButton = ({
  value,
  label,
}: {
  value?: string | number | null;
  label: string;
}) => {
  if (value === null || value === undefined || value === '') return null;

  const text = String(value);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 shrink-0 p-0"
            aria-label={`Copy ${label}`}
            onClick={() => void copyToClipboard(text, label)}
          />
        }
      >
        <IconCopy className="size-3.5" />
      </TooltipTrigger>
      <TooltipContent>Copy {label}</TooltipContent>
    </Tooltip>
  );
};

const DetailRow = ({
  label,
  value,
  mono = false,
  copyValue,
}: {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  copyValue?: string | number | null;
}) => {
  if (value === null || value === undefined || value === '') return null;

  return (
    <div className="grid grid-cols-1 gap-1 border-b py-2 last:border-b-0 sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 items-start gap-2">
        <span className={cn('min-w-0 flex-1 break-words text-sm', mono && 'break-all font-mono text-xs')}>{value}</span>
        <CopyButton value={copyValue} label={label} />
      </dd>
    </div>
  );
};

const accordionTitleClass = 'text-base font-semibold';
const tableSubsectionCellClass = 'border-t bg-muted/30 pt-3 pb-1.5';

function SectionLabelWithCount({
  title,
  count,
  size = 'sm',
}: {
  title: string;
  count?: number;
  size?: 'sm' | 'base';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2',
        size === 'base' ? accordionTitleClass : 'text-sm font-semibold',
      )}
    >
      <span className="leading-none">{title}</span>
      {count !== undefined && (
        <Badge variant="secondary" className="h-5 min-w-5 shrink-0 justify-center px-1.5 text-xs font-medium">
          {count}
        </Badge>
      )}
    </span>
  );
}


const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

const SIM_SWAP_MINIMUM_ASSURANCE_LEVEL = 'SUMSUB_ID_LIVENESS';

const SIM_SWAP_REASONS = [
  { value: 'LOST_SIM_CARD', label: 'Lost SIM card' },
  { value: 'FAULTY_SIM_CARD', label: 'Faulty SIM card' },
  { value: 'STOLEN_SIM_CARD', label: 'Stolen SIM card' },
  { value: 'SIM_UPGRADE', label: 'SIM upgrade' },
];

const IDENTITY_ASSURANCE_LEVELS = [
  {
    value: 'NONE',
    label: 'None',
    rank: 0,
    simSwapEligible: false,
    description: 'No meaningful identity evidence on file.',
  },
  {
    value: 'SELF_ASSERTED',
    label: 'Self asserted',
    rank: 1,
    simSwapEligible: false,
    description: 'Identity captured by customer or agent; not independently verified.',
  },
  {
    value: 'PARTNER_ATTESTED',
    label: 'Partner attested',
    rank: 2,
    simSwapEligible: false,
    description: 'Partner portal RICA attestation only; Sumsub liveness not completed.',
  },
  {
    value: 'LEGACY_MIGRATION',
    label: 'Legacy migration',
    rank: 2,
    simSwapEligible: false,
    description: 'Migrated subscriber state; not a reusable Sumsub verification anchor.',
  },
  {
    value: 'SUMSUB_ID_LIVENESS',
    label: 'Sumsub ID + liveness',
    rank: 3,
    simSwapEligible: true,
    description: 'Minimum level required for SIM swap — Sumsub ID/passport and liveness passed.',
  },
  {
    value: 'SUMSUB_ID_LIVENESS_POA',
    label: 'Sumsub ID + liveness + POA',
    rank: 4,
    simSwapEligible: true,
    description: 'Sumsub verification with proof of address.',
  },
  {
    value: 'SUMSUB_ID_LIVENESS_POA_SOURCE_OF_FUNDS',
    label: 'Sumsub ID + liveness + POA + SOF',
    rank: 5,
    simSwapEligible: true,
    description: 'Full Sumsub verification including source of funds.',
  },
] as const;

const formatIdentityAssuranceLevel = (level: string) =>
  IDENTITY_ASSURANCE_LEVELS.find((entry) => entry.value === level)?.label ?? level;

const formatStatusLabel = (value?: string | null) => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const VOUCHER_STATUS_COPY: Record<string, { label: string; description: string }> = {
  REDEEMED: {
    label: 'Redeemed',
    description: 'This voucher has already been redeemed and cannot be redeemed again.',
  },
  UNREDEEMED: {
    label: 'Not yet redeemed',
    description: 'This voucher is confirmed and still available to redeem.',
  },
  AUTHORISED: {
    label: 'Authorised - not yet redeemed',
    description: 'The voucher purchase was authorised by the provider and is available to redeem.',
  },
  CREATED: {
    label: 'Created - not yet redeemed',
    description: 'The voucher was created and is available to redeem.',
  },
  EXPIRED: {
    label: 'Expired',
    description: 'This voucher has passed its expiry date and can no longer be redeemed.',
  },
  REVERSED: {
    label: 'Reversed',
    description: 'This voucher was reversed or refunded and can no longer be redeemed.',
  },
  UNKNOWN: {
    label: 'Status unclear',
    description: 'Nova could not confidently classify this voucher. Check the source status shown below.',
  },
};

const getVoucherStatusCopy = (status?: string | null) => {
  const key = (status || '').toUpperCase();
  return (
    VOUCHER_STATUS_COPY[key] ?? {
      label: formatStatusLabel(status),
      description: 'Raw status reported by the voucher provider.',
    }
  );
};

const formatCustomerCareText = (value?: string | null) => {
  if (!value) return '-';
  return IDENTITY_ASSURANCE_LEVELS.reduce(
    (text, level) => text.split(level.value).join(level.label),
    value,
  );
};

const rankIdentityAssuranceLevel = (level: string) =>
  IDENTITY_ASSURANCE_LEVELS.find((entry) => entry.value === level)?.rank ?? 0;

const meetsSimSwapAssuranceLevel = (level: string) =>
  rankIdentityAssuranceLevel(level) >= rankIdentityAssuranceLevel(SIM_SWAP_MINIMUM_ASSURANCE_LEVEL);

const buildIdentityAssurance = (account: Customer, novaAccountDetails: NovaAccountDetails | null) => {
  const numericMatch = typeof account.self_rica_photo_match_percentage_faceapp === 'number'
    ? account.self_rica_photo_match_percentage_faceapp
    : Number(account.self_rica_photo_match_percentage_faceapp);
  const faceMatchPercentage = Number.isFinite(numericMatch) ? numericMatch : null;
  const hasSelfie = Boolean(account.id_url || account.id_url_2 || account.selfie_filename);
  const hasIdNumber = hasValue(account.id_number);
  const hasRicaEntity = Boolean(novaAccountDetails?.ricaEntity?.uuid || account.self_rica === 1 || account.rica_agent);
  const hasLiveness = account.self_rica === 1 || faceMatchPercentage !== null;
  const faceMatchPassed = faceMatchPercentage === null ? hasSelfie : faceMatchPercentage >= 80;
  const simSwapReady = hasIdNumber && hasSelfie && hasLiveness && faceMatchPassed && hasRicaEntity;
  const completedSignals = [hasIdNumber, hasSelfie, hasLiveness, faceMatchPassed, hasRicaEntity].filter(Boolean).length;

  return {
    completedSignals,
    totalSignals: 5,
    simSwapReady,
    level: simSwapReady ? 'High assurance' : completedSignals >= 3 ? 'Partial assurance' : 'Needs verification',
    levelVariant: simSwapReady ? 'success' : completedSignals >= 3 ? 'warning' : 'secondary',
    signals: [
      {
        label: 'ID Number Verification',
        ready: hasIdNumber,
        value: hasIdNumber ? 'ID number present' : 'Missing ID number',
      },
      {
        label: 'Selfie Capture',
        ready: hasSelfie,
        value: hasSelfie ? 'Selfie available' : 'No selfie available',
      },
      {
        label: 'Liveness / Self RICA',
        ready: hasLiveness,
        value: hasLiveness ? 'Verification data present' : 'Awaiting liveness source',
      },
      {
        label: 'Face Match',
        ready: faceMatchPassed,
        value: faceMatchPercentage === null ? 'No match score available' : String(faceMatchPercentage.toFixed(2)) + '% match',
      },
      {
        label: 'RICA Entity',
        ready: hasRicaEntity,
        value: novaAccountDetails?.ricaEntity?.uuid || (hasRicaEntity ? 'RICA data present' : 'No RICA entity linked'),
        mono: Boolean(novaAccountDetails?.ricaEntity?.uuid),
      },
    ],
  };
};

interface Customer {
  i_customer?: number;
  id_number?: string;
  msisdn?: string;
  msisdn_dma?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  created_at?: string;
  sim_card?: any;
  [key: string]: any;
}

export function CustomersPage() {
  const [activeSearchType, setActiveSearchType] = useState<'idNumber' | 'account' | 'wallet' | null>(null);
  const [walletQuery, setWalletQuery] = useState('');
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [walletResult, setWalletResult] = useState<{ graph: CustomerGraph; walletAccountNumber: string } | null>(null);
  
  const [idNumberQuery, setIdNumberQuery] = useState('');
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalAccounts, setTotalAccounts] = useState(0);
  
  const [accountQuery, setAccountQuery] = useState('');
  /** Query string that successfully loaded the current `account` (not the live input). */
  const [loadedAccountSearchQuery, setLoadedAccountSearchQuery] = useState<string | null>(null);
  const [loadedAccountSearchType, setLoadedAccountSearchType] = useState<string | null>(null);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [account, setAccount] = useState<any | null>(null);
  const suspension = getAccountSuspension(account);
  const [simInventoryResult, setSimInventoryResult] = useState<SimInventorySearchResult | null>(null);
  const [novaAccountDetails, setNovaAccountDetails] = useState<NovaAccountDetails | null>(null);
  const [loadingNovaAccountDetails, setLoadingNovaAccountDetails] = useState(false);
  const [novaAccountDetailsError, setNovaAccountDetailsError] = useState('');
  const [novaBalancePlans, setNovaBalancePlans] = useState<NovaBalancePlans | null>(null);
  const [loadingNovaBalancePlans, setLoadingNovaBalancePlans] = useState(false);
  const [novaBalancePlansError, setNovaBalancePlansError] = useState('');
  const [identityAssurance, setIdentityAssurance] = useState<IdentityAssuranceResponse | null>(null);
  const [loadingIdentityAssurance, setLoadingIdentityAssurance] = useState(false);
  const [identityAssuranceError, setIdentityAssuranceError] = useState('');
  const [simHistory, setSimHistory] = useState<SimHistoryResponse | null>(null);
  const [loadingSimHistory, setLoadingSimHistory] = useState(false);
  const [simHistoryError, setSimHistoryError] = useState('');
  const [voucherStatus, setVoucherStatus] = useState<VoucherStatusResponse | null>(null);
  const [loadingVoucherStatus, setLoadingVoucherStatus] = useState(false);
  const [voucherStatusError, setVoucherStatusError] = useState('');
  const [resendingVoucherId, setResendingVoucherId] = useState<string | null>(null);
  const [voucherResendConfirmOpen, setVoucherResendConfirmOpen] = useState(false);
  const [pendingVoucherResend, setPendingVoucherResend] = useState<VoucherStatusResponse['vouchers'][number] | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherStatusResponse['vouchers'][number] | null>(null);
  const [voucherSmsResult, setVoucherSmsResult] = useState<{ success: boolean; message: string } | null>(null);
  const [customerMessages, setCustomerMessages] = useState<CustomerMessagesResponse | null>(null);
  const [loadingCustomerMessages, setLoadingCustomerMessages] = useState(false);
  const [customerMessagesError, setCustomerMessagesError] = useState('');
  const [messageHistorySearch, setMessageHistorySearch] = useState('');
  const [messageHistoryStatusFilter, setMessageHistoryStatusFilter] = useState('ALL');
  const [messageHistoryFromDate, setMessageHistoryFromDate] = useState('');
  const [messageHistoryToDate, setMessageHistoryToDate] = useState('');
  const [simSwapPromptOpen, setSimSwapPromptOpen] = useState(false);
  const [simSwapPromptMsisdn, setSimSwapPromptMsisdn] = useState('');
  const [simSwapPromptReason, setSimSwapPromptReason] = useState('');
  const [sendingSimSwapPrompt, setSendingSimSwapPrompt] = useState(false);
  const [simSwapPromptResult, setSimSwapPromptResult] = useState<{ success: boolean; message: string } | null>(null);
  const [simSwapNewIccid, setSimSwapNewIccid] = useState('');
  const [simSwapVerificationNow, setSimSwapVerificationNow] = useState(() => Date.now());
  const [simSwapReason, setSimSwapReason] = useState('LOST_SIM_CARD');
  const [submittingSimSwap, setSubmittingSimSwap] = useState(false);
  const [simSwapExecutionResult, setSimSwapExecutionResult] = useState<{ success: boolean; message: string } | null>(null);
  const [idNumberAccountCount, setIdNumberAccountCount] = useState(0);
  const mtnLookupVersion = useRef(0);
  const [mtnStatus, setMtnStatus] = useState<any | null>(null);
  const [loadingMtnStatus, setLoadingMtnStatus] = useState(false);
  const [mtnError, setMtnError] = useState('');
  const [portStatus, setPortStatus] = useState<any | null>(null);
  const [loadingPortStatus, setLoadingPortStatus] = useState(false);
  const [portStatusError, setPortStatusError] = useState('');
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [loadingAiSummary, setLoadingAiSummary] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState<string | null>(null);
  const [dmaDiagnostics, setDmaDiagnostics] = useState<any | null>(null);
  const [loadingDmaDiagnostics, setLoadingDmaDiagnostics] = useState(false);
  const [fixingChurn, setFixingChurn] = useState(false);
  const [sparMobileCardDetails, setSparMobileCardDetails] = useState<any | null>(null);
  const [loadingSparMobileCard, setLoadingSparMobileCard] = useState(false);
  const [sparMobileCardError, setSparMobileCardError] = useState('');
  const [creatingSparMobileAccount, setCreatingSparMobileAccount] = useState(false);
  const [sparMobileAccountCreated, setSparMobileAccountCreated] = useState<any | null>(null);
  const [sparMobileCreateModalOpen, setSparMobileCreateModalOpen] = useState(false);
  const [sparMobileCreateResult, setSparMobileCreateResult] = useState<{ success: boolean; message: string } | null>(null);
  const [changingPrimaryPhone, setChangingPrimaryPhone] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [transactionsSummary, setTransactionsSummary] = useState<any | null>(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionsDays, setTransactionsDays] = useState(31);
  const [selectedTransaction, setSelectedTransaction] = useState<CustomerTransaction | null>(null);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [transfersSummaryByUser, setTransfersSummaryByUser] = useState<any[]>([]);
  const [transfersOverallSummary, setTransfersOverallSummary] = useState<any | null>(null);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [transfersError, setTransfersError] = useState('');
  const [transfersTab, setTransfersTab] = useState(0);
  const [transfersDays, setTransfersDays] = useState<number | null>(31);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [selectedImageTitle, setSelectedImageTitle] = useState<string>('');
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapLocation, setMapLocation] = useState<{ lat: string; lng: string } | null>(null);
  const [loginHistoryDialogOpen, setLoginHistoryDialogOpen] = useState(false);
  const [loginHistory, setLoginHistory] = useState<any[]>([]);
  const [loginHistoryBinnedCounts, setLoginHistoryBinnedCounts] = useState<any>(null);
  const [loadingLoginHistory, setLoadingLoginHistory] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [unsuspendDialogOpen, setUnsuspendDialogOpen] = useState(false);
  const [unsuspendReason, setUnsuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [customerHistory, setCustomerHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [customerRewards, setCustomerRewards] = useState<CustomerReward[]>([]);
  const [rewardUsage, setRewardUsage] = useState<CustomerRewardsResponse['rewardUsage'] | null>(null);
  const [rewardBlockDialogOpen, setRewardBlockDialogOpen] = useState(false);
  const [rewardUnblockDialogOpen, setRewardUnblockDialogOpen] = useState(false);
  const [rewardBlockReason, setRewardBlockReason] = useState('');
  const [updatingRewardBlock, setUpdatingRewardBlock] = useState(false);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [rewardsError, setRewardsError] = useState('');
  const [processingReward, setProcessingReward] = useState<number | null>(null);
  const [rewardsSuccess, setRewardsSuccess] = useState('');
  const [resettingDma, setResettingDma] = useState(false);
  const [resetDmaConfirmOpen, setResetDmaConfirmOpen] = useState(false);

  const { hasPageAccess, hasWriteAccess } = useAuth();
  const canPerformCustomerCareActions = hasWriteAccess('customer-care-actions');
  const canManageCustomerLifecycle = hasWriteAccess('customer-lifecycle');
  const canPerformSimSwap = hasWriteAccess('customer-sim-swap');

  const currentSimSwapVerification =
    identityAssurance?.identityAssurance?.simSwapVerification ?? null;
  const currentSimSwapVerificationStatus = currentSimSwapVerification?.status ?? null;
  const currentSimSwapVerificationPassed = isSimSwapVerificationCurrent(
    currentSimSwapVerification,
    simSwapVerificationNow,
  );
  const currentSimSwapSourceSimIccid =
    account?.sim_serial_number || novaAccountDetails?.simCard?.iccid || null;
  const currentSimSwapProcess = identityAssurance?.simSwap?.currentProcess ?? null;
  const currentSimSwapIdentityEligible =
    identityAssurance?.identityAssurance?.eligibleForSimSwap === true;
  const simSwapIdentityGateReady =
    currentSimSwapVerificationPassed && currentSimSwapIdentityEligible;
  const simSwapInputEnabled =
    canPerformSimSwap &&
    simSwapIdentityGateReady &&
    identityAssurance?.simSwap?.enabled === true &&
    !currentSimSwapProcess;
  const loadReplacementSimEligibility = useCallback(
    async (candidateIccid: string): Promise<ReplacementSimEligibility> => {
      if (!account?.i_customer) {
        throw new Error('Customer account is not loaded.');
      }

      const params = new URLSearchParams({
        i_customer: String(account.i_customer),
        new_iccid: candidateIccid,
      });
      if (account.account_uuid) params.set('account_uuid', account.account_uuid);
      if (account.mvno) params.set('mvno', account.mvno);

      try {
        const res = await novaOpsApi.get<ReplacementSimEligibility>(
          `/ops/customers/sim-swap/replacement-eligibility?${params.toString()}`,
        );
        return res.data;
      } catch (error: any) {
        throw new Error(
          getApiErrorMessage(error, 'Could not validate replacement SIM'),
        );
      }
    },
    [account?.account_uuid, account?.i_customer, account?.mvno],
  );
  const {
    eligibility: replacementSimEligibility,
    checking: checkingReplacementSim,
    isCurrent: replacementSimEligibilityIsCurrent,
    recheck: recheckReplacementSim,
  } = useSimSwapReplacementEligibility({
    iccid: simSwapNewIccid,
    account,
    canPerform: canPerformSimSwap,
    inputEnabled: simSwapInputEnabled,
    verification: currentSimSwapVerification,
    sourceSimIccid: currentSimSwapSourceSimIccid,
    load: loadReplacementSimEligibility,
  });
  const simSwapReadyForSubmit =
    simSwapIdentityGateReady &&
    identityAssurance?.simSwap?.enabled === true &&
    identityAssurance?.simSwap?.eligible === true &&
    replacementSimEligibility?.eligible === true &&
    replacementSimEligibilityIsCurrent &&
    !currentSimSwapProcess;
  const simSwapSummaryLabel = getSimSwapSummaryLabel({
    verificationStatus: currentSimSwapVerificationStatus,
    identityEligible: simSwapIdentityGateReady,
    replacementEligible: replacementSimEligibility?.eligible === true,
    replacementCheckCurrent: replacementSimEligibilityIsCurrent,
    hasPendingSwap: Boolean(currentSimSwapProcess),
  });

  useEffect(() => {
    setSimSwapVerificationNow(Date.now());

    if (
      currentSimSwapVerificationStatus !== 'PASSED' ||
      !currentSimSwapVerification?.expiresAt
    ) {
      return;
    }

    const expiresAt = Date.parse(currentSimSwapVerification.expiresAt);
    if (!Number.isFinite(expiresAt)) return;

    const timeout = window.setTimeout(
      () => setSimSwapVerificationNow(Date.now()),
      Math.max(0, expiresAt - Date.now() + 1),
    );

    return () => window.clearTimeout(timeout);
  }, [
    currentSimSwapVerificationStatus,
    currentSimSwapVerification?.expiresAt,
  ]);

  const handleCustomerSearch = async (idNumber?: string) => {
    const useParam = idNumber !== undefined;
    const rawValue = useParam ? idNumber : idNumberQuery;
    
    console.log('handleCustomerSearch - rawValue:', rawValue, 'type:', typeof rawValue, 'isObject:', typeof rawValue === 'object');
    
    let searchIdNumber = '';
    
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      searchIdNumber = '';
    } else if (typeof rawValue === 'string') {
      searchIdNumber = rawValue.trim();
    } else if (typeof rawValue === 'object' && rawValue !== null && !Array.isArray(rawValue)) {
      console.warn('handleCustomerSearch - rawValue is an object:', rawValue);
      setCustomerError('Invalid ID Number. Please enter a valid 13-digit ID Number.');
      setIdNumberQuery('');
      return;
    } else {
      searchIdNumber = String(rawValue).trim();
      if (searchIdNumber === '[object Object]') {
        setCustomerError('Invalid ID Number. Please enter a valid 13-digit ID Number.');
        setIdNumberQuery('');
        return;
      }
    }
    
    if (!searchIdNumber) {
      setCustomerError('Please enter an ID Number');
      return;
    }

    if (useParam) {
      setIdNumberQuery(searchIdNumber);
    }

    setActiveSearchType(null);
    
    setAccount(null);
    setSimInventoryResult(null);
    setLoadedAccountSearchQuery(null);
    setLoadedAccountSearchType(null);
    setNovaAccountDetails(null);
    setNovaAccountDetailsError('');
    setNovaBalancePlans(null);
    setNovaBalancePlansError('');
    setIdentityAssurance(null);
    setIdentityAssuranceError('');
    setSimHistory(null);
    setSimHistoryError('');
    setVoucherStatus(null);
    setVoucherStatusError('');
    setVoucherSmsResult(null);
    setResendingVoucherId(null);
    setVoucherResendConfirmOpen(false);
    setPendingVoucherResend(null);
    setCustomerMessages(null);
    setCustomerMessagesError('');
    setMessageHistorySearch('');
    setMessageHistoryStatusFilter('ALL');
    setMessageHistoryFromDate('');
    setMessageHistoryToDate('');
    setSimSwapPromptOpen(false);
    setSimSwapPromptMsisdn('');
    setSimSwapPromptReason('');
    setSimSwapPromptResult(null);
    setSimSwapNewIccid('');
    setSimSwapReason('LOST_SIM_CARD');
    setSimSwapExecutionResult(null);
    setSubmittingSimSwap(false);
    setIdNumberAccountCount(0);
    mtnLookupVersion.current += 1;
    setMtnStatus(null);
    setPortStatus(null);
    setSparMobileCardDetails(null);
    setSparMobileAccountCreated(null);
    setAiSummary(null);
    setAiSummaryError(null);
    setDmaDiagnostics(null);
    setAccountError('');
    setAccountQuery('');
    setLoadingAccount(false);
    setLoadingNovaAccountDetails(false);
    setLoadingNovaBalancePlans(false);
    setLoadingIdentityAssurance(false);
    setLoadingSimHistory(false);
    setLoadingVoucherStatus(false);
    setResendingVoucherId(null);
    setLoadingCustomerMessages(false);
    setLoadingMtnStatus(false);
    setLoadingSparMobileCard(false);

    setCustomers([]);
    setTotalAccounts(0);
    setCustomerError('');
    setWalletResult(null);
    setWalletError('');
    setLoadingCustomer(true);
    
    setActiveSearchType('idNumber');

    try {
      const res = await novaOpsApi.get(`/ops/customers/search-customer?idNumber=${encodeURIComponent(searchIdNumber)}`);
      setCustomers(res.data.customers || []);
      setTotalAccounts(res.data.totalAccounts || 0);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setCustomerError('No accounts found for this ID Number');
      } else {
        setCustomerError(getApiErrorMessage(err, 'Search failed'));
      }
    } finally {
      setLoadingCustomer(false);
    }
  };

  const tenantQuery = (mvno?: string | null) =>
    mvno ? `&mvno=${encodeURIComponent(mvno)}` : '';

  const canonicalAccountQuery = (
    iCustomer: number,
    mvno?: string | null,
    accountUuid?: string | null,
  ) => {
    const params = new URLSearchParams({ i_customer: String(iCustomer) });
    if (accountUuid) params.set('account_uuid', accountUuid);
    if (mvno) params.set('mvno', mvno);
    return params.toString();
  };

  const buildNovaLocalMtnStatus = (accountData: any) => {
    const status = accountData.status || accountData.account_status || 'UNKNOWN';
    const simStatus = accountData.sim_status || accountData.simCard?.status;
    const state = status || simStatus || 'UNKNOWN';

    return {
      isActive: status === 'ACTIVE' || simStatus === 'ACTIVE' || accountData.is_active === true,
      statusCode: 'LOCAL',
      statusMessage: 'Nova local account/SIM status (fallback — live MTN SOA lookup unavailable).',
      subscriptionDetails: {
        subscriptionState: state,
        iccid: accountData.sim_serial_number || accountData.simCard?.iccid || null,
        imsi: accountData.sim_card_info?.imsi || null,
        activationDate: accountData.activated_at || accountData.activatedAt || accountData.provisioned_at || null,
        lastUpdatedDate: accountData.updated_at || accountData.updatedAt || null,
        activeProductList: [],
      },
    };
  };

  const fetchNovaAccountDetails = async (
    iCustomer: number | null | undefined,
    mvno?: string | null,
    accountUuid?: string | null,
  ) => {
    setLoadingNovaAccountDetails(true);
    setNovaAccountDetailsError('');
    try {
      const selector = accountUuid
        ? `account_uuid=${encodeURIComponent(accountUuid)}`
        : `i_customer=${iCustomer}`;
      const res = await novaOpsApi.get(`/ops/customers/account-details?${selector}${tenantQuery(mvno)}`);
      const accountDetails = res.data?.account;
      setNovaAccountDetails(
        accountDetails && typeof accountDetails === 'object'
          ? {
              ...accountDetails,
              linkedUsers: Array.isArray(accountDetails.linkedUsers)
                ? accountDetails.linkedUsers
                : [],
            }
          : null,
      );
    } catch (err: any) {
      console.error('Failed to fetch Nova account details:', err);
      setNovaAccountDetails(null);
      setNovaAccountDetailsError(getApiErrorMessage(err, 'Failed to load Nova account details'));
    } finally {
      setLoadingNovaAccountDetails(false);
    }
  };

  const fetchNovaBalancePlans = async (
    iCustomer: number | null | undefined,
    mvno?: string | null,
    accountUuid?: string | null,
  ) => {
    setLoadingNovaBalancePlans(true);
    setNovaBalancePlansError('');
    try {
      const selector = accountUuid
        ? `account_uuid=${encodeURIComponent(accountUuid)}`
        : `i_customer=${iCustomer}`;
      const res = await novaOpsApi.get(`/ops/customers/balance-plans?${selector}${tenantQuery(mvno)}`);
      const balancePlans = res.data;
      if (
        balancePlans &&
        typeof balancePlans === 'object' &&
        balancePlans.aggregated &&
        balancePlans.bundles
      ) {
        setNovaBalancePlans(balancePlans);
      } else {
        setNovaBalancePlans(null);
        setNovaBalancePlansError('Balance and plans data returned in an unexpected format.');
      }
    } catch (err: any) {
      console.error('Failed to fetch Nova balance and plans:', err);
      setNovaBalancePlans(null);
      setNovaBalancePlansError(getApiErrorMessage(err, 'Failed to load balance and plans'));
    } finally {
      setLoadingNovaBalancePlans(false);
    }
  };

  const fetchIdentityAssurance = async (iCustomer: number, mvno?: string | null, accountUuid?: string | null) => {
    setLoadingIdentityAssurance(true);
    setIdentityAssuranceError('');
    try {
      const res = await novaOpsApi.get(`/ops/customers/identity-assurance?${canonicalAccountQuery(iCustomer, mvno, accountUuid)}`);
      setIdentityAssurance(res.data?.identityAssurance ? res.data : null);
    } catch (err: any) {
      console.error('Failed to fetch identity assurance:', err);
      setIdentityAssurance(null);
      setIdentityAssuranceError(getApiErrorMessage(err, 'Failed to load identity assurance'));
    } finally {
      setLoadingIdentityAssurance(false);
    }
  };

  const fetchSimHistory = async (
    iCustomer: number | null | undefined,
    mvno?: string | null,
    accountUuid?: string | null,
  ) => {
    if (!iCustomer && !accountUuid) return;
    setLoadingSimHistory(true);
    setSimHistoryError('');
    try {
      const selector = accountUuid
        ? `account_uuid=${encodeURIComponent(accountUuid)}`
        : `i_customer=${iCustomer}`;
      const res = await novaOpsApi.get<SimHistoryResponse>(`/ops/customers/sim-history?${selector}${tenantQuery(mvno)}`);
      setSimHistory(res.data || null);
    } catch (err: any) {
      console.error('Failed to fetch SIM history:', err);
      setSimHistory(null);
      setSimHistoryError(getApiErrorMessage(err, 'Failed to load SIM history'));
    } finally {
      setLoadingSimHistory(false);
    }
  };

  const fetchVoucherStatus = async (iCustomer: number, mvno?: string | null, accountUuid?: string | null) => {
    setLoadingVoucherStatus(true);
    setVoucherStatusError('');
    try {
      const res = await novaOpsApi.get<VoucherStatusResponse>(`/ops/customers/voucher-status?${canonicalAccountQuery(iCustomer, mvno, accountUuid)}`);
      setVoucherStatus(res.data || null);
    } catch (err: any) {
      console.error('Failed to fetch voucher status:', err);
      setVoucherStatus(null);
      setVoucherStatusError(getApiErrorMessage(err, 'Failed to load voucher status'));
    } finally {
      setLoadingVoucherStatus(false);
    }
  };

  const getVoucherCareStatus = (voucher: VoucherStatusResponse['vouchers'][number]) =>
    voucher.effective_status || voucher.status || 'UNKNOWN';

  const getVoucherRoleLabel = (role?: string | null) => {
    switch (role) {
      case 'PURCHASED_AND_REDEEMED_BY_CUSTOMER':
        return 'Purchased + redeemed here';
      case 'PURCHASED_BY_CUSTOMER_REDEEMED_ELSEWHERE':
        return 'Purchased here, redeemed elsewhere';
      case 'PURCHASED_BY_CUSTOMER':
        return 'Purchased here';
      case 'THIRD_PARTY_VOUCHER_REDEEMED_BY_CUSTOMER':
        return 'Third-party, redeemed here';
      default:
        return role ? formatStatusLabel(role) : 'Related voucher';
    }
  };

  const isPurchasedVoucher = (voucher: VoucherStatusResponse['vouchers'][number]) =>
    [
      'PURCHASED_BY_CUSTOMER',
      'PURCHASED_AND_REDEEMED_BY_CUSTOMER',
      'PURCHASED_BY_CUSTOMER_REDEEMED_ELSEWHERE',
    ].includes(String(voucher.customer_voucher_role ?? '').toUpperCase());

  const isRedeemedByCustomerVoucher = (voucher: VoucherStatusResponse['vouchers'][number]) =>
    [
      'PURCHASED_AND_REDEEMED_BY_CUSTOMER',
      'THIRD_PARTY_VOUCHER_REDEEMED_BY_CUSTOMER',
    ].includes(String(voucher.customer_voucher_role ?? '').toUpperCase());

  const canResendVoucherSms = (voucher: VoucherStatusResponse['vouchers'][number]) => {
    const status = getVoucherCareStatus(voucher).toUpperCase();
    return (
      (voucher.code_available || voucher.formatted_code_available) &&
      isPurchasedVoucher(voucher) &&
      ['UNREDEEMED', 'AUTHORISED', 'CREATED'].includes(status)
    );
  };

  const getVoucherCodeDisplay = (voucher: VoucherStatusResponse['vouchers'][number]) => {
    const display = voucher.code_display || voucher.code_masked || null;
    const isFull = voucher.code_display_mode === 'full';
    const hasCode = voucher.code_available || voucher.formatted_code_available || Boolean(display);
    return {
      display,
      isFull,
      label: isFull ? 'Full Code' : 'Masked Code',
      fallback: hasCode ? 'Voucher code captured, hidden' : null,
    };
  };

  const getCustomerSmsRecipient = () => account?.msisdn || account?.msisdn_dma || null;

  const edrMsisdnFilter = useMemo(
    () =>
      resolveEdrMsisdnFilter(
        loadedAccountSearchQuery,
        loadedAccountSearchType,
        account,
      ),
    [account, loadedAccountSearchQuery, loadedAccountSearchType],
  );

  const buildVoucherResendSmsPreview = (voucher: VoucherStatusResponse['vouchers'][number]) => {
    const productName = voucher.product_name?.trim() || 'voucher';
    const amount =
      voucher.amount !== null && voucher.amount !== undefined
        ? ` (${voucher.amount})`
        : '';
    const codeDisplay = voucher.code_masked?.trim() || '[full voucher code]';
    return `Your ${productName}${amount} voucher code is: ${codeDisplay}`;
  };

  const openVoucherResendConfirm = (voucher: VoucherStatusResponse['vouchers'][number]) => {
    setPendingVoucherResend(voucher);
    setVoucherResendConfirmOpen(true);
  };

  const confirmVoucherResendSms = async () => {
    if (!pendingVoucherResend) return;
    const voucher = pendingVoucherResend;
    setVoucherResendConfirmOpen(false);
    setPendingVoucherResend(null);
    await resendVoucherSms(voucher);
  };

  const fetchCustomerMessages = async (iCustomer: number, mvno?: string | null, accountUuid?: string | null) => {
    setLoadingCustomerMessages(true);
    setCustomerMessagesError('');
    try {
      const res = await novaOpsApi.get<CustomerMessagesResponse>(`/ops/customers/messages?${canonicalAccountQuery(iCustomer, mvno, accountUuid)}`);
      setCustomerMessages(res.data || null);
    } catch (err: any) {
      console.error('Failed to fetch customer messages:', err);
      setCustomerMessages(null);
      setCustomerMessagesError(getApiErrorMessage(err, 'Failed to load customer messages'));
    } finally {
      setLoadingCustomerMessages(false);
    }
  };

  const normalizePhoneInput = (value: string) =>
    value.trim().replace(/[^\d+]/g, '').replace(/^\+/, '');

  const openSimSwapPromptDialog = () => {
    setSimSwapPromptMsisdn(getCustomerSmsRecipient() ?? '');
    setSimSwapPromptReason('');
    setSimSwapPromptResult(null);
    setSimSwapPromptOpen(true);
  };

  const sendSimSwapPrompt = async (deliveryChannel: 'WHATSAPP' | 'SMS') => {
    if (!account?.i_customer || !canPerformSimSwap || !canSendSimSwapVerification(currentSimSwapVerification, sendingSimSwapPrompt || loadingIdentityAssurance)) return;
    const deliveryMsisdn = normalizePhoneInput(simSwapPromptMsisdn);
    if (!deliveryMsisdn) {
      setSimSwapPromptResult({
        success: false,
        message: 'Enter the number the customer can access.',
      });
      return;
    }

    setSendingSimSwapPrompt(true);
    setSimSwapPromptResult(null);
    try {
      const res = await sendAndRefreshSimSwapVerification(
        () => novaOpsApi.post(
        '/ops/customers/sim-swap/verification-prompt',
        {
          i_customer: account.i_customer,
          account_uuid: account.account_uuid,
          mvno: account.mvno,
          delivery_msisdn: deliveryMsisdn,
          delivery_channel: deliveryChannel,
          reason: simSwapPromptReason.trim() || undefined,
        },
        ),
        () => fetchIdentityAssurance(account.i_customer, account.mvno, account.account_uuid),
        () => fetchCustomerMessages(account.i_customer, account.mvno, account.account_uuid),
      );
      setSimSwapPromptResult({
        success: true,
        message: res.data?.message || 'SIM swap identity check submitted.',
      });
    } catch (err: any) {
      setSimSwapPromptResult({
        success: false,
        message: getApiErrorMessage(
          err,
          'Failed to send SIM swap identity check',
        ),
      });
    } finally {
      setSendingSimSwapPrompt(false);
    }
  };

  const submitSimSwap = async () => {
    if (!account?.i_customer) return;

    const cleanedIccid = simSwapNewIccid.trim().replace(/\s+/g, '');
    if (!/^\d{18,22}$/.test(cleanedIccid)) {
      setSimSwapExecutionResult({
        success: false,
        message: 'Enter a valid replacement ICCID between 18 and 22 digits.',
      });
      return;
    }
    if (!currentSimSwapVerificationPassed || !currentSimSwapIdentityEligible) {
      setSimSwapExecutionResult({
        success: false,
        message: 'Complete the current SIM swap identity verification before submitting.',
      });
      return;
    }
    if (!identityAssurance?.simSwap?.enabled || !identityAssurance.simSwap.eligible) {
      setSimSwapExecutionResult({
        success: false,
        message:
          identityAssurance?.simSwap?.disabledReason ||
          'SIM swap is not currently eligible for this account.',
      });
      return;
    }
    if (
      !replacementSimEligibility?.eligible ||
      !replacementSimEligibilityIsCurrent
    ) {
      setSimSwapExecutionResult({
        success: false,
        message:
          replacementSimEligibility?.blockers
            .map(formatReplacementSimBlocker)
            .join(' ') ||
          'Recheck the replacement SIM before submitting.',
      });
      return;
    }

    const confirmed = window.confirm(
      `Submit SIM swap for ${account.msisdn || 'this account'} to ICCID ${cleanedIccid}?`,
    );
    if (!confirmed) return;

    setSubmittingSimSwap(true);
    setSimSwapExecutionResult(null);
    try {
      const res = await novaOpsApi.post('/ops/customers/sim-swap', {
        i_customer: account.i_customer,
        account_uuid: account.account_uuid,
        mvno: account.mvno,
        new_iccid: cleanedIccid,
        swap_reason: simSwapReason,
      });
      const status = res.data?.status || res.data?.simSwap?.simSwapStatus;
      const transactionId = res.data?.simSwap?.transactionId;
      setSimSwapExecutionResult({
        success: true,
        message:
          res.data?.message ||
          `SIM swap submitted${status ? ` (${formatStatusLabel(status)})` : ''}${transactionId ? `, transaction ${transactionId}` : ''}.`,
      });
      setSimSwapNewIccid('');
      await Promise.all([
        fetchSimHistory(account.i_customer, account.mvno, account.account_uuid),
        fetchIdentityAssurance(account.i_customer, account.mvno, account.account_uuid),
      ]);
    } catch (err: any) {
      setSimSwapExecutionResult({
        success: false,
        message: getApiErrorMessage(err, 'Failed to submit SIM swap'),
      });
    } finally {
      setSubmittingSimSwap(false);
    }
  };

  useEffect(() => {
    setSimSwapNewIccid('');
    setSimSwapExecutionResult(null);
  }, [
    account?.i_customer,
    account?.account_uuid,
    currentSimSwapVerificationStatus,
    currentSimSwapVerification?.verificationRunUuid,
    currentSimSwapVerificationPassed,
    currentSimSwapSourceSimIccid,
  ]);

  const resendVoucherSms = async (voucher: VoucherStatusResponse['vouchers'][number]) => {
    if (!account?.i_customer) return;
    setResendingVoucherId(String(voucher.id));
    setVoucherSmsResult(null);
    try {
      const res = await novaOpsApi.post('/ops/customers/voucher-status/resend-sms', {
        i_customer: account.i_customer,
        account_uuid: account.account_uuid,
        mvno: account.mvno,
        voucher_id: String(voucher.id),
      });
      setVoucherSmsResult({
        success: true,
        message: res.data?.message || 'Voucher SMS submitted to provider.',
      });
      await Promise.all([
        fetchVoucherStatus(account.i_customer, account.mvno, account.account_uuid),
        fetchCustomerMessages(account.i_customer, account.mvno, account.account_uuid),
      ]);
    } catch (err: any) {
      setVoucherSmsResult({
        success: false,
        message: getApiErrorMessage(err, 'Failed to resend voucher SMS'),
      });
    } finally {
      setResendingVoucherId(null);
    }
  };

  const fetchNovaAccountSupportData = async (
    iCustomer?: number | null,
    mvno?: string | null,
    appUserUuid?: string | null,
    accountUuid?: string | null,
  ) => {
    if (!iCustomer && !appUserUuid && !accountUuid) return;
    const supportRequests: Promise<unknown>[] = [];
    if (iCustomer || accountUuid) {
      supportRequests.push(
        fetchNovaAccountDetails(iCustomer, mvno, accountUuid),
        fetchNovaBalancePlans(iCustomer, mvno, accountUuid),
      );
    }
    if (iCustomer || accountUuid) {
      supportRequests.push(fetchSimHistory(iCustomer, mvno, accountUuid));
    }
    if (iCustomer) {
      supportRequests.push(
        fetchIdentityAssurance(iCustomer, mvno, accountUuid),
        fetchVoucherStatus(iCustomer, mvno, accountUuid),
      );
    }
    await Promise.all(supportRequests);
  };

  const handleWalletSearch = async (query?: string) => {
    const searchValue = String(query || walletQuery || '').replace(/\s+/g, '');
    if (!searchValue) {
      setWalletError('Please enter a wallet account number');
      return;
    }
    if (query) setWalletQuery(searchValue);
    setIdNumberQuery('');
    setAccountQuery('');
    setCustomers([]);
    setTotalAccounts(0);
    setCustomerError('');
    setAccount(null);
    setSimInventoryResult(null);
    setAccountError('');
    setWalletError('');
    setWalletResult(null);
    setLoadingWallet(true);
    setActiveSearchType('wallet');
    try {
      const result = await fetchCustomerGraph('wallet', searchValue);
      const graph = result.graphs?.[0];
      if (!graph) {
        setWalletError('No wallet found with that account number');
        return;
      }
      setWalletResult({ graph, walletAccountNumber: searchValue });
    } catch (err: any) {
      if (err.response?.status === 404) {
        setWalletError('No wallet found with that account number');
      } else if (err.response?.status === 409) {
        setWalletError('More than one MVNO holds this wallet number; contact engineering');
      } else {
        setWalletError(getApiErrorMessage(err, 'Wallet search failed'));
      }
    } finally {
      setLoadingWallet(false);
    }
  };

  const handleAccountSearch = async (query?: string) => {
    const searchQuery = String(query || accountQuery || '').trim();
    if (!searchQuery) {
      setAccountError('Please enter a MSISDN, ICCID, username, UUID, or customer ID');
      return;
    }
    if (isWalletAccountNumber(searchQuery)) {
      setAccountQuery('');
      await handleWalletSearch(searchQuery);
      return;
    }

    setIdNumberQuery('');
    setWalletResult(null);
    setWalletError('');

    if (query) {
      setAccountQuery(String(query));
    }

    setActiveSearchType(null);
    
    setCustomers([]);
    setTotalAccounts(0);
    setCustomerError('');
    setLoadingCustomer(false);

    setAccount(null);
    setSimInventoryResult(null);
    setLoadedAccountSearchQuery(null);
    setLoadedAccountSearchType(null);
    setNovaAccountDetails(null);
    setNovaAccountDetailsError('');
    setNovaBalancePlans(null);
    setNovaBalancePlansError('');
    setIdentityAssurance(null);
    setIdentityAssuranceError('');
    setSimHistory(null);
    setSimHistoryError('');
    setVoucherStatus(null);
    setVoucherStatusError('');
    setVoucherSmsResult(null);
    setResendingVoucherId(null);
    setVoucherResendConfirmOpen(false);
    setPendingVoucherResend(null);
    setCustomerMessages(null);
    setCustomerMessagesError('');
    setMessageHistorySearch('');
    setMessageHistoryStatusFilter('ALL');
    setMessageHistoryFromDate('');
    setMessageHistoryToDate('');
    setSimSwapPromptOpen(false);
    setSimSwapPromptMsisdn('');
    setSimSwapPromptReason('');
    setSimSwapPromptResult(null);
    setSimSwapNewIccid('');
    setSimSwapReason('LOST_SIM_CARD');
    setSimSwapExecutionResult(null);
    setSubmittingSimSwap(false);
    setIdNumberAccountCount(0);
    mtnLookupVersion.current += 1;
    setMtnStatus(null);
    setPortStatus(null);
    setSparMobileCardDetails(null);
    setSparMobileAccountCreated(null);
    setAiSummary(null);
    setAiSummaryError(null);
    setDmaDiagnostics(null);
    setAccountError('');
    setMtnError('');
    setPortStatusError('');
    setSparMobileCardError('');
    
    setLoadingAccount(true);
    setLoadingNovaAccountDetails(false);
    setLoadingNovaBalancePlans(false);
    setLoadingIdentityAssurance(false);
    setLoadingSimHistory(false);
    setLoadingVoucherStatus(false);
    setResendingVoucherId(null);
    setLoadingCustomerMessages(false);
    setLoadingMtnStatus(true);
    setLoadingPortStatus(false);
    setLoadingSparMobileCard(true);
    
    setActiveSearchType('account');
    setMtnStatus(null);
    setPortStatus(null);
    setSparMobileCardDetails(null);
    setSparMobileAccountCreated(null);
    setAiSummary(null);
    setAiSummaryError(null);
    setLoadingAiSummary(false);
    setDmaDiagnostics(null);

    const lookupVersion = mtnLookupVersion.current;
    try {
      const res = await novaOpsApi.get(`/ops/customers/search-account?query=${encodeURIComponent(searchQuery)}`);
      if (lookupVersion !== mtnLookupVersion.current) return;
      if (res.data?.simInventory?.hasLinkedCustomer === false) {
        setSimInventoryResult(res.data);
        setLoadingAccount(false);
        setLoadingMtnStatus(false);
        setLoadingSparMobileCard(false);
        return;
      }
      const accountData = res.data?.customer;
      if (!accountData || typeof accountData !== 'object') {
        if (res.data?.inOurDatabase === false) {
          setAccountError(
            `MSISDN ${res.data?.msisdn ?? searchQuery} is not in the Nova database.`,
          );
        } else {
          setAccountError('No account found with the provided information');
        }
        setLoadingAccount(false);
        setLoadingMtnStatus(false);
        setLoadingSparMobileCard(false);
        return;
      }
      if (res.data.sparLinkedAccount) {
        accountData.sparLinkedAccount = res.data.sparLinkedAccount;
        console.log('[SPAR Mobile] Linked account info:', accountData.sparLinkedAccount);
      }
      if (res.data.distributor) {
        accountData.distributor = res.data.distributor;
        console.log('[Distributor] Distributor info:', accountData.distributor);
      }
      if (res.data.suspensionHistory) {
        accountData.suspensionHistory = res.data.suspensionHistory;
      }
      console.log('[SPAR Mobile] Account data:', {
        i_customer: accountData.i_customer,
        msisdn: accountData.msisdn,
        spar_uuid: accountData.spar_uuid,
        spar_linked_i_customer: accountData.spar_linked_i_customer,
        spar_linked_msisdn: accountData.spar_linked_msisdn,
        distributor_i_customer: accountData.distributor_i_customer,
        sparLinkedAccount: accountData.sparLinkedAccount,
        suspended: accountData.suspended,
        suspensionHistory: accountData.suspensionHistory,
      });
      setAccount({
        ...accountData,
        suspended: isAccountSuspended(accountData),
      });
      setLoadedAccountSearchQuery(searchQuery);
      setLoadedAccountSearchType(
        typeof res.data?.searchType === 'string' ? res.data.searchType : null,
      );
      setIdNumberAccountCount(res.data.idNumberAccountCount || 0);
      setLoadingAccount(false);
      void fetchNovaAccountSupportData(
        accountData.i_customer,
        accountData.mvno,
        accountData.app_user_uuid,
        accountData.account_uuid,
      );

      if (accountData.sim_serial_number && searchQuery.replace(/\s+/g, '').length >= 19) {
        setLoadingDmaDiagnostics(true);
        try {
          const dmaRes = await novaOpsApi.get(`/ops/customers/dma-diagnostics?iccid=${encodeURIComponent(accountData.sim_serial_number)}&i_customer=${accountData.i_customer || ''}`);
          setDmaDiagnostics(dmaRes.data);
        } catch (dmaErr: any) {
          console.error('DMA diagnostics failed:', dmaErr);
        } finally {
          setLoadingDmaDiagnostics(false);
        }
      }

      const msisdn = res.data.customer?.msisdn || res.data.customer?.msisdn_dma || res.data.msisdn;
      
      if (lookupVersion !== mtnLookupVersion.current) return;
      if (msisdn) {
        const novaLocalMtnStatus = buildNovaLocalMtnStatus(accountData);
        setMtnStatus(novaLocalMtnStatus);
        try {
          // Strata retrieves live MTN subscriber state through Nexus.
          const mtnRes = await api.get(`/customers/mtn-status?msisdn=${encodeURIComponent(msisdn)}`);
          if (lookupVersion !== mtnLookupVersion.current) return;
          const liveStatus = mtnRes.data?.mtnStatus;
          setMtnStatus(resolveMtnStatus(liveStatus, novaLocalMtnStatus));
          setMtnError(liveStatus?.statusCode === '0' ? '' : 'Live MTN status could not be verified. Showing Nova local account/SIM status only.');
        } catch (mtnErr: any) {
          if (lookupVersion !== mtnLookupVersion.current) return;
          console.error('MTN status check failed:', mtnErr);
          setMtnStatus(novaLocalMtnStatus);
          setMtnError(getApiErrorMessage(mtnErr, 'Showing Nova local account/SIM status. Live MTN status could not be loaded.'));
        } finally {
          if (lookupVersion === mtnLookupVersion.current) setLoadingMtnStatus(false);
        }

        setLoadingPortStatus(true);
        try {
          const portStatusRes = await novaOpsApi.get(`/ops/customers/port-status?msisdn=${encodeURIComponent(msisdn)}${tenantQuery(accountData.mvno)}`);
          setPortStatus(portStatusRes.data.portStatus || null);
        } catch (portErr: any) {
          console.error('Port status check failed:', portErr);
          setPortStatusError(getApiErrorMessage(portErr, 'Failed to load port status'));
        } finally {
          setLoadingPortStatus(false);
        }

        if (accountData.distributor_i_customer === 26215) {
          if (accountData.spar_uuid) {
            try {
              const sparCardRes = await novaOpsApi.get(`/ops/customers/spar-mobile/card-details?msisdn=${encodeURIComponent(msisdn)}`);
              setSparMobileCardDetails(sparCardRes.data);
            } catch (sparCardErr: any) {
              console.error('SPAR Mobile card details failed:', sparCardErr);
              console.error('SPAR Mobile error response:', sparCardErr.response?.data);
              setSparMobileCardError(getApiErrorMessage(sparCardErr, 'Failed to load SPAR Mobile card details'));
            } finally {
              setLoadingSparMobileCard(false);
            }
          } else {
            setLoadingSparMobileCard(false);
          }
        } else {
          setLoadingSparMobileCard(false);
        }
      } else {
        setLoadingMtnStatus(false);
        setLoadingSparMobileCard(false);
      }
    } catch (err: any) {
      setLoadingAccount(false);
      setLoadingMtnStatus(false);
      setLoadingSparMobileCard(false);
      if (err.response?.status === 404) {
        setAccountError('No account found with the provided information');
      } else {
        setAccountError(getApiErrorMessage(err, 'Search failed'));
      }
    }
  };

  const openCustomerAccountByMsisdn = (msisdn?: string | null) => {
    const cleanMsisdn = String(msisdn || '').trim();
    if (!cleanMsisdn) return;

    const url = new URL(window.location.href);
    url.searchParams.set('search', 'msisdn');
    url.searchParams.set('value', cleanMsisdn);
    window.history.pushState({}, '', url);
    void handleAccountSearch(cleanMsisdn);
  };

  const openCustomerAccountByCustomerId = (customerId?: string | number | null) => {
    const cleanCustomerId = String(customerId || '').trim();
    if (!cleanCustomerId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('search', 'customer');
    url.searchParams.set('value', cleanCustomerId);
    window.history.pushState({}, '', url);
    void handleAccountSearch(cleanCustomerId);
  };

  const renderCustomerAccountLink = (
    msisdn?: string | null,
    className = 'text-xs',
    customerId?: string | number | null,
  ) => {
    const cleanMsisdn = String(msisdn || '').trim();
    const cleanCustomerId = String(customerId || '').trim();
    if (!cleanMsisdn && !cleanCustomerId) {
      return <span className="text-muted-foreground">-</span>;
    }

    return (
      <Button
        type="button"
        variant="link"
        className={cn('h-auto min-w-0 p-0 text-primary hover:underline', className)}
        onClick={() => {
          if (cleanMsisdn) {
            openCustomerAccountByMsisdn(cleanMsisdn);
          } else {
            openCustomerAccountByCustomerId(cleanCustomerId);
          }
        }}
      >
        <span className="truncate">
          {cleanMsisdn || `Customer ${cleanCustomerId}`}
        </span>
      </Button>
    );
  };

  const handleOpenCreateSparMobileModal = () => {
    const msisdn = account?.msisdn || account?.msisdn_dma || accountQuery;
    if (!msisdn) {
      alert('MSISDN is required');
      return;
    }
    setSparMobileCreateModalOpen(true);
    setSparMobileCreateResult(null);
  };

  const handleCreateSparMobileAccount = async () => {
    const msisdn = account?.msisdn || account?.msisdn_dma || accountQuery;
    if (!msisdn) {
      setSparMobileCreateResult({ success: false, message: 'MSISDN is required' });
      return;
    }

    setCreatingSparMobileAccount(true);
    setSparMobileCreateResult(null);
    
    try {
      const response = await api.post('/customers/spar-mobile/create-account', { msisdn });
      
      if (response.data.error) {
        setSparMobileCreateResult({ success: false, message: response.data.error });
      } else {
        setSparMobileAccountCreated(response.data);
        setSparMobileCreateResult({ 
          success: true, 
          message: `SPAR Mobile account created successfully!\n\nUUID: ${response.data.uuid}\nStatus: ${response.data.sparRewardsStatus}` 
        });
        
        const res = await api.get(`/customers/search-account?query=${encodeURIComponent(accountQuery)}`);
        setAccount(res.data.customer || res.data);
        
        if (response.data.uuid) {
          setLoadingSparMobileCard(true);
          try {
            const sparCardRes = await api.get(`/customers/spar-mobile/card-details?msisdn=${encodeURIComponent(msisdn)}`);
            setSparMobileCardDetails(sparCardRes.data);
          } catch (sparCardErr: any) {
            console.error('SPAR Mobile card details failed:', sparCardErr);
            setSparMobileCardError(getApiErrorMessage(sparCardErr, 'Failed to load SPAR Mobile card details'));
          } finally {
            setLoadingSparMobileCard(false);
          }
        }
      }
    } catch (error: any) {
      console.error('Create SPAR Mobile account failed:', error);
      console.error('Error response:', error.response?.data);
      setSparMobileCreateResult({
        success: false,
        message: getApiErrorMessage(error, 'Failed to create SPAR Mobile account'),
      });
    } finally {
      setCreatingSparMobileAccount(false);
    }
  };

  const handleCloseSparMobileCreateModal = () => {
    if (!creatingSparMobileAccount) {
      setSparMobileCreateModalOpen(false);
      setSparMobileCreateResult(null);
    }
  };

  const handleChangePrimaryPhone = async () => {
    const msisdn = account?.msisdn || account?.msisdn_dma || accountQuery;
    if (!msisdn) {
      alert('MSISDN is required');
      return;
    }

    const confirmed = window.confirm(
      `This will change the primary phone number for this SPAR Mobile account to: ${msisdn}\n\nContinue?`
    );

    if (!confirmed) return;

    setChangingPrimaryPhone(true);
    try {
      const response = await api.put('/customers/spar-mobile/change-primary-phone', { msisdn });
      
      if (response.data.error) {
        alert(`❌ ${response.data.error}`);
      } else {
        alert('✅ Primary phone number changed successfully!');
      }
    } catch (error: any) {
      console.error('Change primary phone failed:', error);
      alert(`❌ ${getApiErrorMessage(error, 'Failed to change primary phone number')}`);
    } finally {
      setChangingPrimaryPhone(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatMessageHistoryDate = (dateString?: string) => {
    if (!dateString) return { date: '-', time: '' };
    const value = new Date(dateString);
    if (Number.isNaN(value.getTime())) return { date: '-', time: '' };
    return {
      date: value.toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      }),
      time: value.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    };
  };

  const messageHistoryStatusOptions = useMemo(() => {
    const statuses = new Set(
      (customerMessages?.messages ?? [])
        .map((message) => message.status)
        .filter((status): status is string => Boolean(status)),
    );
    return Array.from(statuses).sort();
  }, [customerMessages?.messages]);

  const filteredCustomerMessages = useMemo(() => {
    const messages = customerMessages?.messages ?? [];
    const query = messageHistorySearch.trim().toLowerCase();
    const recipientFallback = account?.msisdn || account?.msisdn_dma || null;
    const hasInvalidDateRange = Boolean(
      messageHistoryFromDate &&
        messageHistoryToDate &&
        messageHistoryFromDate > messageHistoryToDate,
    );

    if (hasInvalidDateRange) {
      return [];
    }

    return messages.filter((message) => {
      if (messageHistoryStatusFilter !== 'ALL' && message.status !== messageHistoryStatusFilter) {
        return false;
      }

      const messageTimestamp = getMessageHistoryTimestamp(message);
      if (messageHistoryFromDate || messageHistoryToDate) {
        if (!messageTimestamp) return false;
        if (
          messageHistoryFromDate &&
          messageTimestamp < parseDateInputStart(messageHistoryFromDate)
        ) {
          return false;
        }
        if (
          messageHistoryToDate &&
          messageTimestamp > parseDateInputEnd(messageHistoryToDate)
        ) {
          return false;
        }
      }

      if (!query) return true;

      const recipient =
        message.recipient_address ||
        recipientFallback ||
        message.recipient_address_masked ||
        '';
      const haystack = [
        formatDate(message.created_at || message.submitted_at || undefined),
        message.channel,
        message.provider,
        message.status,
        recipient,
        message.purpose,
        formatMessagePurposeLabel(message.purpose),
        maskVoucherCodeText(message.content, message.purpose),
        message.provider_ref,
        message.provider_message_id,
        message.failure_reason,
      ]
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [
    customerMessages?.messages,
    messageHistorySearch,
    messageHistoryStatusFilter,
    messageHistoryFromDate,
    messageHistoryToDate,
    account?.msisdn,
    account?.msisdn_dma,
  ]);

  const hasMessageHistoryFilters = Boolean(
    messageHistorySearch.trim() ||
      messageHistoryStatusFilter !== 'ALL' ||
      messageHistoryFromDate ||
      messageHistoryToDate,
  );

  const hasInvalidMessageHistoryDateRange = Boolean(
    messageHistoryFromDate &&
      messageHistoryToDate &&
      messageHistoryFromDate > messageHistoryToDate,
  );

  const clearMessageHistoryFilters = () => {
    setMessageHistorySearch('');
    setMessageHistoryStatusFilter('ALL');
    setMessageHistoryFromDate('');
    setMessageHistoryToDate('');
  };

  const formatTimeAgo = (dateString?: string | null): string => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffMs < 0) return 'in the future';
      if (diffSeconds < 60) return 'just now';
      if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 14) return '1 week ago';
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 60) return '1 month ago';
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) !== 1 ? 's' : ''} ago`;
    } catch {
      return '-';
    }
  };

  const parseRewardAmount = (value: unknown): number => {
    if (value === null || value === undefined || value === '') return 0;

    const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getRewardType = (reward: CustomerReward): string =>
    reward.type || reward.reward_type || 'unknown';

  const getRewardCredit = (reward: CustomerReward): number => {
    return parseRewardAmount(reward.credit);
  };

  const getRewardDebit = (reward: CustomerReward): number =>
    parseRewardAmount(reward.debit);

  const getRewardAllocatedAt = (reward: CustomerReward): string | null =>
    reward.allocated_at || null;

  const getRewardBillingTransactionId = (reward: CustomerReward): string | null =>
    reward.billing_transaction_id || null;

  const formatHistoryValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const fetchCustomerRewards = async (iCustomer: number, mvno?: string | null, accountUuid?: string | null) => {
    setLoadingRewards(true);
    setRewardsError('');
    try {
      const res = await novaOpsApi.get<CustomerRewardsResponse>(`/ops/customers/rewards?${canonicalAccountQuery(iCustomer, mvno, accountUuid)}`);
      setCustomerRewards(res.data.rewards || []);
      setRewardUsage(res.data.rewardUsage || null);
    } catch (err: any) {
      console.error('Failed to fetch customer rewards:', err);
      setCustomerRewards([]);
      setRewardUsage(null);
      setRewardsError(getApiErrorMessage(err, 'Failed to load customer rewards'));
    } finally {
      setLoadingRewards(false);
    }
  };

  const processReward = async (i_customer: number) => {
    setProcessingReward(i_customer);
    setRewardsError('');
    setRewardsSuccess('');

    try {
      const API_URL = API_BASE_URL;
      const response = await fetch(`${API_URL}/spar-mobile/process-reward`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ i_customer }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setRewardsSuccess(
        `Published to Pub/Sub - Topic: ${data.pubsub?.topic || 'reward-event-topic'}, Message ID: ${data.messageId}`
      );
      setTimeout(() => setRewardsSuccess(''), 8000);
    } catch (err: any) {
      setRewardsError(err.message || 'Failed to process reward');
      setTimeout(() => setRewardsError(''), 8000);
    } finally {
      setProcessingReward(null);
    }
  };

  const updateRewardUsageBlock = async (blocked: boolean) => {
    if (!account?.i_customer || !rewardBlockReason.trim()) return;
    setUpdatingRewardBlock(true);
    setRewardsError('');
    setRewardsSuccess('');
    try {
      const endpoint = blocked ? '/ops/customers/rewards/block' : '/ops/customers/rewards/unblock';
      const res = await novaOpsApi.post(endpoint, {
        i_customer: account.i_customer,
        account_uuid: account.account_uuid,
        mvno: account.mvno,
        reason: rewardBlockReason.trim(),
      });
      setRewardsSuccess(res.data?.message || (blocked ? 'Reward usage blocked.' : 'Reward usage unblocked.'));
      setRewardBlockDialogOpen(false);
      setRewardUnblockDialogOpen(false);
      setRewardBlockReason('');
      await fetchCustomerRewards(account.i_customer, account.mvno, account.account_uuid);
      setTimeout(() => setRewardsSuccess(''), 8000);
    } catch (err: any) {
      setRewardsError(getApiErrorMessage(err, blocked ? 'Failed to block reward usage' : 'Failed to unblock reward usage'));
      setTimeout(() => setRewardsError(''), 8000);
    } finally {
      setUpdatingRewardBlock(false);
    }
  };

  const handleIdNumberClick = (idNumber: string) => {
    const idNumberStr = String(idNumber || '');
    setIdNumberQuery(idNumberStr);
    setTimeout(() => handleCustomerSearch(idNumberStr), 100);
  };

  const handleSuspendCustomer = async () => {
    if (!account?.i_customer || !suspendReason.trim()) {
      return;
    }

    setSuspending(true);
    try {
      await novaOpsApi.post('/ops/customers/suspend', {
        i_customer: account.i_customer,
        account_uuid: account.account_uuid,
        mvno: account.mvno,
        reason: suspendReason.trim(),
      });
      
      if (account.msisdn || account.msisdn_dma || account.sim_serial_number) {
        const query = account.msisdn || account.msisdn_dma || account.sim_serial_number || account.i_customer;
        await handleAccountSearch(String(query));
      }
      
      setSuspendDialogOpen(false);
      setSuspendReason('');
    } catch (error: any) {
      console.error('Failed to suspend customer:', error);
      alert(getApiErrorMessage(error, 'Failed to suspend customer'));
    } finally {
      setSuspending(false);
    }
  };

  const handleUnsuspendCustomer = async () => {
    if (!account?.i_customer || !unsuspendReason.trim()) {
      return;
    }

    setSuspending(true);
    try {
      await novaOpsApi.post('/ops/customers/unsuspend', {
        i_customer: account.i_customer,
        account_uuid: account.account_uuid,
        mvno: account.mvno,
        reason: unsuspendReason.trim(),
      });
      
      if (account.msisdn || account.msisdn_dma || account.sim_serial_number) {
        const query = account.msisdn || account.msisdn_dma || account.sim_serial_number || account.i_customer;
        await handleAccountSearch(String(query));
      }
      
      setUnsuspendDialogOpen(false);
      setUnsuspendReason('');
    } catch (error: any) {
      console.error('Failed to unsuspend customer:', error);
      alert(getApiErrorMessage(error, 'Failed to unsuspend customer'));
    } finally {
      setSuspending(false);
    }
  };

  const handleFixChurn = async () => {
    if (!dmaDiagnostics?.orphanedAccount?.isOrphaned) {
      return;
    }

    setFixingChurn(true);
    try {
      alert('Churn fix process will be implemented here. Please provide the detailed steps for fixing churned MSISDNs.');
    } catch (error: any) {
      console.error('Churn fix failed:', error);
      alert(`❌ ${error.response?.data?.message || 'Failed to fix churned MSISDN'}`);
    } finally {
      setFixingChurn(false);
    }
  };

  const handleResetDmaFailedCountClick = () => {
    if (account?.i_customer) setResetDmaConfirmOpen(true);
  };

  const handleResetDmaFailedCountConfirm = async () => {
    if (!account?.i_customer) return;
    setResetDmaConfirmOpen(false);
    setResettingDma(true);
    try {
      const res = await novaOpsApi.post('/ops/customers/reset-dma-failed-count', {
        i_customer: account.i_customer,
        mvno: account.mvno,
      });
      if (account.msisdn || account.msisdn_dma || account.sim_serial_number) {
        const query = account.msisdn || account.msisdn_dma || account.sim_serial_number || account.i_customer;
        const accRes = await novaOpsApi.get(`/ops/customers/search-account?query=${encodeURIComponent(String(query))}${tenantQuery(account.mvno)}`);
        setAccount(accRes.data.customer || accRes.data);
      }
      if (account.sim_serial_number) {
        const dmaRes = await novaOpsApi.get(`/ops/customers/dma-diagnostics?iccid=${encodeURIComponent(account.sim_serial_number)}&i_customer=${account.i_customer}`);
        setDmaDiagnostics(dmaRes.data);
      }
      toast.success(res.data.message);
    } catch (err: unknown) {
      console.error('Reset DMA failed:', err);
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
        ? (err as { response: { data: { message: string } } }).response.data.message
        : 'Failed to reset dma_failed_count';
      toast.error(msg);
    } finally {
      setResettingDma(false);
    }
  };

  const fetchTransactions = async (iCustomer: number, days: number = 30, mvno?: string | null, accountUuid?: string | null) => {
    if (!iCustomer) return;

    setLoadingTransactions(true);
    setTransactionsError('');
    setSelectedTransaction(null);
    try {
      const res = await novaOpsApi.get(`/ops/customers/transactions?${canonicalAccountQuery(iCustomer, mvno, accountUuid)}&days=${days}`);
      setTransactions(res.data.transactions || []);
      setTransactionsSummary(res.data.summary || null);
    } catch (err: any) {
      console.error('Failed to fetch transactions:', err);
      setTransactionsError(getApiErrorMessage(err, 'Failed to load transactions'));
      setTransactions([]);
      setTransactionsSummary(null);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchTransfers = async (iCustomer: number, days?: number | null, mvno?: string | null, accountUuid?: string | null) => {
    if (!iCustomer) return;

    setLoadingTransfers(true);
    setTransfersError('');
    try {
      const selector = canonicalAccountQuery(iCustomer, mvno, accountUuid);
      const url = days 
        ? `/ops/customers/transfers?${selector}&days=${days}`
        : `/ops/customers/transfers?${selector}`;
      const res = await novaOpsApi.get(url);
      setTransfers(res.data.transfers || []);
      
      const sortedSummary = (res.data.summaryByUser || []).sort((a: any, b: any) => {
        const aTotalMoney = (a.total_received || 0) + (a.total_sent || 0);
        const bTotalMoney = (b.total_received || 0) + (b.total_sent || 0);
        
        if (bTotalMoney !== aTotalMoney) {
          return bTotalMoney - aTotalMoney;
        }
        
        const aTotalData = (a.data_received_mb || 0) + (a.data_sent_mb || 0);
        const bTotalData = (b.data_received_mb || 0) + (b.data_sent_mb || 0);
        
        return bTotalData - aTotalData;
      });
      
      setTransfersSummaryByUser(sortedSummary);
      setTransfersOverallSummary(res.data.overallSummary || null);
    } catch (err: any) {
      console.error('Failed to fetch transfers:', err);
      setTransfersError(getApiErrorMessage(err, 'Failed to load transfers'));
      setTransfers([]);
      setTransfersSummaryByUser([]);
      setTransfersOverallSummary(null);
    } finally {
      setLoadingTransfers(false);
    }
  };

  useEffect(() => {
    if (!account) return;
    
    if (loadingMtnStatus) return;
    
    if (loadingPortStatus) return;

    if (loadingNovaBalancePlans) return;

    const generateAiSummary = async () => {
      console.log('[AI Summary] Starting generation...');
      setLoadingAiSummary(true);
      setAiSummaryError(null);

      try {
        const response = await api.post('/gemini/summarize-customer', {
          account: account,
          mtnStatus: mtnStatus,
          portStatus: portStatus,
          balanceSummary: novaBalancePlans?.aggregated
            ? {
                airtime: novaBalancePlans.aggregated.airtime.formatted,
                data: novaBalancePlans.aggregated.data.formatted,
                voice: novaBalancePlans.aggregated.voice.formatted,
                sms: novaBalancePlans.aggregated.sms.formatted,
              }
            : undefined,
        });

        console.log('[AI Summary] Generated successfully');
        setAiSummary(response.data.summary);
      } catch (error: any) {
        console.error('[AI Summary] Generation failed:', error);
        setAiSummaryError(getApiErrorMessage(error, 'Failed to generate AI summary. Please ensure GEMINI_API_KEY is configured.'));
      } finally {
        setLoadingAiSummary(false);
      }
    };

    generateAiSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    account,
    loadingMtnStatus,
    loadingPortStatus,
    loadingNovaBalancePlans,
    novaBalancePlans,
  ]);

  useEffect(() => {
    if (account?.i_customer) {
      setTransactions([]);
      setTransactionsSummary(null);
      setTransactionsError('');
      setTransfers([]);
      setTransfersSummaryByUser([]);
      setTransfersOverallSummary(null);
      setTransfersError('');
    }
  }, [account?.i_customer]);

  useEffect(() => {
    if (account?.i_customer && activeTab === 1 && !loadingTransactions) {
      fetchTransactions(account.i_customer, transactionsDays, account.mvno, account.account_uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadingTransactions is a guard to prevent re-fetch, not a trigger
  }, [account?.i_customer, account?.account_uuid, activeTab, transactionsDays]);

  useEffect(() => {
    if (account?.i_customer && activeTab === 2 && !loadingTransfers) {
      fetchTransfers(account.i_customer, transfersDays, account.mvno, account.account_uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadingTransfers is a guard to prevent re-fetch, not a trigger
  }, [account?.i_customer, account?.account_uuid, activeTab, transfersDays]);

  useEffect(() => {
    if (account?.i_customer && activeTab === 6 && !loadingHistory) {
      const fetchHistory = async () => {
        setLoadingHistory(true);
        setHistoryError('');
        try {
          const res = await novaOpsApi.get(`/ops/customers/history?${canonicalAccountQuery(account.i_customer, account.mvno, account.account_uuid)}`);
          console.log('History API response:', res.data);
          setCustomerHistory(res.data.history || []);
        } catch (err: any) {
          console.error('Failed to fetch customer history:', err);
          setHistoryError(getApiErrorMessage(err, 'Failed to load customer history'));
        } finally {
          setLoadingHistory(false);
        }
      };
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadingHistory is a guard to prevent re-fetch, not a trigger
  }, [account?.i_customer, account?.account_uuid, activeTab]);

  useEffect(() => {
    if (account?.i_customer && activeTab === 5 && !loadingRewards) {
      void fetchCustomerRewards(account.i_customer, account.mvno, account.account_uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadingRewards is a guard to prevent re-fetch, not a trigger
  }, [account?.i_customer, account?.account_uuid, activeTab]);

  useEffect(() => {
    if (account?.i_customer && activeTab === 7 && !loadingCustomerMessages) {
      void fetchCustomerMessages(account.i_customer, account.mvno, account.account_uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadingCustomerMessages is a guard to prevent re-fetch, not a trigger
  }, [account?.i_customer, account?.account_uuid, activeTab]);

  useEffect(() => {
    if (activeSearchType === 'account') {
      setCustomers([]);
      setTotalAccounts(0);
      setIdNumberQuery('');
    }
  }, [activeSearchType]);

  useEffect(() => {
    if (idNumberQuery !== null && idNumberQuery !== undefined && typeof idNumberQuery === 'object' && !Array.isArray(idNumberQuery)) {
      console.warn('idNumberQuery was set to an object, clearing it:', idNumberQuery);
      setIdNumberQuery('');
    } else if (typeof idNumberQuery === 'string' && idNumberQuery === '[object Object]') {
      console.warn('idNumberQuery contains [object Object] string, clearing it');
      setIdNumberQuery('');
    }
  }, [idNumberQuery]);

  useEffect(() => {
    if (activeSearchType === 'idNumber') {
      setAccount(null);
      setSimInventoryResult(null);
      setLoadedAccountSearchQuery(null);
      setLoadedAccountSearchType(null);
      setIdNumberAccountCount(0);
      setAccountQuery('');
    }
  }, [activeSearchType]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchType = urlParams.get('search');
    const searchValue = urlParams.get('value');

    if (searchType && searchValue) {
      const valueStr = String(searchValue);
      if (searchType === 'idNumber') {
        handleCustomerSearch(valueStr);
      } else if (searchType === 'msisdn') {
        handleAccountSearch(valueStr);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TooltipProvider>
    <div className="w-full min-w-0 max-w-full mx-auto overflow-x-hidden p-4 lg:p-6">
      <h2 className="text-2xl font-bold mb-2">
        <IconUser className="inline-block align-middle mr-2 size-10" />
        Customer & Account Search
      </h2>
      <p className="text-base text-muted-foreground mb-8">
        Search by ID Number, by mobile account (MSISDN, ICCID, username, UUID, or I_CUSTOMER), or by wallet account number
      </p>

      {/* Unified Search Section */}
      <div className="rounded-lg border border-border bg-card shadow-sm p-4 mb-6 lg:p-8">
        <h3 className="text-lg font-semibold mb-6">
          <IconSearch className="inline-block align-middle mr-2" />
          Search
        </h3>
        
        <div className="grid w-full min-w-0 grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Customer Search (ID Number) */}
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground mb-4">
              Customer Search (ID Number)
            </p>
            <div className="flex flex-col gap-4">
              <div>
                <Label htmlFor="idNumber" className="mb-1.5">ID Number</Label>
                <div className="relative">
                  <IconId className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    id="idNumber"
                    placeholder="Enter 13-digit SA ID Number"
                    className="pl-9"
                    value={(() => {
                      if (!idNumberQuery) return '';
                      if (typeof idNumberQuery === 'string') {
                        if (idNumberQuery === '[object Object]' || idNumberQuery.startsWith('[object ')) {
                          return '';
                        }
                        return idNumberQuery;
                      }
                      return '';
                    })()}
                    onChange={(e) => setIdNumberQuery(String(e.target.value || ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleCustomerSearch()}
                    disabled={loadingCustomer}
                  />
                </div>
              </div>
              <Button
                className="w-full"
                onClick={() => handleCustomerSearch()}
                disabled={loadingCustomer || !String(idNumberQuery || '').trim()}
              >
                {loadingCustomer ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : <IconSearch className="mr-2 size-4" />}
                {loadingCustomer ? 'Searching...' : 'Find Customer'}
              </Button>
              {customerError && (
                <Alert variant="destructive">
                  <AlertDescription>{customerError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Account Search (MSISDN / ICCID / username / UUID / I_CUSTOMER) */}
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground mb-4">
              Account Search (MSISDN / ICCID / username / UUID / I_CUSTOMER)
            </p>
            <div className="flex flex-col gap-4">
              <div>
                <Label htmlFor="accountQuery" className="mb-1.5">MSISDN, ICCID, username, UUID, or Customer ID</Label>
                <div className="relative">
                  <IconPhone className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    id="accountQuery"
                    placeholder="Enter MSISDN, SIM serial / ICCID, username, UUID, or I_CUSTOMER"
                    className="pl-9"
                    value={String(accountQuery || '')}
                    onChange={(e) => setAccountQuery(String(e.target.value || ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleAccountSearch()}
                    disabled={loadingAccount}
                  />
                </div>
              </div>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => handleAccountSearch()}
                disabled={loadingAccount || !String(accountQuery || '').trim()}
              >
                {loadingAccount ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : <IconSearch className="mr-2 size-4" />}
                {loadingAccount ? 'Searching...' : 'Find Account'}
              </Button>
              {accountError && (
                <Alert variant="destructive">
                  <AlertDescription>{accountError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Wallet Search (wallet account number) */}
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground mb-4">
              Wallet Search (wallet account number)
            </p>
            <div className="flex flex-col gap-4">
              <div>
                <Label htmlFor="walletQuery" className="mb-1.5">Wallet account number</Label>
                <div className="relative">
                  <IconWallet className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    id="walletQuery"
                    placeholder="Enter 10-digit wallet account number"
                    className="pl-9"
                    inputMode="numeric"
                    value={walletQuery}
                    onChange={(e) => setWalletQuery(String(e.target.value || ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleWalletSearch()}
                    disabled={loadingWallet}
                  />
                </div>
              </div>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => handleWalletSearch()}
                disabled={loadingWallet || !walletQuery.trim()}
              >
                {loadingWallet ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : <IconSearch className="mr-2 size-4" />}
                {loadingWallet ? 'Searching...' : 'Find Wallet'}
              </Button>
              {walletError && (
                <Alert variant="destructive">
                  <AlertDescription>{walletError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>

      {activeSearchType === 'wallet' && walletResult && !loadingWallet && (
        <WalletSearchResult
          graph={walletResult.graph}
          walletAccountNumber={walletResult.walletAccountNumber}
          canReleaseHold={hasWriteAccess('customers')}
          onOpenAccount={(msisdn) => void handleAccountSearch(msisdn)}
        />
      )}

      {activeSearchType === 'account' && simInventoryResult && !loadingAccount && (
        <SimInventoryResult result={simInventoryResult} />
      )}

      {/* Multiple Customers Results (ID Number search) */}
      {activeSearchType === 'idNumber' && customers.length > 0 && !loadingCustomer && (() => {
        const customersWithMsisdn = customers.filter(cust => cust.msisdn || cust.msisdn_dma);
        const customersWithoutMsisdn = customers.filter(cust => !cust.msisdn && !cust.msisdn_dma);
        
        return (
          <div className="rounded-lg border border-border bg-card shadow-sm">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Customer Accounts</h3>
                <Badge variant="info">
                  {totalAccounts} account{totalAccounts !== 1 ? 's' : ''}
                </Badge>
              </div>

              <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200 mb-6">
                <AlertDescription>
                  Found {totalAccounts} account{totalAccounts !== 1 ? 's' : ''} for ID Number: <strong>{customers[0]?.id_number}</strong>
                </AlertDescription>
              </Alert>

              {customersWithMsisdn.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">
                    Accounts with MSISDN ({customersWithMsisdn.length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-bold">MVNO</TableHead>
                        <TableHead className="font-bold">Customer ID</TableHead>
                        <TableHead className="font-bold">Name</TableHead>
                        <TableHead className="font-bold">MSISDN</TableHead>
                        <TableHead className="font-bold">Email</TableHead>
                        <TableHead className="font-bold">ICCID</TableHead>
                        <TableHead className="font-bold">Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customersWithMsisdn.map((cust, index) => {
                        const msisdn = cust.msisdn || cust.msisdn_dma;
                        return (
                          <TableRow key={index}>
                            <TableCell>{cust.tenant?.displayName || cust.mvno || '-'}</TableCell>
                          <TableCell>{cust.i_customer}</TableCell>
                            <TableCell>{`${cust.first_name || ''} ${cust.last_name || ''}`.trim() || '-'}</TableCell>
                            <TableCell>
                              {msisdn ? (
                                <div className="flex items-center gap-1">
                                  <a
                                    href={`/customers?search=msisdn&value=${encodeURIComponent(msisdn)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline text-sm"
                                  >
                                    {msisdn}
                                  </a>
                                  {(cust.suspended === 1 || cust.suspended === true) && (
                                    <Badge variant="destructive" className="text-[0.65rem] h-[18px] leading-none px-1">
                                      Suspended
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>{cust.email || '-'}</TableCell>
                            <TableCell>{cust.sim_serial_number || '-'}</TableCell>
                            <TableCell>{formatDate(cust.created_at)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {customersWithoutMsisdn.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Accounts without MSISDN ({customersWithoutMsisdn.length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-bold">MVNO</TableHead>
                        <TableHead className="font-bold">Customer ID</TableHead>
                        <TableHead className="font-bold">Name</TableHead>
                        <TableHead className="font-bold">Email</TableHead>
                        <TableHead className="font-bold">ICCID</TableHead>
                        <TableHead className="font-bold">Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customersWithoutMsisdn.map((cust, index) => (
                        <TableRow key={index}>
                          <TableCell>{cust.tenant?.displayName || cust.mvno || '-'}</TableCell>
                          <TableCell>{cust.i_customer}</TableCell>
                          <TableCell>{`${cust.first_name || ''} ${cust.last_name || ''}`.trim() || '-'}</TableCell>
                          <TableCell>{cust.email || '-'}</TableCell>
                          <TableCell>{cust.sim_serial_number || '-'}</TableCell>
                          <TableCell>{formatDate(cust.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Single Account Result (MSISDN or ICCID search) */}
      {activeSearchType === 'account' && account && (
        <div className="mt-6 min-w-0 max-w-full">
          {/* Churn Banner */}
          {account.msisdn_churned && (
            <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200 mb-4">
              <IconAlertCircle className="size-4" />
              <AlertTitle><strong>Customer Has Churned</strong></AlertTitle>
              <AlertDescription>
                <div className="mt-2">
                  <p className="text-sm">
                    <strong>Churned MSISDN:</strong> {account.msisdn_churned}
                  </p>
                  <p className="text-sm mt-1">
                    This customer's number has been churned. The MSISDN is no longer active on this account.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* AI Summary Section */}
          <div className="mb-4 min-w-0">
            <AiSummaryCard
              suspension={suspension}
              summary={aiSummary}
              isLoading={loadingAiSummary}
              error={aiSummaryError}
            />
          </div>

          {/* Tabs Section */}
          <div className="min-w-0 rounded-lg border border-border bg-card shadow-sm mb-4">
            <Tabs value={String(activeTab)} onValueChange={(val) => setActiveTab(Number(val))}>
              <TabsList variant="line" className="flex !h-14 w-full min-w-0 flex-nowrap gap-1 overflow-x-auto overflow-y-hidden border-b border-border px-2 pt-1 pb-2">
                <TabsTrigger value="0" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconDashboard className="size-4 shrink-0" /><span className="whitespace-nowrap">Account Overview</span></TabsTrigger>
                <TabsTrigger value="1" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconReceipt className="size-4 shrink-0" /><span className="whitespace-nowrap">Transactions</span></TabsTrigger>
                <TabsTrigger value="2" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconArrowsExchange className="size-4 shrink-0" /><span className="whitespace-nowrap">Transfers</span></TabsTrigger>
                <TabsTrigger value="3" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconWallet className="size-4 shrink-0" /><span className="whitespace-nowrap">Wallet</span></TabsTrigger>
                <TabsTrigger value="4" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconPhone className="size-4 shrink-0" /><span className="whitespace-nowrap">SIM & Network</span></TabsTrigger>
                <TabsTrigger value="8" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconChartBar className="size-4 shrink-0" /><span className="whitespace-nowrap">Usage & XDR</span></TabsTrigger>
                <TabsTrigger value="7" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconMessage className="size-4 shrink-0" /><span className="whitespace-nowrap">Messages</span></TabsTrigger>
                <TabsTrigger value="5" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconGift className="size-4 shrink-0" /><span className="whitespace-nowrap">Rewards</span></TabsTrigger>
                <TabsTrigger value="6" className="!h-11 !flex-none gap-1 px-2 text-[11px] leading-none xl:text-xs 2xl:text-sm after:bottom-0"><IconInfoCircle className="size-4 shrink-0" /><span className="whitespace-nowrap">History</span></TabsTrigger>
              </TabsList>

              {/* Account Overview Tab */}
              <TabsContent value="0">
              <div className="p-6">
                {/* Status Badges */}
                <div className="mb-6">
                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <h3 className="text-lg font-semibold">Account Overview</h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      {account.inOurDatabase !== false && (
                        <Badge variant="success">
                          <IconCircleCheck className="size-3 mr-1" />
                          In Our Database
                        </Badge>
                      )}
                      {loadingMtnStatus && (
                        <Badge variant="outline">
                          <IconLoader2 className="size-3 animate-spin mr-1" />
                          Checking MTN...
                        </Badge>
                      )}
                      {!loadingMtnStatus && mtnStatus?.statusCode === '0' && mtnStatus.isActive && (
                        <Badge variant="success">
                          <IconCircleCheck className="size-3 mr-1" />
                          MTN Active
                        </Badge>
                      )}
                      {!loadingMtnStatus && mtnStatus?.statusCode === '0' && !mtnStatus.isActive && (
                        <Badge variant="destructive">
                          <IconAlertCircle className="size-3 mr-1" />
                          MTN Inactive
                        </Badge>
                      )}
                      {idNumberAccountCount > 1 && (
                        <Badge
                          variant="info"
                          className="cursor-pointer"
                          onClick={() => account.id_number && handleIdNumberClick(account.id_number)}
                        >
                          {idNumberAccountCount} accounts with this ID
                        </Badge>
                      )}
                      {account.i_customer && canManageCustomerLifecycle && (
                        <>
                          {isAccountSuspended(account) ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="min-w-28"
                              onClick={() => setUnsuspendDialogOpen(true)}
                              disabled={suspending}
                            >
                              <IconLockOpen className="size-4 mr-1" />
                              Unsuspend
                            </Button>
                          ) : (
                            <Button
                              variant="destructive"
                              size="sm"
                              className="min-w-28"
                              onClick={() => setSuspendDialogOpen(true)}
                              disabled={suspending}
                            >
                              <IconBan className="size-4 mr-1" />
                              Suspend
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {account.inOurDatabase === false && (
                    <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200 mt-4">
                      <AlertDescription>
                        MSISDN not found in our database. Showing MTN network status only.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {account.inOurDatabase !== false && (loadingNovaAccountDetails || novaAccountDetailsError) && (
                  <div className="mb-4">
                    {novaAccountDetailsError ? (
                      <Alert variant="destructive">
                        <AlertDescription>{novaAccountDetailsError}</AlertDescription>
                      </Alert>
                    ) : (
                      <Alert>
                        <IconLoader2 className="size-4 animate-spin" />
                        <AlertDescription>Loading Nova account details...</AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                {/* Customer Spend Dashboard */}
                {(account.spend_mtd !== undefined || account.spend_1_day !== undefined || account.spend_30_day !== undefined ||
                  account.spend_60_day !== undefined || account.spend_90_day !== undefined || account.spend_ltv !== undefined) && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-4">
                      {account.spend_mtd !== undefined && account.spend_mtd !== null && (
                        <StatCard className="min-w-[150px] flex-auto" title="Current Month" value={formatRand(account.spend_mtd)} />
                      )}
                      {account.spend_1_day !== undefined && account.spend_1_day !== null && (
                        <StatCard className="min-w-[150px] flex-auto" title="Last 24 Hours" value={formatRand(account.spend_1_day)} />
                      )}
                      {account.spend_30_day !== undefined && account.spend_30_day !== null && (
                        <StatCard className="min-w-[150px] flex-auto" title="Last 30 Days" value={formatRand(account.spend_30_day)} />
                      )}
                      {account.spend_60_day !== undefined && account.spend_60_day !== null && (
                        <StatCard className="min-w-[150px] flex-auto" title="Last 60 Days" value={formatRand(account.spend_60_day)} />
                      )}
                      {account.spend_90_day !== undefined && account.spend_90_day !== null && (
                        <StatCard className="min-w-[150px] flex-auto" title="Last 90 Days" value={formatRand(account.spend_90_day)} />
                      )}
                      {account.spend_ltv !== undefined && account.spend_ltv !== null && (
                        <StatCard className="min-w-[150px] flex-auto" title="Lifetime" value={formatRand(account.spend_ltv)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Information Blocks - 2 Column Masonry */}
                <div className="columns-1 md:columns-2 gap-4 [&>*]:break-inside-avoid [&>*]:mb-4">

            {/* Balance & Plans Block */}
            {account.inOurDatabase !== false && (
              <div>
                <Accordion multiple defaultValue={['open']}>
                  <AccordionItem value="open">
                    <AccordionTrigger>
                      <div className="flex items-center gap-2 w-full">
                        <IconGift className="size-4" />
                        <p className={accordionTitleClass}>Balance & Plans</p>
                        {loadingNovaBalancePlans && (
                          <Badge variant="outline" className="ml-auto">
                            <IconLoader2 className="size-3 animate-spin mr-1" />
                            Loading...
                          </Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="mb-3 flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            (!account.i_customer && !account.account_uuid) ||
                            loadingNovaBalancePlans
                          }
                          onClick={() =>
                            void fetchNovaBalancePlans(
                              account.i_customer,
                              account.mvno,
                              account.account_uuid,
                            )
                          }
                        >
                          {loadingNovaBalancePlans ? (
                            <IconLoader2 className="size-4 animate-spin" />
                          ) : (
                            <IconRefresh className="size-4" />
                          )}
                          Refresh
                        </Button>
                      </div>
                      {novaBalancePlansError && (
                        <Alert variant="destructive" className="mb-4">
                          <AlertDescription>{novaBalancePlansError}</AlertDescription>
                        </Alert>
                      )}
                      {loadingNovaBalancePlans && !novaBalancePlans && (
                        <div className="flex justify-center py-6">
                          <IconLoader2 className="h-8 w-8 animate-spin" />
                        </div>
                      )}
                      {novaBalancePlans?.aggregated && novaBalancePlans?.bundles && (() => {
                        const allBundles = bundleLabels.flatMap(({ key }) => novaBalancePlans.bundles[key] || []);
                        const basePlanGroups = createBasePlanGroups(
                          novaBalancePlans.activePlanVersions || [],
                          isAccountSuspended(account),
                        );
                        const attachedBundlesByBasePlan = new Map<BasePlanName, BundleSummary[]>();
                        for (const bundle of allBundles) {
                          const basePlanName = getBasePlanName(bundle.name);
                          if (!basePlanName) continue;
                          const list = attachedBundlesByBasePlan.get(basePlanName) ?? [];
                          list.push(bundle);
                          attachedBundlesByBasePlan.set(basePlanName, list);
                        }
                        const excludePlanSubscriptionIds = new Set(
                          allBundles
                            .map((bundle) => bundle.planSubscriptionId)
                            .filter((id): id is string => Boolean(id)),
                        );
                        const otherBalances = getOtherApplicableBalances({
                          balances: novaBalancePlans.balances || [],
                          excludePlanSubscriptionIds,
                        });
                        const activePlanCount = bundleLabels.reduce(
                          (total, { key }) =>
                            total + (novaBalancePlans.bundles[key] || []).filter((b) => !isBasePlanName(b.name)).length,
                          0,
                        );
                        const basePlanActiveCount = basePlanGroups.reduce((total, g) => total + g.count, 0);
                        const basePlanHasIssue = basePlanGroups.some((g) => g.isMissing || g.isDuplicate);

                        return (
                        <div className="space-y-5">
                          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                            {balanceLabels.map(({ key, label }) => {
                              const balance = novaBalancePlans.aggregated[key];
                              const segments = getBalanceSegments(balance);
                              return (
                                <div key={key} className="rounded-md border p-3">
                                  <div className="flex items-baseline justify-between gap-3">
                                    <p className="min-w-0 text-sm font-bold">
                                      <span>{label}</span>
                                      <span className="px-1 font-normal text-muted-foreground">-</span>
                                      <span>{balance.formatted}</span>
                                    </p>
                                    <Tooltip>
                                      <TooltipTrigger render={<p className="shrink-0 cursor-default text-xs text-muted-foreground" />}>
                                        {balance.percentageUsed.toFixed(0)}% used
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {formatBalanceSegmentAmount(key, segments.used)} used of{' '}
                                        {formatBalanceSegmentAmount(key, segments.total)} total
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <div className="mt-2">
                                    <BalanceSegmentBar balance={balance} categoryKey={key} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <Accordion defaultValue={['active-plans']}>
                            <AccordionItem value="active-plans">
                              <AccordionTrigger>
                                <SectionLabelWithCount title="Active Plans" count={activePlanCount} />
                              </AccordionTrigger>
                              <AccordionContent>
                                {activePlanCount === 0 ? (
                                  <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                                    No active plans found.
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 gap-3">
                                    {bundleLabels.map(({ key, label }) => {
                                      const bundles = (novaBalancePlans.bundles[key] || []).filter(
                                        (b) => !isBasePlanName(b.name),
                                      );
                                      if (bundles.length === 0) return null;
                                      return (
                                        <div key={key} className="rounded-md border p-3">
                                          <div className="mb-3">
                                            <SectionLabelWithCount title={label} count={bundles.length} />
                                          </div>
                                          <div className="space-y-2">
                                            {bundles.map((bundle, index) => (
                                              <div key={`${bundle.name}-${index}`} className="rounded-md bg-muted/40 p-2">
                                                <div className="flex items-center justify-between gap-2">
                                                  <p className="text-sm font-medium">{bundle.name}</p>
                                                  <Badge variant={bundle.remainingPercent > 20 ? 'success' : 'destructive'}>
                                                    {bundle.remainingPercent}%
                                                  </Badge>
                                                </div>
                                                <p className="mt-1 text-xs text-muted-foreground">{bundle.formattedUsage}</p>
                                                <p className="mt-1 text-xs text-muted-foreground">{bundle.formattedTimeRemaining}</p>
                                                {bundle.components && bundle.components.length > 1 && (
                                                  <div className="mt-2 space-y-1">
                                                    {bundle.components.map((component) => (
                                                      <div key={`${bundle.name}-${component.category}-${component.label}`} className="text-xs text-muted-foreground">
                                                        {component.label}: {component.formattedUsage}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>

                          <Accordion defaultValue={['base-plans']}>
                            <AccordionItem value="base-plans">
                              <AccordionTrigger>
                                <div className="flex items-center gap-2">
                                  {basePlanHasIssue && <IconAlertCircle className="size-4 text-destructive" />}
                                  <SectionLabelWithCount title="Base Plans" count={basePlanActiveCount} />
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                  {basePlanGroups.map((group) => {
                                    if (group.isMissing) {
                                      return (
                                        <div
                                          key={group.name}
                                          className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
                                        >
                                          <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm font-medium">{group.name}</p>
                                            <Badge variant="destructive">Missing</Badge>
                                          </div>
                                          <p className="mt-1 text-xs text-destructive">Missing required base plan</p>
                                        </div>
                                      );
                                    }

                                    const attached = attachedBundlesByBasePlan.get(group.name) ?? [];

                                    return group.subscriptions.map((subscription) => (
                                      <div
                                        key={subscription.id}
                                        className={cn(
                                          'rounded-md border p-3',
                                          group.isDuplicate && 'border-destructive/40 bg-destructive/5',
                                        )}
                                      >
                                        <div className="flex items-center justify-between gap-2">
                                          <p className="text-sm font-medium">{group.name}</p>
                                          <div className="flex flex-wrap justify-end gap-1">
                                            <Badge variant="secondary">Base Plan</Badge>
                                            {group.isDuplicate && <Badge variant="destructive">Duplicate</Badge>}
                                          </div>
                                        </div>
                                        <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                                          {subscription.id}
                                        </p>
                                        <p className="mt-1 text-xs text-muted-foreground">
                                          {group.isDuplicate
                                            ? `Duplicate base plan: ${group.count} active subscriptions`
                                            : 'Required base plan'}
                                        </p>
                                        {attached.length > 0 && (
                                          <div className="mt-2 space-y-2 rounded-md bg-muted/40 p-2">
                                            <p className="text-xs font-medium">Attached balances</p>
                                            {attached.map((bundle, bundleIndex) => {
                                              const rows = bundle.components && bundle.components.length > 0
                                                ? bundle.components
                                                : [
                                                    {
                                                      label:
                                                        bundleLabels.find(({ key }) => key === bundle.category)?.label ??
                                                        bundle.category,
                                                      remainingPercent: bundle.remainingPercent,
                                                      formattedUsage: bundle.formattedUsage,
                                                    },
                                                  ];
                                              return (
                                                <div key={`${bundle.planSubscriptionId ?? bundle.name}-${bundleIndex}`}>
                                                  {rows.map((row, rowIndex) => (
                                                    <div
                                                      key={`${row.label}-${rowIndex}`}
                                                      className="flex items-center justify-between gap-2"
                                                    >
                                                      <span className="truncate text-xs font-medium">{row.label}</span>
                                                      <span className="text-xs text-muted-foreground">
                                                        {row.remainingPercent}% - {row.formattedUsage}
                                                      </span>
                                                    </div>
                                                  ))}
                                                  <p className="mt-1 flex justify-end text-xs text-muted-foreground">
                                                    {bundle.formattedTimeRemaining}
                                                  </p>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    ));
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>

                          <Accordion defaultValue={['other-balances']}>
                            <AccordionItem value="other-balances">
                              <AccordionTrigger>
                                <SectionLabelWithCount title="Other Balances" count={otherBalances.length} />
                              </AccordionTrigger>
                              <AccordionContent>
                                {otherBalances.length === 0 ? (
                                  <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                                    No other balances found.
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                    {otherBalances.map((balance) => {
                                      const segments = getOtherBalanceSegments(balance);
                                      return (
                                        <div key={balance.balanceId} className="rounded-md border p-3">
                                          <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm font-medium">{getBalanceLabel(balance)}</p>
                                            <Badge variant={segments.remainingPercent > 20 ? 'success' : 'destructive'}>
                                              {segments.remainingPercent}%
                                            </Badge>
                                          </div>
                                          <p className="mt-1 text-xs text-muted-foreground">
                                            {formatOtherBalanceUsage(balance)} ({segments.usedPercent}% used)
                                          </p>
                                          <p className="mt-1 text-xs text-muted-foreground">
                                            {formatOtherBalanceExpiry(balance)}
                                          </p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                        );
                      })()}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}

            {/* Customer Information Block */}
            {account.inOurDatabase !== false && (
              <div>
                <Accordion multiple defaultValue={['open']}>
                  <AccordionItem value="open">
                    <AccordionTrigger>
                      <div className="flex items-center gap-2">
                        <IconUser className="size-4" />
                        <p className={accordionTitleClass}>Customer Information</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Table>
                        <TableBody>
                          {account.i_customer && (
                            <TableRow>
                              <TableCell className="font-bold w-[45%] text-sm">Customer ID</TableCell>
                              <TableCell className="text-sm">{account.i_customer}</TableCell>
                            </TableRow>
                          )}
                          {(account.mvno || account.tenant?.mvno) && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Brand</TableCell>
                              <TableCell className="text-sm">
                                <Badge variant="outline">
                                  {account.mvno || account.tenant?.mvno}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          )}
                          {account.nova_account_id && String(account.nova_account_id) !== String(account.i_customer) && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Nova Account ID</TableCell>
                              <TableCell className="text-sm">{account.nova_account_id}</TableCell>
                            </TableRow>
                          )}
                          {account.legacy_i_customer && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Legacy Customer ID</TableCell>
                              <TableCell className="text-sm">{account.legacy_i_customer}</TableCell>
                            </TableRow>
                          )}
                          {account.account_uuid && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Account UUID</TableCell>
                              <TableCell className="text-xs font-mono break-all">{account.account_uuid}</TableCell>
                            </TableRow>
                          )}
                          {novaAccountDetails?.uuid && novaAccountDetails.uuid !== account.account_uuid && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Nova Account UUID</TableCell>
                              <TableCell className="text-xs font-mono break-all">{novaAccountDetails.uuid}</TableCell>
                            </TableRow>
                          )}
                          {(account.first_name || account.last_name) && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Name</TableCell>
                              <TableCell className="text-sm">{`${account.first_name || ''} ${account.last_name || ''}`.trim()}</TableCell>
                            </TableRow>
                          )}
                          {account.id_number && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">ID Number</TableCell>
                              <TableCell className="text-sm">
                                {idNumberAccountCount > 1 ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="font-bold p-0 text-sm"
                                    onClick={() => handleIdNumberClick(account.id_number!)}
                                  >
                                    {account.id_number}
                                  </Button>
                                ) : (
                                  account.id_number
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                          {account.email && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Email</TableCell>
                              <TableCell className="text-sm break-all">{account.email}</TableCell>
                            </TableRow>
                          )}
                          {novaAccountDetails && (
                            <>
                              <TableRow>
                                <TableCell colSpan={2} className={tableSubsectionCellClass}>
                                  <SectionLabelWithCount
                                    title="Linked App Users"
                                    count={(novaAccountDetails.linkedUsers ?? []).length}
                                  />
                                </TableCell>
                              </TableRow>
                              {(novaAccountDetails.linkedUsers ?? []).length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={2} className="pl-6 text-sm text-muted-foreground">
                                    No linked app users.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                (novaAccountDetails.linkedUsers ?? []).map((user) => (
                                  <TableRow key={user.uuid}>
                                    <TableCell className="font-bold text-sm pl-6">
                                      <div className="flex flex-wrap items-center gap-1">
                                        <span>{formatLinkedUserName(user)}</span>
                                        {user.isPrimary && <Badge variant="outline">Primary</Badge>}
                                        <Badge variant={user.verified ? 'success' : 'secondary'}>
                                          {user.verified ? 'Verified' : 'Unverified'}
                                        </Badge>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {user.username && (
                                        <p className="break-all">
                                          <span className="font-medium">Username:</span> {user.username}
                                        </p>
                                      )}
                                      {user.email && (
                                        <p className="break-all">
                                          <span className="font-medium">Email:</span> {user.email}
                                        </p>
                                      )}
                                      <p className="text-xs font-mono break-all text-muted-foreground">{user.uuid}</p>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </>
                          )}
                          {!novaAccountDetails && account.linked_app_user && (
                            <>
                              <TableRow>
                                <TableCell colSpan={2} className={tableSubsectionCellClass}>
                                  <SectionLabelWithCount title="Linked App User" />
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-bold text-sm pl-6">User</TableCell>
                                <TableCell className="text-sm">
                                  {[
                                    account.linked_app_user.firstName,
                                    account.linked_app_user.lastName,
                                  ].filter(Boolean).join(' ') || account.linked_app_user.email || account.linked_app_user.username || 'Linked'}
                                </TableCell>
                              </TableRow>
                              {account.linked_app_user.email && (
                                <TableRow>
                                  <TableCell className="font-bold text-sm pl-6">Email</TableCell>
                                  <TableCell className="text-sm break-all">{account.linked_app_user.email}</TableCell>
                                </TableRow>
                              )}
                              {account.linked_app_user.username && (
                                <TableRow>
                                  <TableCell className="font-bold text-sm pl-6">Username</TableCell>
                                  <TableCell className="text-sm break-all">{account.linked_app_user.username}</TableCell>
                                </TableRow>
                              )}
                              {account.linked_app_user.recoveryPhoneE164 && (
                                <TableRow>
                                  <TableCell className="font-bold text-sm pl-6">Recovery Phone</TableCell>
                                  <TableCell className="text-sm">{account.linked_app_user.recoveryPhoneE164}</TableCell>
                                </TableRow>
                              )}
                              {account.linked_app_user.status && (
                                <TableRow>
                                  <TableCell className="font-bold text-sm pl-6">Status</TableCell>
                                  <TableCell className="text-sm">
                                    <Badge variant={account.linked_app_user.status === 'ACTIVE' ? 'success' : 'outline'}>
                                      {account.linked_app_user.status}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          )}
                          {account.created_at && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Created At</TableCell>
                              <TableCell className="text-sm">{formatDate(account.created_at)}</TableCell>
                            </TableRow>
                          )}
                          {!account.created_at && novaAccountDetails?.createdAt && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Created At</TableCell>
                              <TableCell className="text-sm">{formatDate(novaAccountDetails.createdAt)}</TableCell>
                            </TableRow>
                          )}
                          {novaAccountDetails?.updatedAt && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Updated At</TableCell>
                              <TableCell className="text-sm">{formatDate(novaAccountDetails.updatedAt)}</TableCell>
                            </TableRow>
                          )}
                          {account.created_at && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Customer Since</TableCell>
                              <TableCell className="text-sm">
                                {(() => {
                                  const createdDate = new Date(account.created_at);
                                  const now = new Date();
                                  const diffTime = Math.abs(now.getTime() - createdDate.getTime());
                                  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                  const diffMonths = Math.floor(diffDays / 30);
                                  const diffYears = Math.floor(diffDays / 365);
                                  
                                  if (diffYears > 0) {
                                    return `${diffYears} year${diffYears !== 1 ? 's' : ''} ${diffMonths % 12} month${(diffMonths % 12) !== 1 ? 's' : ''}`;
                                  } else if (diffMonths > 0) {
                                    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
                                  } else {
                                    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
                                  }
                                })()}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}

            {/* Login Information Block */}
            {account.inOurDatabase !== false && (
              <div>
                <Accordion multiple defaultValue={['open']}>
                  <AccordionItem value="open">
                    <AccordionTrigger>
                      <div className="flex items-center gap-2">
                        <IconInfoCircle className="size-4" />
                        <p className={accordionTitleClass}>Login Information</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Table>
                        <TableBody>
                          {account.last_app_login && (
                            <TableRow>
                              <TableCell className="font-bold w-[45%] text-sm">Last Login</TableCell>
                              <TableCell className="text-sm">
                                {account.last_app_login ? new Date(account.last_app_login).toLocaleString('en-US', {
                                  month: '2-digit',
                                  day: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit',
                                  hour12: false
                                }) : '-'}
                              </TableCell>
                            </TableRow>
                          )}
                          {account.lat && account.lng && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Location</TableCell>
                              <TableCell className="text-sm">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm">
                                    {parseFloat(account.lat).toFixed(6)}, {parseFloat(account.lng).toFixed(6)}
                                  </p>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setMapLocation({ lat: account.lat, lng: account.lng });
                                      setMapDialogOpen(true);
                                    }}
                                    className="text-xs"
                                  >
                                    View on Map
                                  </Button>
                                  {account.i_customer && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={async () => {
                                        if (!account.i_customer) return;
                                        setLoadingLoginHistory(true);
                                        try {
                                          const res = await novaOpsApi.get(`/ops/customers/login-history?${canonicalAccountQuery(account.i_customer, account.mvno, account.account_uuid)}`);
                                          const loginsWithMetadata = (res.data.logins || []).map((login: any) => ({
                                            ...login,
                                            lat: login.location_lat || login.lat,
                                            lng: login.location_lng || login.lng,
                                          }));
                                          setLoginHistory(loginsWithMetadata);
                                          setLoginHistoryBinnedCounts(res.data.binnedCounts || null);
                                          setLoginHistoryDialogOpen(true);
                                        } catch (err: any) {
                                          console.error('Failed to fetch login history:', err);
                                          const errorMessage = getApiErrorMessage(err, 'Failed to load login history');
                                          alert(`Failed to load login history: ${errorMessage}`);
                                        } finally {
                                          setLoadingLoginHistory(false);
                                        }
                                      }}
                                      disabled={loadingLoginHistory}
                                      className="text-xs"
                                    >
                                      {loadingLoginHistory ? 'Loading...' : 'View Login History'}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          {account.device_type && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Device Type</TableCell>
                              <TableCell className="text-sm">{account.device_type}</TableCell>
                            </TableRow>
                          )}
                          {account.device_id && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Device ID</TableCell>
                              <TableCell className="text-sm">{account.device_id}</TableCell>
                            </TableRow>
                          )}
                          {account.app_version && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">App Version</TableCell>
                              <TableCell className="text-sm">{account.app_version}</TableCell>
                            </TableRow>
                          )}
                          {!account.last_app_login &&
                            !(account.lat && account.lng) &&
                            !account.device_type &&
                            !account.device_id &&
                            !account.app_version && (
                            <TableRow>
                              <TableCell colSpan={2} className="text-sm text-muted-foreground">
                                No app login history recorded for this account.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}

            {/* Distributor & RICA Information Block */}
            {account.inOurDatabase !== false && (
              <div>
                <Accordion multiple defaultValue={['open']}>
                  <AccordionItem value="open">
                    <AccordionTrigger>
                      <div className="flex items-center gap-2">
                        <IconId className="size-4" />
                        <p className={accordionTitleClass}>Distributor & RICA</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {suspension?.isRica && (
                        <Alert variant="destructive" className="mb-3 border-destructive/30">
                          <IconAlertCircle className="size-4" />
                          <AlertTitle>RICA suspended</AlertTitle>
                          <AlertDescription>
                            <p>{suspension.reason}</p>
                            {canManageCustomerLifecycle && account.account_uuid && account.mvno ? (
                              <RicaDeadlineRecovery
                                key={`${account.mvno}:${account.account_uuid}`}
                                accountUuid={account.account_uuid}
                                mvno={account.mvno}
                                showUnavailableReason
                              />
                            ) : (
                              <p>{!canManageCustomerLifecycle
                                ? 'Contact a team member with customer lifecycle access to check resend availability.'
                                : 'No linked verification account is available. This suspension needs case review.'}</p>
                            )}
                          </AlertDescription>
                        </Alert>
                      )}
                      <Table>
                        <TableBody>
                          {account.distributor_i_customer && (
                            <TableRow>
                              <TableCell className="font-bold w-[45%] text-sm">Distributor ID</TableCell>
                              <TableCell className="text-sm">{account.distributor_i_customer}</TableCell>
                            </TableRow>
                          )}
                          {account.distributor?.company_name && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Distributor Name</TableCell>
                              <TableCell className="text-sm">{account.distributor.company_name}</TableCell>
                            </TableRow>
                          )}
                          {!account.distributor?.company_name && novaAccountDetails?.attributedPartner?.name && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Distributor Name</TableCell>
                              <TableCell className="text-sm">{novaAccountDetails.attributedPartner.name}</TableCell>
                            </TableRow>
                          )}
                          {account.distributor?.code && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Distributor Code</TableCell>
                              <TableCell className="text-sm">{account.distributor.code}</TableCell>
                            </TableRow>
                          )}
                          {novaAccountDetails?.attributedPartner?.uuid && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Partner UUID</TableCell>
                              <TableCell className="text-xs font-mono break-all">{novaAccountDetails.attributedPartner.uuid}</TableCell>
                            </TableRow>
                          )}
                          {novaAccountDetails?.ricaEntity?.uuid && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">RICA UUID</TableCell>
                              <TableCell className="text-xs font-mono break-all">{novaAccountDetails.ricaEntity.uuid}</TableCell>
                            </TableRow>
                          )}
                          {account.self_rica !== undefined && account.self_rica !== null && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Self RICA</TableCell>
                              <TableCell className="text-sm">
                                <Badge variant={account.self_rica === 1 ? 'success' : 'outline'}>
                                  {account.self_rica === 1 ? 'Yes' : 'No'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          )}
                          {account.self_rica === 1 && account.self_rica_channel && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Self RICA Channel</TableCell>
                              <TableCell className="text-sm">{account.self_rica_channel}</TableCell>
                            </TableRow>
                          )}
                          {account.self_rica === 1 && account.self_rica_photo_match_percentage_faceapp !== null && account.self_rica_photo_match_percentage_faceapp !== undefined && (() => {
                            const faceMatchScore = Number(account.self_rica_photo_match_percentage_faceapp);
                            if (!Number.isFinite(faceMatchScore)) return null;
                            return (
                            <TableRow>
                              <TableCell className="font-bold text-sm">Photo Match Percentage (FaceApp)</TableCell>
                              <TableCell className="text-sm">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm">
                                    {faceMatchScore.toFixed(2)}%
                                  </p>
                                  <Badge
                                    variant={
                                      faceMatchScore >= 80 ? 'success' :
                                      faceMatchScore >= 60 ? 'warning' :
                                      'destructive'
                                    }
                                  >
                                    {faceMatchScore >= 80 ? 'High Match' : faceMatchScore >= 60 ? 'Medium Match' : 'Low Match'}
                                  </Badge>
                                </div>
                              </TableCell>
                            </TableRow>
                            );
                          })()}
                          {account.rica_agent && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">RICA Agent</TableCell>
                              <TableCell className="text-sm">{account.rica_agent}</TableCell>
                            </TableRow>
                          )}
                          {!account.rica_agent && account.self_rica === 0 && (
                            <TableRow>
                              <TableCell className="font-bold text-sm">RICA Agent</TableCell>
                              <TableCell className="text-sm italic text-muted-foreground">Not specified</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      
                      {/* Self RICA Images */}
                      {account.self_rica === 1 && (
                        <div className="mt-6 border-t pt-4">
                          <SectionLabelWithCount title="Self RICA Documents" />
                          <div className="mt-4 grid grid-cols-12 gap-4">
                            
                            {(account.id_url || account.id_url_2) && (
                              <div className="col-span-12 sm:col-span-6">
                                <div className="rounded-lg border border-border">
                                  <div className="p-4">
                                    <span className="text-xs text-muted-foreground block mb-2">
                                      Selfie
                                    </span>
                                    <img
                                      src={account.id_url || account.id_url_2}
                                      alt="Selfie"
                                      className="w-full max-h-[200px] object-contain cursor-pointer rounded border border-border"
                                      onClick={() => {
                                        setSelectedImageUrl(account.id_url || account.id_url_2);
                                        setSelectedImageTitle('Selfie');
                                        setImageDialogOpen(true);
                                      }}
                                      onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {account.selfie_filename && !account.id_url && !account.id_url_2 && (
                              <div className="col-span-12 sm:col-span-6">
                                <div className="rounded-lg border border-border">
                                  <div className="p-4">
                                    <span className="text-xs text-muted-foreground block mb-2">
                                      Selfie
                                    </span>
                                    <img
                                      src={account.selfie_filename.startsWith('http') 
                                        ? account.selfie_filename 
                                        : `https://storage.googleapis.com/megs_api/customer_selfies/${account.selfie_filename}`}
                                      alt="Selfie"
                                      className="w-full max-h-[200px] object-contain cursor-pointer rounded border border-border"
                                      onClick={() => {
                                        const selfieUrl = account.selfie_filename.startsWith('http') 
                                          ? account.selfie_filename 
                                          : `https://storage.googleapis.com/megs_api/customer_selfies/${account.selfie_filename}`;
                                        setSelectedImageUrl(selfieUrl);
                                        setSelectedImageTitle('Selfie');
                                        setImageDialogOpen(true);
                                      }}
                                      onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}



                </div>
              </div>
              </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="1">
            {account?.i_customer && (
              <div className="p-6">
                <Alert className="mb-4"><AlertDescription>This view shows Nova account transactions. The complete wallet ledger, including wallet transfers and fees, is in Wallet transaction history. <Button variant="link" onClick={() => setActiveTab(3)}>View wallet history</Button></AlertDescription></Alert>

                  <div>
                    <div className="mb-6 flex gap-2 flex-wrap">
                      {[
                        { label: '7 Days', value: 8 },
                        { label: '30 Days', value: 31 },
                        { label: '90 Days', value: 91 },
                        { label: '180 Days', value: 181 },
                        { label: '1 Year', value: 366 },
                        { label: 'All Time', value: 9999 },
                      ].map((btn) => (
                        <Button
                          key={btn.value}
                          variant={transactionsDays === btn.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setTransactionsDays(btn.value)}
                        >
                          {btn.label}
                        </Button>
                      ))}
                    </div>

                    {loadingTransactions ? (
                      <div className="flex justify-center py-8">
                        <IconLoader2 className="h-8 w-8 animate-spin" />
                      </div>
                    ) : transactionsError ? (
                      <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{transactionsError}</AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        {transactionsSummary && (
                          <div className="flex gap-6 mb-6 w-full">
                            <div className="flex-1 min-w-0">
                              <StatCard title="Total Transactions" value={transactionsSummary.totalTransactions.toLocaleString('en-US')} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <StatCard title="Money In" value={`R ${transactionsSummary.moneyIn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <StatCard title="Money Out" value={`R ${transactionsSummary.moneyOut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <StatCard title="Net" value={`R ${(transactionsSummary.moneyIn - transactionsSummary.moneyOut).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                            </div>
                          </div>
                        )}

                        {transactionsSummary && Object.keys(transactionsSummary.transactionTypes).length > 0 && (
                          <MainCard className="mb-6">
                            <div className="p-4">
                              <h3 className="text-lg font-semibold mb-2">Transaction Types Summary</h3>
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="font-bold">Type</TableHead>
                                  <TableHead className="font-bold text-right">Count</TableHead>
                                  <TableHead className="font-bold text-right">Money In</TableHead>
                                  <TableHead className="font-bold text-right">Money Out</TableHead>
                                  <TableHead className="font-bold text-right">Net</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {Object.entries(transactionsSummary.transactionTypes).map(([typeName, data]: [string, any]) => (
                                  <TableRow key={typeName}>
                                    <TableCell>{typeName}</TableCell>
                                    <TableCell className="text-right">{data.count.toLocaleString('en-US')}</TableCell>
                                    <TableCell className="text-right">R {data.totalIn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                    <TableCell className="text-right">R {data.totalOut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                    <TableCell className="text-right">
                                      R {(data.totalIn - data.totalOut).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </MainCard>
                        )}

                        <MainCard>
                          <div className="p-4">
                            <h3 className="text-lg font-semibold mb-2">
                              Recent Transactions ({transactionsDays >= 9999 ? 'All Time' : `Last ${transactionsDays} Days`})
                            </h3>
                          </div>
                          {transactions.length === 0 ? (
                            <div className="p-8 text-center">
                              <p className="text-muted-foreground">
                                No transactions found {transactionsDays >= 9999 ? '' : `in the last ${transactionsDays} days`}
                              </p>
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="font-bold">Date / Reference</TableHead>
                                  <TableHead className="font-bold">Type / Product</TableHead>
                                  <TableHead className="font-bold">Channel / Payment / Voucher</TableHead>
                                  <TableHead className="font-bold text-right">Debit</TableHead>
                                  <TableHead className="font-bold text-right">Credit</TableHead>
                                  <TableHead className="font-bold text-right">Quantity</TableHead>
                                  <TableHead className="font-bold text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {transactions.map((tx) => (
                                  <TableRow key={tx.id}>
                                    <TableCell className="max-w-[200px]">
                                      <div>
                                        <p className="text-sm">
                                          {tx.created_at ? new Date(tx.created_at).toLocaleString('en-US', {
                                            month: '2-digit', day: '2-digit', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit', hour12: false
                                          }) : '-'}
                                        </p>
                                        {tx.reference ? (
                                          <Tooltip>
                                            <TooltipTrigger
                                              render={
                                                <span className="text-xs text-muted-foreground block mt-1 truncate max-w-full" />
                                              }
                                            >
                                              {tx.reference}
                                            </TooltipTrigger>
                                            <TooltipContent>{tx.reference}</TooltipContent>
                                          </Tooltip>
                                        ) : null}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <p className="text-sm font-medium">{tx.transaction_type_name || 'Unknown'}</p>
                                        {tx.product_name || tx.product_code ? (
                                          <span className="text-xs text-muted-foreground">{tx.product_name || tx.product_code}</span>
                                        ) : null}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        {tx.request_channel && (
                                          <span className="text-xs text-muted-foreground block">Channel: {tx.request_channel}</span>
                                        )}
                                        {tx.payment_method && (
                                          <span className="text-xs text-muted-foreground block">
                                            Payment: {tx.payment_method}
                                            {tx.payment_method === 'voucher' && tx.voucher_type && (<> ({tx.voucher_type})</>)}
                                          </span>
                                        )}
                                        {tx.voucher_type && tx.payment_method !== 'voucher' && (
                                          <span className="text-xs text-muted-foreground block">Voucher: {tx.voucher_type}</span>
                                        )}
                                        {!tx.request_channel && !tx.payment_method && !tx.voucher_type && (
                                          <span className="text-xs text-muted-foreground">-</span>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {tx.debit ? `R ${parseFloat(String(tx.debit)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {tx.credit ? `R ${parseFloat(String(tx.credit)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {tx.quantity ? parseFloat(String(tx.quantity)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-8 px-2"
                                        onClick={() => setSelectedTransaction(tx)}
                                      >
                                        <IconEye className="size-4" />
                                        Details
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </MainCard>
                      </>
                    )}
                  </div>
              </div>
            )}
            </TabsContent>

            {/* Transfers Tab */}
            <TabsContent value="2">
            {account?.i_customer && (
              <div className="p-6">
                <Alert className="mb-4"><AlertDescription>This view shows airtime transfers. Wallet money transfers, credits and fees are in Wallet transaction history. <Button variant="link" onClick={() => setActiveTab(3)}>View wallet history</Button></AlertDescription></Alert>

                <div>
                  <div className="mb-6 flex gap-2 flex-wrap">
                    {[
                      { label: '7 Days', value: 8 },
                      { label: '30 Days', value: 31 },
                      { label: '90 Days', value: 91 },
                      { label: '180 Days', value: 181 },
                      { label: '1 Year', value: 366 },
                    ].map((btn) => (
                      <Button
                        key={btn.value}
                        variant={transfersDays === btn.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setTransfersDays(btn.value)}
                      >
                        {btn.label}
                      </Button>
                    ))}
                    <Button
                      variant={transfersDays === null ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTransfersDays(null)}
                    >
                      All Time
                    </Button>
                  </div>

                  {loadingTransfers ? (
                    <div className="flex justify-center py-8">
                      <IconLoader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : transfersError ? (
                    <Alert variant="destructive" className="mb-4">
                      <AlertDescription>{transfersError}</AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      {transfersOverallSummary && (
                        <div className="flex gap-6 mb-6 w-full">
                          <div className="flex-1 min-w-0">
                            <StatCard title="Total Transfers" value={transfersOverallSummary.totalTransfers.toLocaleString('en-US')} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <StatCard title="Total Received" value={`R ${transfersOverallSummary.totalReceived.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <StatCard title="Total Sent" value={`R ${transfersOverallSummary.totalSent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <StatCard title="Unique Users" value={transfersOverallSummary.uniqueUsers.toLocaleString('en-US')} />
                          </div>
                        </div>
                      )}

                      <MainCard>
                        <Tabs value={String(transfersTab)} onValueChange={(val) => setTransfersTab(Number(val))}>
                          <TabsList variant="line" className="px-4 border-b border-border">
                            <TabsTrigger value="0">{transfersDays ? `All Transfers (${transfersDays}d)` : 'All Transfers (All Time)'}</TabsTrigger>
                            <TabsTrigger value="1">Summary by User</TabsTrigger>
                          </TabsList>

                          <TabsContent value="0">
                            {transfers.length === 0 ? (
                              <div className="p-8 text-center">
                                <p className="text-muted-foreground">
                                  No transfers found{transfersDays ? ` in the last ${transfersDays} days` : ''}
                                </p>
                              </div>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="font-bold">Date</TableHead>
                                    <TableHead className="font-bold">Direction</TableHead>
                                    <TableHead className="font-bold">Other User</TableHead>
                                    <TableHead className="font-bold">Type</TableHead>
                                    <TableHead className="font-bold">Reference</TableHead>
                                    <TableHead className="font-bold text-right">Amount</TableHead>
                                    <TableHead className="font-bold text-right">Data (MB)</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {transfers.map((tx: any) => {
                                    const isReceived = tx.transaction_type_name?.toLowerCase().includes(' in') ||
                                      tx.transaction_type_name?.toLowerCase().includes(' in -') ||
                                      tx.transfer_direction === 'received';
                                    return (
                                    <TableRow key={tx.id}>
                                      <TableCell>
                                        {tx.created_at ? new Date(tx.created_at).toLocaleString('en-US', {
                                          month: '2-digit', day: '2-digit', year: 'numeric',
                                          hour: '2-digit', minute: '2-digit', hour12: false
                                        }) : '-'}
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant={isReceived ? 'success' : 'destructive'}>
                                          {isReceived ? 'Received' : 'Sent'}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <div>
                                          <p className="text-sm">{tx.other_customer_name}</p>
                                          {(tx.other_customer_msisdn || tx.other_customer_id) && (
                                            <div className="flex items-center gap-1 mt-1">
                                              {renderCustomerAccountLink(
                                                tx.other_customer_msisdn,
                                                undefined,
                                                tx.other_customer_id,
                                              )}
                                              {(tx.other_customer_suspended === 1 || tx.other_customer_suspended === true) && (
                                                <Badge variant="destructive" className="text-[0.65rem] h-[18px] leading-none px-1">
                                                  Suspended
                                                </Badge>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell>{tx.transaction_type_name || `Type ${tx.transaction_type_id}`}</TableCell>
                                      <TableCell className="max-w-[200px]">
                                        {tx.reference ? (
                                          <Tooltip>
                                            <TooltipTrigger render={<p className="text-sm truncate" />}>
                                              {tx.reference}
                                            </TooltipTrigger>
                                            <TooltipContent>{tx.reference}</TooltipContent>
                                          </Tooltip>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {tx.quantity && parseFloat(tx.quantity) > 0 ? (
                                          <p className="text-sm text-muted-foreground">-</p>
                                        ) : (
                                          <p className={cn("text-sm", isReceived ? 'text-green-600' : 'text-red-600')}>
                                            {isReceived ? '+' : '-'}R {((parseFloat(tx.debit || 0) || parseFloat(tx.credit || 0))).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </p>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {tx.quantity && parseFloat(tx.quantity) > 0 ? (
                                          <p className={cn("text-sm", isReceived ? 'text-green-600' : 'text-red-600')}>
                                            {isReceived ? '+' : '-'}{parseFloat(tx.quantity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </p>
                                        ) : (
                                          <p className="text-sm text-muted-foreground">-</p>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}
                          </TabsContent>

                          <TabsContent value="1">
                            {transfersSummaryByUser.length === 0 ? (
                              <div className="p-8 text-center">
                                <p className="text-muted-foreground">No summary data available</p>
                              </div>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="font-bold">User</TableHead>
                                    <TableHead className="font-bold">ID Number</TableHead>
                                    <TableHead className="font-bold">MSISDN</TableHead>
                                    <TableHead className="font-bold text-right">Transfers</TableHead>
                                    <TableHead className="font-bold text-right">Money Received</TableHead>
                                    <TableHead className="font-bold text-right">Money Sent</TableHead>
                                    <TableHead className="font-bold text-right">Data Received (MB)</TableHead>
                                    <TableHead className="font-bold text-right">Data Sent (MB)</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {transfersSummaryByUser.map((summary: any, index: number) => (
                                    <TableRow key={summary.customer_id || index}>
                                      <TableCell>{summary.customer_name || `Customer ${summary.customer_id}`}</TableCell>
                                      <TableCell>
                                        {summary.customer_id_number ? (
                                          <a
                                            href={`/customers?search=idNumber&value=${encodeURIComponent(summary.customer_id_number)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline text-sm"
                                          >
                                            {summary.customer_id_number}
                                          </a>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell>
                                        {(summary.customer_msisdn || summary.customer_id) ? (
                                          <div className="flex items-center gap-1">
                                            {renderCustomerAccountLink(
                                              summary.customer_msisdn,
                                              'text-sm',
                                              summary.customer_id,
                                            )}
                                            {(summary.customer_suspended === 1 || summary.customer_suspended === true) && (
                                              <Badge variant="destructive" className="text-[0.65rem] h-[18px] leading-none px-1">
                                                Suspended
                                              </Badge>
                                            )}
                                          </div>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="text-right">{summary.transfer_count.toLocaleString('en-US')}</TableCell>
                                      <TableCell className="text-right">
                                        <span className="text-green-600">
                                          R {summary.total_received.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <span className="text-red-600">
                                          R {summary.total_sent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <span className="text-green-600">
                                          {summary.data_received_mb ? summary.data_received_mb.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <span className="text-red-600">
                                          {summary.data_sent_mb ? summary.data_sent_mb.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                        </span>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </TabsContent>
                        </Tabs>
                      </MainCard>
                    </>
                  )}
                </div>
              </div>
            )}
            </TabsContent>

            {/* Rewards Tab */}
            <TabsContent value="5">
            {account?.i_customer && (
              <div className="p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-lg font-semibold">Customer Rewards</h3>
                    {!loadingRewards && !rewardsError && rewardUsage && (
                      <Badge variant={rewardUsage.blocked ? 'destructive' : 'success'}>
                        {rewardUsage.blocked ? 'Suspended' : 'Active'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!loadingRewards && !rewardsError && rewardUsage && canPerformCustomerCareActions && (
                      <Button
                        variant={rewardUsage.blocked ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setRewardBlockReason('');
                          if (rewardUsage.blocked) {
                            setRewardUnblockDialogOpen(true);
                          } else {
                            setRewardBlockDialogOpen(true);
                          }
                        }}
                      >
                        {rewardUsage.blocked ? (
                          <IconLockOpen className="size-4 mr-2" />
                        ) : (
                          <IconBan className="size-4 mr-2" />
                        )}
                        {rewardUsage.blocked ? 'Unsuspend' : 'Suspend'}
                      </Button>
                    )}
                    {account?.i_customer && hasPageAccess('spar-mobile') && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => processReward(account.i_customer)}
                        disabled={processingReward === account.i_customer}
                      >
                        {processingReward === account.i_customer ? <IconLoader2 className="h-4 w-4 animate-spin mr-2" /> : <IconCircleCheck className="size-4 mr-2" />}
                        {processingReward === account.i_customer ? 'Processing...' : 'Reprocess Rewards'}
                      </Button>
                    )}
                  </div>
                </div>
                {loadingRewards && (
                  <div className="flex justify-center py-8">
                    <IconLoader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}
                {rewardsError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{rewardsError}</AlertDescription>
                  </Alert>
                )}
                {rewardsSuccess && (
                  <Alert className="border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200 mb-4">
                    <AlertDescription>{rewardsSuccess}</AlertDescription>
                  </Alert>
                )}
                {!loadingRewards && !rewardsError && customerRewards.length === 0 && (
                  <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
                    <AlertDescription>No rewards found for this customer.</AlertDescription>
                  </Alert>
                )}
                {!loadingRewards && !rewardsError && customerRewards.length > 0 && (
                  <>
                    {(() => {
                      const categories = ['promo-rules-engine', 'vouchers', 'ads', 'surveys', 'loyalty', 'hq-promo-allocation'];
                      
                      const stats = categories.reduce((acc, category) => {
                        const categoryRewards = customerRewards.filter((reward) => getRewardType(reward) === category);
                        const totalCredit = categoryRewards.reduce((sum, reward) => sum + getRewardCredit(reward), 0);
                        const totalDebit = categoryRewards.reduce((sum, reward) => sum + getRewardDebit(reward), 0);
                        acc[category] = { totalCredit, totalDebit, count: categoryRewards.length };
                        return acc;
                      }, {} as Record<string, { totalCredit: number; totalDebit: number; count: number }>);

                      const totalCredit = customerRewards.reduce((sum, reward) => sum + getRewardCredit(reward), 0);
                      const totalDebit = customerRewards.reduce((sum, reward) => sum + getRewardDebit(reward), 0);
                      const netTotal = totalCredit - totalDebit;

                      return (
                        <div className="mb-6">
                          <p className={cn(accordionTitleClass, 'mb-4')}>Reward Ledger Summary</p>
                          <div className="grid grid-cols-12 gap-4 mb-4">
                            {categories.map((category) => {
                              const stat = stats[category];
                              const displayName = category.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                              
                              return (
                                <div key={category} className="col-span-12 sm:col-span-6 md:col-span-4">
                                  <div className="rounded-lg border border-border p-4">
                                    <p className="text-sm text-muted-foreground mb-1">{displayName}</p>
                                    <h3 className="text-lg font-semibold text-green-600 font-bold">+{stat.totalCredit} MB</h3>
                                    <p className="text-sm text-red-600 font-bold">-{stat.totalDebit} MB</p>
                                    <span className="text-xs text-muted-foreground">{stat.count} reward{stat.count !== 1 ? 's' : ''}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="rounded-lg border border-border bg-primary/10 p-4">
                            <p className="text-sm mb-1"><strong>Total Reward Movement</strong></p>
                            <h3 className="text-xl font-bold">+{totalCredit} MB / -{totalDebit} MB</h3>
                            <p className="text-sm">Net: {netTotal} MB</p>
                            <span className="text-xs">{customerRewards.length} total reward{customerRewards.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      );
                    })()}
                  <div className="rounded-lg border border-border bg-card shadow-sm">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-bold">Date</TableHead>
                          <TableHead className="font-bold">Reward Type</TableHead>
                          <TableHead className="font-bold">Credit</TableHead>
                          <TableHead className="font-bold">Debit</TableHead>
                          <TableHead className="font-bold">Status</TableHead>
                          <TableHead className="font-bold">Spend Requirement</TableHead>
                          <TableHead className="font-bold">Spend Check Date</TableHead>
                          <TableHead className="font-bold">Allocated At</TableHead>
                          <TableHead className="font-bold">Expires At</TableHead>
                          <TableHead className="font-bold">Billing Transaction</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customerRewards.map((reward, index: number) => (
                          <TableRow key={reward.id || index}>
                            <TableCell>
                              <span className="whitespace-nowrap text-sm">
                                {reward.created_at ? formatAppDateTime(reward.created_at) : '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="info">{getRewardType(reward) || 'N/A'}</Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-green-600">{getRewardCredit(reward) ? `${getRewardCredit(reward)} MB` : '-'}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-red-600">{getRewardDebit(reward) ? `${getRewardDebit(reward)} MB` : '-'}</span>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                let displayStatus = reward.status || 'N/A';
                                let badgeVariant: 'success' | 'warning' | 'destructive' | 'outline' = 'outline';

                                if (reward.status === 'pending' && account?.spend_30_day !== undefined && account?.spend_30_day !== null && reward.spend_requirement) {
                                  const spend30Day = typeof account.spend_30_day === 'string' ? parseFloat(account.spend_30_day) : account.spend_30_day;
                                  const spendRequirement = typeof reward.spend_requirement === 'string' ? parseFloat(reward.spend_requirement) : reward.spend_requirement;

                                  if (spend30Day < spendRequirement) {
                                    displayStatus = 'spend not met';
                                    badgeVariant = 'destructive';
                                  } else {
                                    badgeVariant = 'warning';
                                  }
                                } else if (reward.status === 'allocated') {
                                  badgeVariant = 'success';
                                } else if (reward.status === 'pending') {
                                  badgeVariant = 'warning';
                                } else if (reward.status === 'failed') {
                                  badgeVariant = 'destructive';
                                }

                                return <Badge variant={badgeVariant}>{displayStatus}</Badge>;
                              })()}
                            </TableCell>
                            <TableCell>
                              {reward.spend_requirement ? `R${reward.spend_requirement.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            </TableCell>
                            <TableCell>
                              {reward.spend_check_date ? (() => {
                                const date = new Date(reward.spend_check_date);
                                const now = new Date();
                                const diffMs = now.getTime() - date.getTime();
                                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                const timestamp = formatAppDateTime(reward.spend_check_date);
                                const badgeVariant = diffDays >= 0 && diffDays < 7 ? 'success' :
                                  diffDays >= 7 && diffDays < 30 ? 'warning' :
                                  diffDays >= 30 ? 'destructive' : 'outline';
                                return (
                                  <Tooltip>
                                    <TooltipTrigger render={<Badge variant={badgeVariant} />}>
                                      {timestamp}
                                    </TooltipTrigger>
                                    <TooltipContent>{timestamp}</TooltipContent>
                                  </Tooltip>
                                );
                              })() : <Badge variant="outline">-</Badge>}
                            </TableCell>
                            <TableCell>
                              {getRewardAllocatedAt(reward) ? (() => {
                                const allocatedAt = getRewardAllocatedAt(reward);
                                const date = new Date(allocatedAt as string);
                                const now = new Date();
                                const diffMs = now.getTime() - date.getTime();
                                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                const timestamp = formatAppDateTime(allocatedAt);
                                const badgeVariant = diffDays >= 0 && diffDays < 7 ? 'success' :
                                  diffDays >= 7 && diffDays < 30 ? 'warning' :
                                  diffDays >= 30 ? 'destructive' : 'outline';
                                return (
                                  <Tooltip>
                                    <TooltipTrigger render={<Badge variant={badgeVariant} />}>
                                      {timestamp}
                                    </TooltipTrigger>
                                    <TooltipContent>{timestamp}</TooltipContent>
                                  </Tooltip>
                                );
                              })() : <Badge variant="outline">-</Badge>}
                            </TableCell>
                            <TableCell>
                              {reward.expires_at ? (() => {
                                const date = new Date(reward.expires_at);
                                const now = new Date();
                                const diffMs = date.getTime() - now.getTime();
                                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                const timestamp = formatAppDateTime(reward.expires_at);
                                const badgeVariant = diffMs < 0 ? 'destructive' :
                                  diffDays < 7 ? 'warning' :
                                  'success';
                                return (
                                  <Tooltip>
                                    <TooltipTrigger render={<Badge variant={badgeVariant} />}>
                                      {timestamp}
                                    </TooltipTrigger>
                                    <TooltipContent>{timestamp}</TooltipContent>
                                  </Tooltip>
                                );
                              })() : <Badge variant="outline">-</Badge>}
                            </TableCell>
                            <TableCell>
                              {getRewardBillingTransactionId(reward) ? (
                                <Tooltip>
                                  <TooltipTrigger render={<p className="text-sm max-w-[150px] truncate" />}>
                                    {getRewardBillingTransactionId(reward)}
                                  </TooltipTrigger>
                                  <TooltipContent>{getRewardBillingTransactionId(reward)}</TooltipContent>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  </>
                )}
              </div>
            )}
            </TabsContent>

            {/* Wallet Tab */}
            <TabsContent value="3">
              <WalletPane
                key={`${account?.mvno ?? ''}:${account?.app_user_uuid ?? ''}:${account?.i_customer ?? ''}`}
                scope={{
                  mvno: account?.mvno,
                  appUserUuid: account?.app_user_uuid,
                  iCustomer: account?.i_customer,
                }}
                canReleaseHold={hasWriteAccess('customers')}
              />
            </TabsContent>

            {/* Vouchers Section */}
            <TabsContent value="1">
              <div className="px-6 pb-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold">Voucher Status</h3>
                </div>

                {voucherStatusError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{voucherStatusError}</AlertDescription>
                  </Alert>
                )}

                {voucherSmsResult && (
                  <Alert
                    variant={voucherSmsResult.success ? 'default' : 'destructive'}
                    className="mb-4"
                  >
                    <AlertDescription>{voucherSmsResult.message}</AlertDescription>
                  </Alert>
                )}

                {(() => {
                  const vouchers = voucherStatus?.vouchers ?? [];
                  const purchasedVouchers = vouchers.filter(isPurchasedVoucher);
                  const redeemedByCustomerVouchers = vouchers.filter(isRedeemedByCustomerVoucher);

                  const renderVoucherTable = (
                    rows: VoucherStatusResponse['vouchers'],
                    options: { showResend: boolean; emptyText: string },
                  ) => (
                    rows.length ? (
                      <Table className="w-full table-fixed">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[132px]">Date</TableHead>
                            <TableHead>Voucher</TableHead>
                            <TableHead className="w-[150px]">Status</TableHead>
                            <TableHead className="w-[100px] text-right">Value</TableHead>
                            <TableHead className="w-[120px] text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((voucher) => {
                            const careStatus = getVoucherCareStatus(voucher);
                            const voucherCode = getVoucherCodeDisplay(voucher);
                            return (
                              <TableRow key={`${voucher.id}-${voucher.voucher_id}-${options.showResend ? 'purchased' : 'redeemed'}`}>
                                <TableCell className="align-top text-xs">
                                  {formatDate(voucher.redeemed_at || voucher.confirmed_at || voucher.requested_at || voucher.created_at || undefined)}
                                </TableCell>
                                <TableCell className="align-top">
                                  <p className="text-sm font-medium">{voucher.product_name || voucher.value_type || 'Voucher'}</p>
                                  {voucher.voucher_id && (
                                    <p className="break-all font-mono text-xs text-muted-foreground">{voucher.voucher_id}</p>
                                  )}
                                  {voucherCode.display ? (
                                    <p className="break-all font-mono text-xs text-muted-foreground">
                                      {voucherCode.isFull ? 'Full code' : 'Code'}: {voucherCode.display}
                                    </p>
                                  ) : voucherCode.fallback ? (
                                    <p className="text-xs text-muted-foreground">{voucherCode.fallback}</p>
                                  ) : null}
                                </TableCell>
                                <TableCell className="align-top">
                                  <Badge
                                    variant={statusBadgeVariant(careStatus) as any}
                                    title={getVoucherStatusCopy(careStatus).description}
                                  >
                                    {getVoucherStatusCopy(careStatus).label}
                                  </Badge>
                                  {voucher.status && voucher.status !== careStatus && (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                      Source: {formatStatusLabel(voucher.status)}
                                    </p>
                                  )}
                                  {voucher.reversal_reason && (
                                    <p className="mt-1 text-xs text-destructive">{voucher.reversal_reason}</p>
                                  )}
                                </TableCell>
                                <TableCell className="align-top text-right">{voucher.amount !== null ? formatRand(voucher.amount) : '-'}</TableCell>
                                <TableCell className="align-top">
                                  <div className="flex justify-end">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setSelectedVoucher(voucher)}
                                    >
                                      <IconEye className="size-4" />
                                      Details
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="p-8 text-center text-muted-foreground">{options.emptyText}</div>
                    )
                  );

                  return (
                    <>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mb-6">
                        <StatCard title="Related Vouchers" value={String(voucherStatus?.summary?.totalVouchers ?? vouchers.length)} />
                        <StatCard title="Purchased from MSISDN" value={String(voucherStatus?.summary?.purchasedVouchers ?? purchasedVouchers.length)} />
                        <StatCard title="Redeemed by Customer" value={String(voucherStatus?.summary?.redeemedByCustomerVouchers ?? redeemedByCustomerVouchers.length)} />
                      </div>

                      <div className="space-y-6">
                        <MainCard>
                          <div className="border-b border-border p-4">
                            <h4 className="text-base font-semibold">Vouchers Purchased from This MSISDN</h4>
                            <p className="text-sm text-muted-foreground">
                              These are voucher purchases linked to the customer account. Resend is only available here when the voucher has not been redeemed.
                            </p>
                          </div>
                          {renderVoucherTable(purchasedVouchers, {
                            showResend: true,
                            emptyText: 'No vouchers purchased from this customer MSISDN.',
                          })}
                        </MainCard>

                        <MainCard>
                          <div className="border-b border-border p-4">
                            <h4 className="text-base font-semibold">Vouchers Redeemed by This Customer</h4>
                            <p className="text-sm text-muted-foreground">
                              These are vouchers redeemed on this customer account, including vouchers bought elsewhere.
                            </p>
                          </div>
                          {renderVoucherTable(redeemedByCustomerVouchers, {
                            showResend: false,
                            emptyText: 'No vouchers redeemed by this customer.',
                          })}
                        </MainCard>
                      </div>
                    </>
                  );
                })()}
              </div>
            </TabsContent>

            {/* SIM & Network Section */}
            <TabsContent value="4">
              <div className="px-6 pb-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold">SIM & Network</h3>
                  <p className="text-sm text-muted-foreground">
                    SIM identity, MTN status, porting, SIM swap readiness, and SIM history.
                  </p>
                </div>

                {(() => {
                  return (
                    <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <StatCard title="MSISDN" value={account?.msisdn || account?.msisdn_dma || '-'} />
                      <StatCard title="SIM Status" value={formatStatusLabel(account?.sim_status || novaAccountDetails?.simCard?.status)} />
                      <StatCard
                        title="MTN Status"
                        value={loadingMtnStatus ? 'Checking' : getMtnStatusLabel(mtnStatus)}
                      />
                      <StatCard
                        title="SIM Swap"
                        value={simSwapSummaryLabel}
                      />
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <MainCard>
                    <div className="p-4">
                      <h4 className="text-base font-semibold">SIM Details</h4>
                    </div>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-bold w-[35%]">MSISDN</TableCell>
                          <TableCell>{account?.msisdn || account?.msisdn_dma || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">Account Status</TableCell>
                          <TableCell>
                            <Badge variant={statusBadgeVariant(account?.status || novaAccountDetails?.status) as any}>
                              {formatStatusLabel(account?.status || novaAccountDetails?.status)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">SIM Status</TableCell>
                          <TableCell>
                            <Badge variant={statusBadgeVariant(account?.sim_status || novaAccountDetails?.simCard?.status) as any}>
                              {formatStatusLabel(account?.sim_status || novaAccountDetails?.simCard?.status)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-bold">ICCID</TableCell>
                          <TableCell className="break-all font-mono text-xs">
                            {account?.sim_serial_number || novaAccountDetails?.simCard?.iccid || '-'}
                          </TableCell>
                        </TableRow>
                        <SimPukDetails
                          key={`${account?.mvno}:${account?.account_uuid ?? account?.i_customer}:${account?.sim_serial_number || novaAccountDetails?.simCard?.iccid || null}`}
                          accountUuid={account?.account_uuid}
                          iCustomer={account?.i_customer}
                          mvno={account?.mvno}
                          iccid={account?.sim_serial_number || novaAccountDetails?.simCard?.iccid || null}
                        />
                        {novaAccountDetails?.simCard?.starterPack && (
                          <TableRow>
                            <TableCell className="font-bold">Starter Pack</TableCell>
                            <TableCell>
                              {novaAccountDetails.simCard.starterPack.name} ({novaAccountDetails.simCard.starterPack.code})
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </MainCard>

                  <MainCard>
                    <div className="p-4">
                      <h4 className="text-base font-semibold">MTN Status</h4>
                    </div>
                    {mtnError && (
                      <Alert variant="destructive" className="mx-4 mb-4 w-auto">
                        <AlertDescription>{mtnError}</AlertDescription>
                      </Alert>
                    )}
                    {loadingMtnStatus ? (
                      <div className="flex justify-center py-8">
                        <IconLoader2 className="h-8 w-8 animate-spin" />
                      </div>
                    ) : mtnStatus ? (
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-bold w-[35%]">Status</TableCell>
                            <TableCell>
                              <Badge variant={getMtnStatusLabel(mtnStatus) === 'Active' ? 'success' : 'outline'}>
                                {getMtnStatusLabel(mtnStatus)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-bold">Source</TableCell>
                            <TableCell>{mtnStatus.statusCode === '0' ? 'Live MTN network lookup' : mtnStatus.statusMessage || '-'}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="p-8 text-center text-muted-foreground">No MTN status loaded.</div>
                    )}
                  </MainCard>

                  <MainCard>
                    <div className="p-4">
                      <h4 className="text-base font-semibold">Porting Status</h4>
                    </div>
                    {portStatus?.latest ? (
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-bold w-[35%]">Latest Status</TableCell>
                            <TableCell>
                              <Badge variant={statusBadgeVariant(portStatus.latest.status) as any}>{portStatus.latest.status}</Badge>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-bold">Port MSISDN</TableCell>
                            <TableCell>{portStatus.latest.port_msisdn || '-'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-bold">Donor Network</TableCell>
                            <TableCell>{portStatus.latest.donor_network_label || portStatus.latest.donor_network_code || '-'}</TableCell>
                          </TableRow>
                          {portStatus.latest.last_error_message && (
                            <TableRow>
                              <TableCell className="font-bold">Last Error</TableCell>
                              <TableCell className="text-destructive">{portStatus.latest.last_error_message}</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="p-8 text-center text-muted-foreground">No Nova porting records found.</div>
                    )}
                  </MainCard>

                  <MainCard className="xl:col-span-2">
                    <div className="border-b border-border p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h4 className="text-base font-semibold">SIM Swap Readiness</h4>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Identity assurance, evidence signals, and policy blockers.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                              account?.i_customer &&
                              void fetchIdentityAssurance(
                                account.i_customer,
                                account.mvno,
                                account.account_uuid,
                              )
                            }
                            disabled={!account?.i_customer || loadingIdentityAssurance}
                          >
                            {loadingIdentityAssurance ? (
                              <IconLoader2 className="size-4 animate-spin" />
                            ) : (
                              <IconRefresh className="size-4" />
                            )}
                            Refresh verification
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={openSimSwapPromptDialog}
                            disabled={
                              !account?.i_customer ||
                              loadingIdentityAssurance ||
                              sendingSimSwapPrompt ||
                              !canPerformSimSwap ||
                              !canSendSimSwapVerification(currentSimSwapVerification, false)
                            }
                          >
                            {sendingSimSwapPrompt ? (
                              <IconLoader2 className="size-4 animate-spin" />
                            ) : (
                              <IconMessage className="size-4" />
                            )}
                            {currentSimSwapVerificationStatus === 'PENDING' ? 'Resend identity check' : 'Send identity check'}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4 p-4">
                      {(() => {
                        const fallbackAssurance = buildIdentityAssurance(account, novaAccountDetails);
                        const serverAssurance = identityAssurance?.identityAssurance;
                        const assuranceSignals: IdentityAssuranceSignal[] =
                          serverAssurance?.signals?.length
                            ? serverAssurance.signals
                            : fallbackAssurance.signals.map((signal, index) => ({
                                key: signal.label || String(index),
                                label: signal.label,
                                status: signal.ready ? 'ready' : 'missing',
                                value: signal.value,
                                mono: signal.mono,
                              }));
                        const completedSignals = assuranceSignals.filter((signal) => signal.status === 'ready').length;
                        const totalSignals = assuranceSignals.length || fallbackAssurance.totalSignals;
                        const assuranceLevel = serverAssurance?.level ?? fallbackAssurance.level;
                        const currentAssuranceLevel =
                          typeof serverAssurance?.level === 'string'
                            ? serverAssurance.level
                            : null;
                        const levelVariant =
                          currentAssuranceLevel && meetsSimSwapAssuranceLevel(currentAssuranceLevel)
                            ? 'success'
                            : completedSignals >= Math.ceil(totalSignals / 2)
                              ? 'warning'
                              : 'secondary';
                        const requiredAssuranceLevel =
                          serverAssurance?.requiredLevel ?? SIM_SWAP_MINIMUM_ASSURANCE_LEVEL;
                        const assuranceLevelLabel = currentAssuranceLevel
                          ? formatIdentityAssuranceLevel(currentAssuranceLevel)
                          : assuranceLevel;
                        const levelMeetsSimSwapMinimum = currentAssuranceLevel
                          ? meetsSimSwapAssuranceLevel(currentAssuranceLevel)
                          : null;
                        const blockers = serverAssurance?.blockers ?? [];
                        const currentSimSwap = currentSimSwapProcess;
                        const providerLabel =
                          serverAssurance?.provider &&
                          serverAssurance.provider !== currentAssuranceLevel
                            ? formatStatusLabel(serverAssurance.provider)
                            : null;

                        return (
                          <>
                            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/20 p-3">
                              <IconShieldCheck className="size-4 text-muted-foreground" />
                              <span className="text-sm font-semibold">Identity Assurance</span>
                              <Badge variant={levelVariant as any}>{assuranceLevelLabel}</Badge>
                              {providerLabel && (
                                <Badge variant="outline">{providerLabel}</Badge>
                              )}
                              {loadingIdentityAssurance && (
                                <Badge variant="outline">
                                  <IconLoader2 className="size-3 animate-spin mr-1" />
                                  Loading...
                                </Badge>
                              )}
                              <Badge variant={simSwapReadyForSubmit ? 'success' : 'secondary'}>
                                {simSwapSummaryLabel}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {completedSignals}/{totalSignals} required signals
                              </span>
                            </div>

                            <SimSwapVerificationPanel
                              verification={currentSimSwapVerification}
                              blockers={[
                                ...(serverAssurance?.blockers ?? []),
                                ...(currentSimSwapVerification?.blockers ?? []),
                              ]
                                .filter((blocker, index, values) => values.indexOf(blocker) === index)
                                .map(formatCustomerCareText)}
                              loading={loadingIdentityAssurance}
                              formatDate={formatDate}
                            />

                            {identityAssuranceError && (
                              <Alert variant="destructive">
                                <AlertDescription>
                                  {identityAssuranceError}. Historical evidence remains visible, but the current SIM swap verification is blocked until status loads.
                                </AlertDescription>
                              </Alert>
                            )}

                            <div className="space-y-4">
                              <div className="rounded-md border bg-muted/20 p-3 text-sm">
                                <div className="space-y-3">
                                  <div>
                                    <p className="font-semibold">SIM swap assurance policy</p>
                                    <p className="mt-1 text-muted-foreground">
                                      Historical assurance must include{' '}
                                      <span className="font-medium text-foreground">
                                        {formatIdentityAssuranceLevel(requiredAssuranceLevel)} or stronger
                                      </span>
                                      , plus a fresh current SIM swap verification and a linked RICA entity.
                                    </p>
                                  </div>
                                  {currentAssuranceLevel && (
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-muted-foreground">Current level:</span>
                                      <Badge variant="outline">{formatIdentityAssuranceLevel(currentAssuranceLevel)}</Badge>
                                      <Badge variant={levelMeetsSimSwapMinimum ? 'success' : 'warning'}>
                                        {levelMeetsSimSwapMinimum ? 'Meets minimum level' : 'Below minimum level'}
                                      </Badge>
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 rounded-md border bg-background">
                                  <Table>
                                    <TableBody>
                                      {IDENTITY_ASSURANCE_LEVELS.map((level) => {
                                        const isCurrent = currentAssuranceLevel === level.value;
                                        const isRequiredMinimum = level.value === requiredAssuranceLevel;
                                        return (
                                          <TableRow key={level.value} className={cn(isCurrent && 'bg-muted/40')}>
                                            <TableCell className="w-[34%] min-w-[220px] align-top py-2">
                                              <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="font-medium">{level.label}</span>
                                                {isCurrent && (
                                                  <Badge variant="info" className="px-1.5 py-0 text-[10px]">
                                                    Current
                                                  </Badge>
                                                )}
                                                {isRequiredMinimum && (
                                                  <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                                    Minimum
                                                  </Badge>
                                                )}
                                                <Badge
                                                  variant={level.simSwapEligible ? 'success' : 'secondary'}
                                                  className="px-1.5 py-0 text-[10px]"
                                                >
                                                  {level.simSwapEligible ? 'Meets level' : 'Insufficient'}
                                                </Badge>
                                              </div>
                                            </TableCell>
                                            <TableCell className="whitespace-normal align-top py-2 text-muted-foreground">
                                              {level.description}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>

                              <div className="rounded-md border">
                                <div className="border-b border-border p-3">
                                  <p className="text-sm font-semibold">Evidence Signals</p>
                                </div>
                                <Table>
                                  <TableBody>
                                    {assuranceSignals.map((signal) => (
                                      <TableRow key={signal.key ?? signal.label}>
                                        <TableCell className="w-[32%] min-w-[220px] font-bold text-sm">{signal.label}</TableCell>
                                        <TableCell className={cn('whitespace-normal text-sm', signal.mono && 'break-all text-xs font-mono')}>
                                          <div className="flex flex-wrap items-center gap-2">
                                            <Badge
                                              variant={
                                                signal.status === 'ready'
                                                  ? 'success'
                                                  : signal.status === 'partial'
                                                    ? 'warning'
                                                    : 'secondary'
                                              }
                                            >
                                              {signal.status === 'ready'
                                                ? 'Ready'
                                                : signal.status === 'partial'
                                                  ? 'Partial'
                                                  : 'Missing'}
                                            </Badge>
                                            <span>{signal.value}</span>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {blockers.length > 0 && (
                                      <TableRow>
                                        <TableCell className="font-bold text-sm">SIM Swap Blockers</TableCell>
                                        <TableCell className="whitespace-normal text-sm">
                                          <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
                                            {blockers.map((blocker) => (
                                              <li key={blocker}>{formatCustomerCareText(blocker)}</li>
                                            ))}
                                          </ul>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>

                            <div className="rounded-md border p-3">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                  <p className="text-sm font-semibold">Submit SIM swap</p>
                                  <p className="mt-1 text-sm text-muted-foreground">
                                    Sends the replacement ICCID to the Nova MTN SOA flow. The local account updates after MTN confirms the swap.
                                  </p>
                                </div>
                                <Badge variant={simSwapReadyForSubmit ? 'success' : 'secondary'}>
                                  {simSwapSummaryLabel}
                                </Badge>
                              </div>

                              {identityAssurance?.simSwap?.disabledReason && !currentSimSwap && (
                                <Alert variant="destructive" className="mt-3">
                                  <AlertDescription>
                                    {identityAssurance.simSwap.disabledReason}
                                  </AlertDescription>
                                </Alert>
                              )}

                              {currentSimSwap && (
                                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="space-y-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold">SIM swap awaiting MTN confirmation</span>
                                        <Badge variant="warning">
                                          {formatStatusLabel(currentSimSwap.simSwapStatus)}
                                        </Badge>
                                      </div>
                                      <p>Do not submit another SIM swap while this request is pending.</p>
                                      <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
                                        <dt className="font-medium">Replacement ICCID</dt>
                                        <dd className="font-mono">{currentSimSwap.newSimIccid || 'Not recorded'}</dd>
                                        <dt className="font-medium">Transaction</dt>
                                        <dd className="break-all font-mono">{currentSimSwap.transactionId}</dd>
                                        <dt className="font-medium">Submitted</dt>
                                        <dd>{formatDate(currentSimSwap.requestedAt || currentSimSwap.updatedAt)}</dd>
                                      </dl>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      disabled={loadingIdentityAssurance || !account?.i_customer}
                                      onClick={() =>
                                        account?.i_customer &&
                                        void Promise.all([
                                          fetchIdentityAssurance(account.i_customer, account.mvno, account.account_uuid),
                                          fetchSimHistory(
                                            account.i_customer,
                                            account.mvno,
                                            account.account_uuid,
                                          ),
                                        ])
                                      }
                                    >
                                      {loadingIdentityAssurance ? (
                                        <IconLoader2 className="size-4 animate-spin" />
                                      ) : (
                                        <IconRefresh className="size-4" />
                                      )}
                                      Refresh status
                                    </Button>
                                  </div>
                                </div>
                              )}

                              <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_auto] md:items-end">
                                <div className="space-y-1.5">
                                  <Label htmlFor="sim-swap-new-iccid">Replacement ICCID</Label>
                                  <div className="flex gap-2">
                                    <Input
                                      id="sim-swap-new-iccid"
                                      value={simSwapNewIccid}
                                      onChange={(event) => setSimSwapNewIccid(event.target.value)}
                                      placeholder="8927000000000000000"
                                      inputMode="numeric"
                                      disabled={!simSwapInputEnabled || submittingSimSwap}
                                    />
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="shrink-0"
                                      onClick={recheckReplacementSim}
                                      disabled={
                                        !simSwapInputEnabled ||
                                        submittingSimSwap ||
                                        checkingReplacementSim ||
                                        !/^\d{18,22}$/.test(simSwapNewIccid.trim().replace(/\s+/g, ''))
                                      }
                                      aria-label="Recheck replacement SIM"
                                    >
                                      {checkingReplacementSim ? (
                                        <IconLoader2 className="size-4 animate-spin" />
                                      ) : (
                                        <IconRefresh className="size-4" />
                                      )}
                                      <span className="hidden sm:inline">Recheck SIM</span>
                                    </Button>
                                  </div>
                                  {!simSwapInputEnabled && (
                                    <p className="text-xs text-muted-foreground">
                                      Complete the current identity verification before entering a replacement SIM.
                                    </p>
                                  )}
                                  {checkingReplacementSim && (
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <IconLoader2 className="size-3.5 animate-spin" />
                                      Checking Nova inventory, DMA, and RICA state…
                                    </div>
                                  )}
                                  {!checkingReplacementSim &&
                                    replacementSimEligibility &&
                                    replacementSimEligibilityIsCurrent && (
                                    <div className={replacementSimEligibility.eligible ? 'text-xs text-emerald-700' : 'text-xs text-destructive'}>
                                      {replacementSimEligibility.eligible
                                        ? `Eligible: ${replacementSimEligibility.simStatus}, DMA ${replacementSimEligibility.dmaStatus}${replacementSimEligibility.dmaMsisdn ? ` (${replacementSimEligibility.dmaMsisdn})` : ''}, not RICA'd.`
                                        : replacementSimEligibility.blockers.map(formatReplacementSimBlocker).join(' ')}
                                    </div>
                                  )}
                                  {!checkingReplacementSim &&
                                    replacementSimEligibility &&
                                    !replacementSimEligibilityIsCurrent && (
                                      <p className="text-xs text-muted-foreground">
                                        The replacement SIM result is for a previous ICCID. Recheck this SIM before submitting.
                                      </p>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                  <Label htmlFor="sim-swap-reason">Reason</Label>
                                  <Select
                                    value={simSwapReason}
                                    onValueChange={(value) =>
                                      value && setSimSwapReason(value)
                                    }
                                    disabled={!simSwapInputEnabled || submittingSimSwap}
                                  >
                                    <SelectTrigger id="sim-swap-reason">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {SIM_SWAP_REASONS.map((reason) => (
                                        <SelectItem key={reason.value} value={reason.value}>
                                          {reason.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <Button
                                  type="button"
                                  onClick={submitSimSwap}
                                  disabled={
                                    !account?.i_customer ||
                                    !simSwapReadyForSubmit ||
                                    submittingSimSwap ||
                                    checkingReplacementSim ||
                                    !replacementSimEligibility?.eligible ||
                                    !replacementSimEligibilityIsCurrent
                                  }
                                >
                                  {submittingSimSwap ? (
                                    <IconLoader2 className="size-4 animate-spin" />
                                  ) : (
                                    <IconArrowsExchange className="size-4" />
                                  )}
                                  Submit swap
                                </Button>
                              </div>

                              {simSwapExecutionResult && (
                                <Alert
                                  variant={simSwapExecutionResult.success ? 'default' : 'destructive'}
                                  className="mt-3"
                                >
                                  <AlertDescription>{simSwapExecutionResult.message}</AlertDescription>
                                </Alert>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </MainCard>

                  <MainCard className="xl:col-span-2">
                    <div className="p-4">
                      <h4 className="text-base font-semibold">SIM Card History</h4>
                    </div>
                    {simHistory?.history?.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Event</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>ICCID</TableHead>
                            <TableHead>Performed By</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {simHistory.history.map((event) => (
                            <TableRow key={`${event.event_type}-${event.id}`}>
                              <TableCell className="text-xs">{formatDate(event.created_at || undefined)}</TableCell>
                              <TableCell>
                                <p className="text-sm font-medium">{formatStatusLabel(event.event_type)}</p>
                                {event.description && <p className="text-xs text-muted-foreground">{event.description}</p>}
                              </TableCell>
                              <TableCell>
                                {event.status ? <Badge variant={statusBadgeVariant(event.status) as any}>{formatStatusLabel(event.status)}</Badge> : '-'}
                              </TableCell>
                              <TableCell className="break-all font-mono text-xs">{event.iccid || '-'}</TableCell>
                              <TableCell>{event.performed_by || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="p-8 text-center text-muted-foreground">No Nova SIM history events found.</div>
                    )}
                  </MainCard>
                </div>
              </div>
            </TabsContent>

            {/* Usage & XDR Tab (BigQuery BILL events) */}
            <TabsContent value="8">
              <div className="px-6 pb-6">
                <CustomerEdrUsagePanel
                  key={`${account?.account_uuid ?? account?.i_customer ?? 'none'}`}
                  iCustomer={account?.i_customer}
                  accountUuid={account?.account_uuid}
                  mvno={account?.mvno}
                  balancePlans={novaBalancePlans}
                  enabled={activeTab === 8}
                />
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="7">
              <div className="px-6 pb-6">
                <div className="mb-6">
                  <div>
                    <h3 className="text-lg font-semibold">Messages</h3>
                    <p className="text-sm text-muted-foreground">
                      Review SMS delivery history for the customer MSISDN.
                    </p>
                  </div>
                </div>

                <SmsPortalBlistPanel
                  messages={customerMessages?.messages ?? []}
                  msisdn={account?.msisdn || account?.msisdn_dma}
                  mvno={account?.mvno || account?.tenant?.mvno}
                  canSubmit={canPerformCustomerCareActions}
                />

                <MainCard>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
                    <div>
                      <h4 className="text-base font-semibold">Delivery History</h4>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Recent Nova communication delivery records for this account. Details includes purpose and SMS Portal ref.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!account?.i_customer || loadingCustomerMessages}
                      onClick={() => account?.i_customer && void fetchCustomerMessages(account.i_customer, account.mvno, account.account_uuid)}
                    >
                      {loadingCustomerMessages ? (
                        <IconLoader2 className="size-4 animate-spin" />
                      ) : (
                        <IconRefresh className="size-4" />
                      )}
                      Refresh
                    </Button>
                  </div>
                  <div className="p-4">
                    {customerMessagesError && (
                      <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{customerMessagesError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="mb-4 flex flex-wrap gap-2">
                      <Badge variant="outline">Total {customerMessages?.summary?.totalMessages ?? customerMessages?.messages?.length ?? 0}</Badge>
                      <Badge variant="info">Submitted {customerMessages?.summary?.submittedMessages ?? 0}</Badge>
                      <Badge variant="destructive">Failed {customerMessages?.summary?.failedMessages ?? 0}</Badge>
                    </div>
                    {!loadingCustomerMessages && (customerMessages?.messages?.length ?? 0) > 0 && (
                      <div className="mb-4 space-y-3">
                        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                          <div className="flex flex-1 flex-wrap items-center gap-2">
                            <div className="relative w-full sm:w-64">
                              <IconSearch className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
                              <Input
                                value={messageHistorySearch}
                                onChange={(event) => setMessageHistorySearch(event.target.value)}
                                placeholder="Search message, purpose, status, recipient, provider ref..."
                                className="h-8 pl-8"
                                aria-label="Search message delivery history"
                              />
                            </div>
                            <Select
                              value={messageHistoryStatusFilter}
                              onValueChange={(value) =>
                                setMessageHistoryStatusFilter(value ?? 'ALL')
                              }
                            >
                              <SelectTrigger className="h-8 w-[140px]">
                                <SelectValue placeholder="All statuses" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ALL">All statuses</SelectItem>
                                {messageHistoryStatusOptions.map((status) => (
                                  <SelectItem key={status} value={status}>
                                    {formatStatusLabel(status)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Popover>
                              <PopoverTrigger
                                type="button"
                                className="inline-flex h-8 w-[240px] items-center justify-start gap-2 rounded-md border border-input bg-background px-3 text-sm font-normal hover:bg-accent hover:text-accent-foreground"
                              >
                                <IconCalendar className="size-4 shrink-0" />
                                <span className="truncate">
                                  {formatMessageHistoryDateRangeLabel(
                                    messageHistoryFromDate,
                                    messageHistoryToDate,
                                  )}
                                </span>
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-auto p-3">
                                <div className="space-y-3">
                                  <p className="text-sm font-medium">Date range</p>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="date"
                                      value={messageHistoryFromDate}
                                      onChange={(event) => setMessageHistoryFromDate(event.target.value)}
                                      className="h-8 w-[140px]"
                                      aria-label="Filter messages from date"
                                    />
                                    <span className="text-xs text-muted-foreground">to</span>
                                    <Input
                                      type="date"
                                      value={messageHistoryToDate}
                                      onChange={(event) => setMessageHistoryToDate(event.target.value)}
                                      className="h-8 w-[140px]"
                                      aria-label="Filter messages to date"
                                    />
                                  </div>
                                  {hasInvalidMessageHistoryDateRange && (
                                    <p className="text-xs text-destructive">
                                      Start date must be on or before end date.
                                    </p>
                                  )}
                                  {(messageHistoryFromDate || messageHistoryToDate) && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2"
                                      onClick={() => {
                                        setMessageHistoryFromDate('');
                                        setMessageHistoryToDate('');
                                      }}
                                    >
                                      Clear dates
                                    </Button>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                            {hasMessageHistoryFilters && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8"
                                onClick={clearMessageHistoryFilters}
                              >
                                Clear filters
                              </Button>
                            )}
                          </div>
                          <p className="shrink-0 text-xs text-muted-foreground">
                            Showing {filteredCustomerMessages.length} of {customerMessages?.messages?.length ?? 0}
                          </p>
                        </div>
                      </div>
                    )}
                    {loadingCustomerMessages ? (
                      <div className="flex justify-center py-8">
                        <IconLoader2 className="h-8 w-8 animate-spin" />
                      </div>
                    ) : customerMessages?.messages?.length ? (
                      filteredCustomerMessages.length ? (
                      <Table className="w-full table-fixed">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[74px] px-2">Date</TableHead>
                            <TableHead className="w-[84px] px-2">Delivery</TableHead>
                            <TableHead className="w-[132px] px-2">Details</TableHead>
                            <TableHead className="px-2">Message</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredCustomerMessages.map((message) => {
                            const sentAt = formatMessageHistoryDate(
                              message.created_at || message.submitted_at || undefined,
                            );
                            const purposeLabel = formatMessagePurposeLabel(message.purpose);
                            const portalRef =
                              message.provider_ref || message.provider_message_id || '-';
                            const safeContent = customerMessageContentForDisplay(
                              message.content,
                              message.contentRedacted,
                            );
                            const displayContent = message.contentRedacted
                              ? safeContent
                              : formatMessageContentPreview(
                                  message.linkedVoucherStatus === 'REDEEMED'
                                    ? safeContent
                                    : maskVoucherCodeText(
                                        safeContent,
                                        message.purpose,
                                      ),
                                  message.purpose,
                                );

                            return (
                              <TableRow key={message.id}>
                                <TableCell className="px-2 align-top">
                                  <p className="text-[11px] leading-4 whitespace-nowrap">{sentAt.date}</p>
                                  {sentAt.time && (
                                    <p className="text-[10px] leading-4 text-muted-foreground whitespace-nowrap">
                                      {sentAt.time}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="px-2 align-top">
                                  <div className="flex flex-col items-start gap-1">
                                    <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                      {message.channel}
                                    </Badge>
                                    <Badge
                                      variant={statusBadgeVariant(message.status) as any}
                                      className="px-1.5 py-0 text-[10px]"
                                    >
                                      {message.status}
                                    </Badge>
                                  </div>
                                  {message.failure_reason && (
                                    <p
                                      className="mt-1 text-[10px] leading-4 text-destructive line-clamp-2"
                                      title={message.failure_reason}
                                    >
                                      {message.failure_reason}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="min-w-0 px-2 align-top">
                                  <p
                                    className="text-[11px] leading-4 font-medium truncate"
                                    title={message.purpose || undefined}
                                  >
                                    {purposeLabel}
                                  </p>
                                  <p
                                    className="mt-0.5 font-mono text-[10px] leading-4 text-muted-foreground truncate"
                                    title={portalRef}
                                  >
                                    Ref {portalRef}
                                  </p>
                                </TableCell>
                                <TableCell className="min-w-0 px-2 align-top text-xs leading-5 break-words">
                                  {displayContent || '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      ) : (
                        <div className="p-8 text-center text-muted-foreground">
                          No messages match your search or filters.
                        </div>
                      )
                    ) : (
                      <div className="p-8 text-center text-muted-foreground">
                        No Nova communication delivery records found for this customer.
                      </div>
                    )}
                  </div>
                </MainCard>
              </div>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="6">
            {account?.i_customer && (
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2">Customer History</h3>
                {loadingHistory && (
                  <div className="flex justify-center py-8">
                    <IconLoader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}
                {historyError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{historyError}</AlertDescription>
                  </Alert>
                )}
                {!loadingHistory && !historyError && customerHistory.length === 0 && (
                  <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
                    <AlertDescription>No history found for this customer.</AlertDescription>
                  </Alert>
                )}
                {!loadingHistory && !historyError && customerHistory.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-bold">Date</TableHead>
                        <TableHead className="font-bold">Event Type</TableHead>
                        <TableHead className="font-bold">Category</TableHead>
                        <TableHead className="font-bold">Description</TableHead>
                        <TableHead className="font-bold">Field</TableHead>
                        <TableHead className="font-bold">Old Value</TableHead>
                        <TableHead className="font-bold">New Value</TableHead>
                        <TableHead className="font-bold">Reason</TableHead>
                        <TableHead className="font-bold">Performed By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customerHistory.map((event: any, index: number) => {
                        const oldValue = formatHistoryValue(event.old_value);
                        const newValue = formatHistoryValue(event.new_value);
                        return (
                        <TableRow key={event.id || index}>
                          <TableCell>
                            {event.created_at 
                              ? new Date(event.created_at).toLocaleString('en-US', {
                                  month: '2-digit', day: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit', hour12: false
                                })
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="info">{event.event_type || '-'}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{event.event_category || '-'}</Badge>
                          </TableCell>
                          <TableCell>{event.description || '-'}</TableCell>
                          <TableCell>{event.field_name || '-'}</TableCell>
                          <TableCell>
                            <p className="text-sm max-w-[200px] truncate" title={oldValue}>
                              {oldValue}
                            </p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm max-w-[200px] truncate" title={newValue}>
                              {newValue}
                            </p>
                          </TableCell>
                          <TableCell>{event.reason || '-'}</TableCell>
                          <TableCell>{event.performed_by || '-'}</TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
            </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedImageTitle}</DialogTitle>
          </DialogHeader>
          <img
            src={selectedImageUrl}
            alt={selectedImageTitle}
            className="w-full h-auto object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
              const errorBox = document.createElement('div');
              errorBox.textContent = 'Failed to load image';
              errorBox.style.padding = '20px';
              errorBox.style.textAlign = 'center';
              errorBox.style.color = '#666';
              (e.currentTarget as HTMLElement).parentElement?.appendChild(errorBox);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Map Dialog */}
      <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Location on Map</DialogTitle>
          </DialogHeader>
          {mapLocation && (
            <div className="w-full h-[500px]">
              <iframe
                width="100%"
                height="100%"
                style={{ border: 0 }}
                src={`https://www.google.com/maps?q=${mapLocation.lat},${mapLocation.lng}&output=embed&zoom=15`}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Login History Map Dialog */}
      <Dialog open={loginHistoryDialogOpen} onOpenChange={setLoginHistoryDialogOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Login History Locations</DialogTitle>
          </DialogHeader>
          {loginHistory.length > 0 ? (
            <div>
              {loginHistory.length > 0 && loginHistory[0]?.lat && loginHistory[0]?.lng && (
                <div className="mb-4">
                  <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200 mb-4">
                    <AlertDescription>
                      <p className="text-sm font-bold mb-1">
                        Last Login: {loginHistory[0].created_at ? new Date(loginHistory[0].created_at).toLocaleString('en-US', {
                          month: '2-digit', day: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', hour12: false
                        }) : 'Unknown'}
                      </p>
                      <p className="text-sm">
                        Location: {parseFloat(loginHistory[0].lat).toFixed(6)}, {parseFloat(loginHistory[0].lng).toFixed(6)}
                      </p>
                    </AlertDescription>
                  </Alert>
                  
                  {loginHistoryBinnedCounts && (
                    <div className="flex gap-2 flex-wrap mb-4">
                      <Badge variant="success" className="text-xs h-8 font-bold">≤500m: {loginHistoryBinnedCounts.within500m}</Badge>
                      <Badge variant="success" className="text-xs h-8 font-bold">500m-1km: {loginHistoryBinnedCounts.within1km}</Badge>
                      <Badge variant="warning" className="text-xs h-8 font-bold">1km-1.5km: {loginHistoryBinnedCounts.within1_5km}</Badge>
                      <Badge variant="warning" className="text-xs h-8 font-bold">1.5km-2km: {loginHistoryBinnedCounts.within2km}</Badge>
                      <Badge variant="warning" className="text-xs h-8 font-bold">2km-2.5km: {loginHistoryBinnedCounts.within2_5km}</Badge>
                      <Badge variant="destructive" className="text-xs h-8 font-bold">2.5km-3km: {loginHistoryBinnedCounts.within3km}</Badge>
                      <Badge variant="destructive" className="text-xs h-8 font-bold">&gt;3km: {loginHistoryBinnedCounts.beyond3km}</Badge>
                    </div>
                  )}
                </div>
              )}
              
              {loginHistory.length > 0 && loginHistory[0]?.lat && loginHistory[0]?.lng && (
                <div className="w-full mb-4">
                  <span className="text-xs text-muted-foreground block mb-2">
                    {loginHistory.length} login{loginHistory.length !== 1 ? 's' : ''} shown on map
                  </span>
                  <LoginHistoryHeatmap 
                    loginHistory={loginHistory} 
                    lastLogin={loginHistory[0]} 
                    isOpen={loginHistoryDialogOpen}
                  />
                </div>
              )}
              
              <span className="text-xs text-muted-foreground block mb-2">
                Showing {loginHistory.length} login location{loginHistory.length !== 1 ? 's' : ''} with coordinates
              </span>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-bold">Date</TableHead>
                    <TableHead className="font-bold">Location</TableHead>
                    <TableHead className="font-bold">Device</TableHead>
                    <TableHead className="font-bold">Distance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginHistory.slice(0, 20).map((login: any) => (
                    <TableRow key={login.id}>
                      <TableCell>
                        {login.created_at ? new Date(login.created_at).toLocaleString('en-US', {
                          month: '2-digit', day: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', hour12: false
                        }) : '-'}
                      </TableCell>
                      <TableCell>
                        {(login.lat || login.location_lat) && (login.lng || login.location_lng) ? (
                          `${parseFloat(login.lat || login.location_lat).toFixed(6)}, ${parseFloat(login.lng || login.location_lng).toFixed(6)}`
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {login.device_type || '-'} {login.app_version || login.version_number ? `(${login.app_version || login.version_number})` : ''}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          if (!loginHistory[0]?.lat || !loginHistory[0]?.lng) return '-';
                          const lastLat = parseFloat(loginHistory[0].lat);
                          const lastLng = parseFloat(loginHistory[0].lng);
                          const loginLat = parseFloat(login.lat || login.location_lat);
                          const loginLng = parseFloat(login.lng || login.location_lng);
                          if (isNaN(loginLat) || isNaN(loginLng) || isNaN(lastLat) || isNaN(lastLng)) return '-';
                          const R = 6371;
                          const dLat = (loginLat - lastLat) * Math.PI / 180;
                          const dLng = (loginLng - lastLng) * Math.PI / 180;
                          const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                            Math.cos(lastLat * Math.PI / 180) * Math.cos(loginLat * Math.PI / 180) *
                            Math.sin(dLng / 2) * Math.sin(dLng / 2);
                          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                          const distance = R * c;
                          
                          const label = distance <= 0.5 ? '≤500m' : distance <= 1 ? '500m-1km' : distance <= 1.5 ? '1km-1.5km' : distance <= 2 ? '1.5km-2km' : distance <= 2.5 ? '2km-2.5km' : distance <= 3 ? '2.5km-3km' : '>3km';
                          const badgeVariant = distance <= 3 ? 'success' : distance <= 6 ? 'warning' : 'destructive';
                          return <Badge variant={badgeVariant} className="text-[0.7rem] h-5">{label}</Badge>;
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {loginHistory.length > 20 && (
                <span className="text-xs text-muted-foreground block mt-2">
                  Showing first 20 of {loginHistory.length} logins
                </span>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No login history with location data found</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={rewardBlockDialogOpen}
        onOpenChange={(open) => { if (!updatingRewardBlock) setRewardBlockDialogOpen(open); }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={!updatingRewardBlock}>
          <DialogHeader>
            <DialogTitle>Suspend Reward Use</DialogTitle>
            <DialogDescription>
              This blocks reward usage for this customer account and records the reason in reward history.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="rewardBlockReason" className="mb-1.5">Reason</Label>
            <Textarea
              id="rewardBlockReason"
              placeholder="Enter why reward use is being suspended..."
              rows={4}
              value={rewardBlockReason}
              onChange={(e) => setRewardBlockReason(e.target.value)}
              disabled={updatingRewardBlock}
              required
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRewardBlockDialogOpen(false);
                setRewardBlockReason('');
              }}
              disabled={updatingRewardBlock}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void updateRewardUsageBlock(true)}
              disabled={updatingRewardBlock || !rewardBlockReason.trim()}
            >
              {updatingRewardBlock ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : <IconBan className="size-4 mr-2" />}
              {updatingRewardBlock ? 'Suspending...' : 'Suspend Reward Use'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rewardUnblockDialogOpen}
        onOpenChange={(open) => { if (!updatingRewardBlock) setRewardUnblockDialogOpen(open); }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={!updatingRewardBlock}>
          <DialogHeader>
            <DialogTitle>Unsuspend Reward Use</DialogTitle>
            <DialogDescription>
              This allows reward usage again for this customer account and records the reason in reward history.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="rewardUnblockReason" className="mb-1.5">Reason</Label>
            <Textarea
              id="rewardUnblockReason"
              placeholder="Enter why reward use is being unsuspended..."
              rows={4}
              value={rewardBlockReason}
              onChange={(e) => setRewardBlockReason(e.target.value)}
              disabled={updatingRewardBlock}
              required
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRewardUnblockDialogOpen(false);
                setRewardBlockReason('');
              }}
              disabled={updatingRewardBlock}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => void updateRewardUsageBlock(false)}
              disabled={updatingRewardBlock || !rewardBlockReason.trim()}
            >
              {updatingRewardBlock ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : <IconLockOpen className="size-4 mr-2" />}
              {updatingRewardBlock ? 'Unsuspending...' : 'Unsuspend Reward Use'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SPAR Mobile Account Creation Modal */}
      <Dialog 
        open={sparMobileCreateModalOpen} 
        onOpenChange={(open) => { if (!creatingSparMobileAccount) setSparMobileCreateModalOpen(open); }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={!creatingSparMobileAccount}>
          <DialogHeader>
            <DialogTitle>Create SPAR Mobile Account</DialogTitle>
          </DialogHeader>
          {creatingSparMobileAccount ? (
            <div className="flex flex-col items-center py-8">
              <IconLoader2 className="h-12 w-12 animate-spin mb-4" />
              <p className="text-base text-muted-foreground">Creating SPAR Mobile account...</p>
              <p className="text-sm text-muted-foreground mt-2">Please wait while we process your request.</p>
            </div>
          ) : sparMobileCreateResult ? (
            <div className="py-4">
              {sparMobileCreateResult.success ? (
                <Alert className="border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200 whitespace-pre-line">
                  <AlertDescription>{sparMobileCreateResult.message}</AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive" className="whitespace-pre-line">
                  <AlertDescription>{sparMobileCreateResult.message}</AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <div className="py-4">
              <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200 mb-4">
                <AlertDescription>
                  This will create a SPAR Mobile account for this customer using their details from the database.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                Customer: {account?.first_name} {account?.last_name}
                <br />
                MSISDN: {account?.msisdn || account?.msisdn_dma || accountQuery}
              </p>
            </div>
          )}
          {!creatingSparMobileAccount && (
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseSparMobileCreateModal}>
                {sparMobileCreateResult ? 'Close' : 'Cancel'}
              </Button>
              {!sparMobileCreateResult && (
                <Button onClick={handleCreateSparMobileAccount}>
                  Create Account
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Voucher Details */}
      <Dialog
        open={Boolean(selectedVoucher)}
        onOpenChange={(open) => {
          if (!open) setSelectedVoucher(null);
        }}
      >
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto sm:max-w-4xl">
          {selectedVoucher && (
            <>
              <DialogHeader>
                <DialogTitle>Voucher Details</DialogTitle>
                <DialogDescription>
                  {selectedVoucher.product_name || selectedVoucher.value_type || 'Voucher'} · {selectedVoucher.voucher_id || selectedVoucher.id}
                </DialogDescription>
              </DialogHeader>

              {(() => {
                const careStatus = getVoucherCareStatus(selectedVoucher);
                const voucherCode = getVoucherCodeDisplay(selectedVoucher);
                return (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <StatCard
                        title="Value"
                        value={selectedVoucher.amount !== null ? formatRand(selectedVoucher.amount) : '-'}
                      />
                      <StatCard title="Status" value={formatStatusLabel(careStatus)} />
                      <StatCard
                        title="Role"
                        value={formatStatusLabel(selectedVoucher.customer_voucher_role)}
                      />
                      <StatCard
                        title="Date"
                        value={
                          selectedVoucher.redeemed_at ||
                          selectedVoucher.confirmed_at ||
                          selectedVoucher.requested_at ||
                          selectedVoucher.created_at
                            ? formatDate(
                                selectedVoucher.redeemed_at ||
                                  selectedVoucher.confirmed_at ||
                                  selectedVoucher.requested_at ||
                                  selectedVoucher.created_at ||
                                  undefined,
                              )
                            : '-'
                        }
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div className="rounded-md border p-4">
                        <h4 className="mb-3 text-base font-semibold">Voucher</h4>
                        <dl>
                          <DetailRow label="Voucher Row ID" value={selectedVoucher.id} mono copyValue={selectedVoucher.id} />
                          <DetailRow label="Voucher ID" value={selectedVoucher.voucher_id} mono copyValue={selectedVoucher.voucher_id} />
                          <DetailRow label="Product" value={selectedVoucher.product_name || selectedVoucher.value_type} />
                          <DetailRow label="Source" value={formatStatusLabel(selectedVoucher.source)} />
                          <DetailRow label="Quantity" value={selectedVoucher.quantity} />
                          <DetailRow
                            label={voucherCode.label}
                            value={voucherCode.display || voucherCode.fallback}
                            mono={Boolean(voucherCode.display)}
                            copyValue={voucherCode.isFull ? voucherCode.display : undefined}
                          />
                        </dl>
                      </div>

                      <div className="rounded-md border p-4">
                        <h4 className="mb-3 text-base font-semibold">Status</h4>
                        <dl>
                          <DetailRow
                            label="Care Status"
                            value={
                              <Badge variant={statusBadgeVariant(careStatus) as any}>
                                {formatStatusLabel(careStatus)}
                              </Badge>
                            }
                          />
                          <DetailRow label="Source Status" value={formatStatusLabel(selectedVoucher.status)} />
                          <DetailRow label="State" value={formatStatusLabel(selectedVoucher.state)} />
                          <DetailRow label="Matched Status" value={formatStatusLabel(selectedVoucher.matched_status)} />
                          <DetailRow label="Reversal Reason" value={selectedVoucher.reversal_reason} />
                        </dl>
                      </div>

                      <div className="rounded-md border p-4">
                        <h4 className="mb-3 text-base font-semibold">References</h4>
                        <dl>
                          <DetailRow label="Request ID" value={selectedVoucher.request_id} mono copyValue={selectedVoucher.request_id} />
                          <DetailRow label="Advice ID" value={selectedVoucher.advice_id} mono copyValue={selectedVoucher.advice_id} />
                          <DetailRow label="RRN" value={selectedVoucher.rrn} mono copyValue={selectedVoucher.rrn} />
                          <DetailRow label="STAN" value={selectedVoucher.stan} mono copyValue={selectedVoucher.stan} />
                          <DetailRow label="Originator Ref" value={selectedVoucher.originator_reference} mono copyValue={selectedVoucher.originator_reference} />
                          <DetailRow label="Service Ref" value={selectedVoucher.service_reference} mono copyValue={selectedVoucher.service_reference} />
                        </dl>
                      </div>

                      <div className="rounded-md border p-4">
                        <h4 className="mb-3 text-base font-semibold">Transactions</h4>
                        <dl>
                          <DetailRow label="Sale Transaction" value={selectedVoucher.sale_transaction_id} mono copyValue={selectedVoucher.sale_transaction_id} />
                          <DetailRow label="Sale Status" value={formatStatusLabel(selectedVoucher.sale_transaction_status)} />
                          <DetailRow label="Sale Payment Ref" value={selectedVoucher.sale_payment_reference} mono copyValue={selectedVoucher.sale_payment_reference} />
                          <DetailRow label="Redemption Transaction" value={selectedVoucher.redemption_transaction_id} mono copyValue={selectedVoucher.redemption_transaction_id} />
                          <DetailRow label="Redemption Status" value={formatStatusLabel(selectedVoucher.redemption_transaction_status)} />
                          <DetailRow label="Redemption Payment Ref" value={selectedVoucher.redemption_payment_reference} mono copyValue={selectedVoucher.redemption_payment_reference} />
                        </dl>
                      </div>

                      <div className="rounded-md border p-4">
                        <h4 className="mb-3 text-base font-semibold">Customer Links</h4>
                        <dl>
                          <DetailRow label="Redeemed Account" value={selectedVoucher.redeemed_by_account_id} mono copyValue={selectedVoucher.redeemed_by_account_id} />
                          <DetailRow label="Redeemed Customer" value={selectedVoucher.redeemed_by_i_customer} copyValue={selectedVoucher.redeemed_by_i_customer} />
                          <DetailRow label="Redeemed MSISDN" value={selectedVoucher.redeemed_by_msisdn} mono copyValue={selectedVoucher.redeemed_by_msisdn} />
                          <DetailRow label="Selling Partner" value={selectedVoucher.selling_partner_name} />
                          <DetailRow label="Reseller Partner" value={selectedVoucher.reseller_partner_name} />
                        </dl>
                      </div>

                      <div className="rounded-md border p-4">
                        <h4 className="mb-3 text-base font-semibold">Dates</h4>
                        <dl>
                          <DetailRow label="Requested" value={selectedVoucher.requested_at ? formatDate(selectedVoucher.requested_at) : null} />
                          <DetailRow label="Confirmed" value={selectedVoucher.confirmed_at ? formatDate(selectedVoucher.confirmed_at) : null} />
                          <DetailRow label="Redeemed" value={selectedVoucher.redeemed_at ? formatDate(selectedVoucher.redeemed_at) : null} />
                          <DetailRow label="Expired" value={selectedVoucher.expired_at ? formatDate(selectedVoucher.expired_at) : null} />
                          <DetailRow label="Reversed" value={selectedVoucher.reversed_at ? formatDate(selectedVoucher.reversed_at) : null} />
                          <DetailRow label="Created" value={selectedVoucher.created_at ? formatDate(selectedVoucher.created_at) : null} />
                          <DetailRow label="Updated" value={selectedVoucher.updated_at ? formatDate(selectedVoucher.updated_at) : null} />
                        </dl>
                      </div>
                    </div>

                    {canResendVoucherSms(selectedVoucher) && (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          disabled={!account?.i_customer || !canPerformCustomerCareActions || resendingVoucherId === String(selectedVoucher.id)}
                          onClick={() => {
                            const voucher = selectedVoucher;
                            setSelectedVoucher(null);
                            openVoucherResendConfirm(voucher);
                          }}
                        >
                          {resendingVoucherId === String(selectedVoucher.id) ? (
                            <IconLoader2 className="size-4 animate-spin" />
                          ) : (
                            <IconMessage className="size-4" />
                          )}
                          Resend SMS
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Transaction Details */}
      <Dialog
        open={Boolean(selectedTransaction)}
        onOpenChange={(open) => {
          if (!open) setSelectedTransaction(null);
        }}
      >
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-6xl overflow-y-auto sm:max-w-6xl">
          {selectedTransaction && (
            <>
              <DialogHeader>
                <DialogTitle>Transaction Details</DialogTitle>
                <DialogDescription>
                  {selectedTransaction.transaction_type_name || selectedTransaction.product_name || 'Transaction'} · {selectedTransaction.id}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                  <StatCard title="Amount" value={formatMoney(selectedTransaction.amount, selectedTransaction.currency || 'ZAR')} />
                  <StatCard title="Direction" value={formatStatusLabel(selectedTransaction.transaction_direction)} />
                  <StatCard title="Status" value={formatStatusLabel(selectedTransaction.status)} />
                  <StatCard title="Created" value={selectedTransaction.created_at ? formatDate(selectedTransaction.created_at) : '-'} />
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="rounded-md border p-4">
                    <h4 className="mb-3 text-base font-semibold">Core</h4>
                    <dl>
                      <DetailRow label="Transaction ID" value={selectedTransaction.id} mono copyValue={selectedTransaction.id} />
                      <DetailRow label="Legacy ID" value={selectedTransaction.legacy_transaction_id} copyValue={selectedTransaction.legacy_transaction_id} />
                      <DetailRow label="Legacy Source" value={selectedTransaction.legacy_transaction_source} />
                      <DetailRow label="Customer" value={selectedTransaction.i_customer} />
                      <DetailRow label="MSISDN" value={selectedTransaction.msisdn} copyValue={selectedTransaction.msisdn} />
                      <DetailRow
                        label="Status"
                        value={
                          selectedTransaction.status ? (
                            <Badge variant={statusBadgeVariant(selectedTransaction.status) as any}>
                              {formatStatusLabel(selectedTransaction.status)}
                            </Badge>
                          ) : null
                        }
                      />
                      <DetailRow label="Intent" value={formatStatusLabel(selectedTransaction.intent)} />
                      <DetailRow label="Channel" value={formatStatusLabel(selectedTransaction.request_channel)} />
                    </dl>
                  </div>

                  <div className="rounded-md border p-4">
                    <h4 className="mb-3 text-base font-semibold">Product & Payment</h4>
                    <dl>
                      <DetailRow label="Type" value={selectedTransaction.transaction_type_name} />
                      <DetailRow label="Type Category" value={formatStatusLabel(selectedTransaction.transaction_type_type)} />
                      <DetailRow label="Product" value={selectedTransaction.product_name} />
                      <DetailRow label="Product Code" value={selectedTransaction.product_code || selectedTransaction.product_type_code} mono />
                      <DetailRow label="Payment Category" value={formatStatusLabel(selectedTransaction.payment_category)} />
                      <DetailRow label="Payment Method" value={formatStatusLabel(selectedTransaction.payment_method)} />
                      <DetailRow label="Payment Status" value={formatStatusLabel(selectedTransaction.payment_status)} />
                      <DetailRow label="Payment Reference" value={selectedTransaction.payment_reference} mono copyValue={selectedTransaction.payment_reference} />
                      <DetailRow label="Payment Provider" value={selectedTransaction.payment_provider} />
                      <DetailRow label="Provider Reference" value={selectedTransaction.payment_provider_reference} mono copyValue={selectedTransaction.payment_provider_reference} />
                      <DetailRow label="Payment Completed" value={selectedTransaction.payment_completed_at ? formatDate(selectedTransaction.payment_completed_at) : null} />
                    </dl>
                  </div>

                  <div className="rounded-md border p-4">
                    <h4 className="mb-3 text-base font-semibold">Provisioning</h4>
                    <dl>
                      <DetailRow label="Status" value={formatStatusLabel(selectedTransaction.provisioning_status)} />
                      <DetailRow label="Reference" value={selectedTransaction.provisioning_reference} mono copyValue={selectedTransaction.provisioning_reference} />
                      <DetailRow label="Completed" value={selectedTransaction.provisioning_completed_at ? formatDate(selectedTransaction.provisioning_completed_at) : null} />
                      <DetailRow label="Totogi Reference" value={selectedTransaction.totogi_reference} mono copyValue={selectedTransaction.totogi_reference} />
                      <DetailRow label="External Reference" value={selectedTransaction.external_reference} mono copyValue={selectedTransaction.external_reference} />
                      <DetailRow label="Idempotency Key" value={selectedTransaction.idempotency_key} mono copyValue={selectedTransaction.idempotency_key} />
                    </dl>
                  </div>

                  <div className="rounded-md border p-4">
                    <h4 className="mb-3 text-base font-semibold">Resolution</h4>
                    <dl>
                      <DetailRow label="Failure Reason" value={selectedTransaction.failure_reason} />
                      <DetailRow label="Reversal Reason" value={selectedTransaction.reversal_reason} />
                      <DetailRow label="Reversed At" value={selectedTransaction.reversed_at ? formatDate(selectedTransaction.reversed_at) : null} />
                      <DetailRow label="Ops Notes" value={selectedTransaction.ops_notes} />
                      <DetailRow label="Updated" value={selectedTransaction.updated_at ? formatDate(selectedTransaction.updated_at) : null} />
                    </dl>
                  </div>
                </div>

                {(selectedTransaction.activating_partner_name ||
                  selectedTransaction.selling_partner_name ||
                  selectedTransaction.selling_agent_partner_name ||
                  selectedTransaction.linked_transaction_id) && (
                  <div className="rounded-md border p-4">
                    <h4 className="mb-3 text-base font-semibold">Related Parties</h4>
                    <dl>
                      <DetailRow label="Activating Partner" value={selectedTransaction.activating_partner_name} />
                      <DetailRow label="Selling Partner" value={selectedTransaction.selling_partner_name} />
                      <DetailRow label="Selling Agent" value={selectedTransaction.selling_agent_partner_name} />
                      <DetailRow label="Linked Transaction" value={selectedTransaction.linked_transaction_id} mono copyValue={selectedTransaction.linked_transaction_id} />
                      <DetailRow label="Linked Status" value={formatStatusLabel(selectedTransaction.linked_transaction_status)} />
                      <DetailRow
                        label="Linked Amount"
                        value={
                          selectedTransaction.linked_transaction_amount
                            ? formatMoney(selectedTransaction.linked_transaction_amount, selectedTransaction.currency || 'ZAR')
                            : null
                        }
                      />
                    </dl>
                  </div>
                )}

                {Array.isArray(selectedTransaction.linked_vouchers) && selectedTransaction.linked_vouchers.length > 0 && (
                  <div className="rounded-md border">
                    <div className="border-b p-4">
                      <h4 className="text-base font-semibold">Linked Vouchers</h4>
                    </div>
                    <Table className="w-full table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Voucher</TableHead>
                          <TableHead className="w-[132px]">Status</TableHead>
                          <TableHead className="w-[160px]">Redeemed By</TableHead>
                          <TableHead className="w-[100px] text-right">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedTransaction.linked_vouchers.map((voucher, index) => (
                          <TableRow key={`${voucher.id ?? index}`}>
                            <TableCell className="min-w-0 align-top">
                              <p className="text-sm font-medium">{voucher.product_name || voucher.voucher_id || voucher.id}</p>
                              <div className="mt-1 space-y-1">
                                {voucher.request_id ? (
                                  <div className="flex min-w-0 items-start gap-1 text-xs text-muted-foreground">
                                    <span className="min-w-0 flex-1 break-all font-mono">Request {voucher.request_id}</span>
                                    <CopyButton value={voucher.request_id} label="Voucher request ID" />
                                  </div>
                                ) : null}
                                {voucher.advice_id ? (
                                  <div className="flex min-w-0 items-start gap-1 text-xs text-muted-foreground">
                                    <span className="min-w-0 flex-1 break-all font-mono">Advice {voucher.advice_id}</span>
                                    <CopyButton value={voucher.advice_id} label="Voucher advice ID" />
                                  </div>
                                ) : null}
                                {!voucher.request_id && !voucher.advice_id ? (
                                  <p className="text-xs text-muted-foreground">-</p>
                                ) : null}
                              </div>
                              {voucher.code_available ? (
                                <p className="text-xs text-muted-foreground">Voucher code captured, hidden</p>
                              ) : null}
                            </TableCell>
                            <TableCell className="align-top">
                              <Badge
                                variant={statusBadgeVariant(voucher.effective_status || voucher.status) as any}
                                title={getVoucherStatusCopy(voucher.effective_status || voucher.status).description}
                              >
                                {getVoucherStatusCopy(voucher.effective_status || voucher.status).label}
                              </Badge>
                            </TableCell>
                            <TableCell className="align-top text-sm">
                              {voucher.redeemed_by_customer ? 'This customer' : voucher.redeemed_by_i_customer || '-'}
                            </TableCell>
                            <TableCell className="align-top text-right">
                              {voucher.amount ? formatMoney(voucher.amount, selectedTransaction.currency || 'ZAR') : voucher.quantity || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {Array.isArray(selectedTransaction.transaction_steps) && selectedTransaction.transaction_steps.length > 0 && (
                  <div className="rounded-md border">
                    <div className="border-b p-4">
                      <h4 className="text-base font-semibold">Processing Steps</h4>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Step</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>External Ref</TableHead>
                          <TableHead>Completed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedTransaction.transaction_steps.map((step, index) => (
                          <TableRow key={`${step.sequence ?? index}-${step.handler ?? 'step'}`}>
                            <TableCell>
                              <p className="text-sm font-medium">{step.handler || '-'}</p>
                              <p className="text-xs text-muted-foreground">Attempt {step.attempts ?? 0}</p>
                            </TableCell>
                            <TableCell>
                              <Badge variant={statusBadgeVariant(step.status) as any}>{formatStatusLabel(step.status)}</Badge>
                            </TableCell>
                            <TableCell className="max-w-[240px] break-all text-xs font-mono">
                              <div className="flex items-start gap-1">
                                <span className="min-w-0 flex-1 break-all">{step.external_reference || '-'}</span>
                                <CopyButton value={step.external_reference} label="Step external reference" />
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">
                              {step.completed_at ? formatDate(step.completed_at) : step.started_at ? formatDate(step.started_at) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {Array.isArray(selectedTransaction.transaction_events) && selectedTransaction.transaction_events.length > 0 && (
                  <div className="rounded-md border">
                    <div className="border-b p-4">
                      <h4 className="text-base font-semibold">Status Events</h4>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Change</TableHead>
                          <TableHead>Trigger</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedTransaction.transaction_events.map((event, index) => (
                          <TableRow key={`${event.created_at ?? index}-${event.to_status ?? 'event'}`}>
                            <TableCell className="text-xs">{event.created_at ? formatDate(event.created_at) : '-'}</TableCell>
                            <TableCell className="text-sm">
                              {formatStatusLabel(event.from_status) || 'New'} → {formatStatusLabel(event.to_status)}
                            </TableCell>
                            <TableCell className="text-sm">{formatStatusLabel(event.trigger_type)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend Customer Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onOpenChange={(open) => { if (!suspending) setSuspendDialogOpen(open); }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={!suspending}>
          <DialogHeader>
            <DialogTitle>Suspend Customer Account</DialogTitle>
          </DialogHeader>
          <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200 mb-4">
            <AlertDescription>
              This will suspend the customer account in Nova. This action will be logged in the customer history.
            </AlertDescription>
          </Alert>
          <div>
            <Label htmlFor="suspendReason" className="mb-1.5">Suspension Reason</Label>
            <Textarea
              id="suspendReason"
              placeholder="Enter the reason for suspending this customer..."
              rows={4}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              disabled={suspending}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">Please provide a clear reason for suspending this customer account.</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSuspendDialogOpen(false);
                setSuspendReason('');
              }}
              disabled={suspending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspendCustomer}
              disabled={suspending || !suspendReason.trim()}
            >
              {suspending ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : <IconBan className="size-4 mr-2" />}
              {suspending ? 'Suspending...' : 'Suspend Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsuspend Customer Dialog */}
      <Dialog
        open={unsuspendDialogOpen}
        onOpenChange={(open) => { if (!suspending) setUnsuspendDialogOpen(open); }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={!suspending}>
          <DialogHeader>
            <DialogTitle>Unsuspend Customer Account</DialogTitle>
          </DialogHeader>
          <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200 mb-4">
            <AlertDescription>
              This will unsuspend the customer account in Nova. This action will be logged in the customer history.
            </AlertDescription>
          </Alert>
          <div>
            <Label htmlFor="unsuspendReason" className="mb-1.5">Unsuspension Reason</Label>
            <Textarea
              id="unsuspendReason"
              placeholder="Enter the reason for unsuspending this customer..."
              rows={4}
              value={unsuspendReason}
              onChange={(e) => setUnsuspendReason(e.target.value)}
              disabled={suspending}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">Please provide a clear reason for unsuspending this customer account.</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUnsuspendDialogOpen(false);
                setUnsuspendReason('');
              }}
              disabled={suspending}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleUnsuspendCustomer}
              disabled={suspending || !unsuspendReason.trim()}
            >
              {suspending ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : <IconLockOpen className="size-4 mr-2" />}
              {suspending ? 'Unsuspending...' : 'Unsuspend Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset DMA failed count confirmation */}
      <Dialog open={resetDmaConfirmOpen} onOpenChange={(open) => { if (!resettingDma) setResetDmaConfirmOpen(open); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset DMA failed count</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Reset dma_failed_count to 0 for this customer? This will update the customers table.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDmaConfirmOpen(false)} disabled={resettingDma}>
              Cancel
            </Button>
            <Button
              onClick={handleResetDmaFailedCountConfirm}
              disabled={resettingDma}
            >
              {resettingDma ? <IconLoader2 className="h-5 w-5 animate-spin mr-2" /> : null}
              {resettingDma ? 'Resetting...' : 'Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voucher resend SMS confirmation */}
      <Dialog
        open={voucherResendConfirmOpen}
        onOpenChange={(open) => {
          if (!resendingVoucherId) {
            setVoucherResendConfirmOpen(open);
            if (!open) setPendingVoucherResend(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto sm:max-w-lg" showCloseButton={!resendingVoucherId}>
          <DialogHeader>
            <DialogTitle>Confirm voucher SMS</DialogTitle>
            <DialogDescription>
              Review the message before sending it to the customer. This action is recorded in communication history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Recipient</p>
              <p className="text-sm text-muted-foreground">{getCustomerSmsRecipient() || 'No MSISDN available'}</p>
            </div>
            {pendingVoucherResend && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Voucher</p>
                <p className="text-sm text-muted-foreground">
                  {pendingVoucherResend.product_name || pendingVoucherResend.value_type || 'Voucher'}
                  {pendingVoucherResend.voucher_id ? ` (${pendingVoucherResend.voucher_id})` : ''}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm font-medium">Message preview</p>
              <div className="rounded-md border bg-muted/40 p-3 text-sm whitespace-pre-wrap break-words">
                {pendingVoucherResend ? buildVoucherResendSmsPreview(pendingVoucherResend) : ''}
              </div>
              <p className="text-xs text-muted-foreground">
                The customer receives the full voucher code. The preview above shows the masked code for verification only.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setVoucherResendConfirmOpen(false);
                setPendingVoucherResend(null);
              }}
              disabled={Boolean(resendingVoucherId)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void confirmVoucherResendSms()}
              disabled={!pendingVoucherResend || !getCustomerSmsRecipient() || Boolean(resendingVoucherId)}
            >
              {resendingVoucherId ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconMessage className="size-4" />
              )}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIM swap identity check */}
      <Dialog
        open={simSwapPromptOpen}
        onOpenChange={(open) => {
          if (!sendingSimSwapPrompt) setSimSwapPromptOpen(open);
        }}
      >
        <DialogContent
          className="max-h-[90vh] max-w-lg overflow-y-auto sm:max-w-lg"
          showCloseButton={!sendingSimSwapPrompt}
        >
          <DialogHeader>
            <DialogTitle>Send SIM swap identity check</DialogTitle>
            <DialogDescription>
              {getSimSwapVerificationRequirement(currentSimSwapVerification?.flow)}{' '}
              Enter the number the customer can currently access. Choose WhatsApp
              or SMS for the verification link. The message will reference the
              customer account number.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Alert>
              <AlertTitle>{getSimSwapVerificationFlowLabel(currentSimSwapVerification?.flow)}</AlertTitle>
              <AlertDescription>
                The selected verification must pass before a replacement SIM can be checked or submitted.
                {currentSimSwapVerificationStatus === 'PENDING' && ' Sending again cancels the previous check. Ask the customer to use the newest link.'}
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label htmlFor="sim-swap-prompt-msisdn">Send verification link to</Label>
              <Input
                id="sim-swap-prompt-msisdn"
                value={simSwapPromptMsisdn}
                onChange={(event) => setSimSwapPromptMsisdn(event.target.value)}
                placeholder="27640822549"
                disabled={sendingSimSwapPrompt}
              />
              <p className="text-xs text-muted-foreground">
                Customer account in template:{' '}
                {getCustomerSmsRecipient() || 'No account MSISDN available'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sim-swap-prompt-reason">Internal note</Label>
              <Textarea
                id="sim-swap-prompt-reason"
                value={simSwapPromptReason}
                onChange={(event) =>
                  setSimSwapPromptReason(event.target.value.slice(0, 500))
                }
                rows={3}
                placeholder="Customer lost handset and requested SIM swap identity check"
                disabled={sendingSimSwapPrompt}
              />
              <div className="text-right text-xs text-muted-foreground">
                {simSwapPromptReason.length}/500
              </div>
            </div>
            {simSwapPromptResult && (
              <Alert
                variant={simSwapPromptResult.success ? 'default' : 'destructive'}
              >
                <AlertDescription>{simSwapPromptResult.message}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSimSwapPromptOpen(false)}
              disabled={sendingSimSwapPrompt}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void sendSimSwapPrompt('SMS')}
              disabled={
                !simSwapPromptMsisdn.trim() ||
                !canSendSimSwapVerification(currentSimSwapVerification, sendingSimSwapPrompt || loadingIdentityAssurance) ||
                !canPerformSimSwap
              }
            >
              {sendingSimSwapPrompt ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconMessage className="size-4" />
              )}
              Send SMS
            </Button>
            <Button
              type="button"
              onClick={() => void sendSimSwapPrompt('WHATSAPP')}
              disabled={
                !simSwapPromptMsisdn.trim() ||
                !canSendSimSwapVerification(currentSimSwapVerification, sendingSimSwapPrompt || loadingIdentityAssurance) ||
                !canPerformSimSwap
              }
            >
              {sendingSimSwapPrompt ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconMessage className="size-4" />
              )}
              Send WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
    </TooltipProvider>
  );
}
